import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowPathIcon, 
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentArrowDownIcon,
  ClockIcon,
  BuildingOfficeIcon,
  FireIcon,
  SparklesIcon,
  FunnelIcon, // Ajout de l'icône pour le filtre
  XMarkIcon, // Icône pour fermer le modal
  ArrowDownTrayIcon // Icône pour l'export des statistiques
} from '@heroicons/react/24/outline';
import { format, startOfMonth, endOfMonth, subMonths, addMonths, startOfQuarter, endOfQuarter, subQuarters, addQuarters, startOfYear, endOfYear, subYears, addYears } from 'date-fns';
import fr from 'date-fns/locale/fr';
import { getRoleColor } from '../utils/roleColors';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Importation des bibliothèques pour le graphique
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';

// Enregistrement des éléments nécessaires pour Chart.js
ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * Normalise le type de personnel en gérant les variations de casse, espaces et accents
 */
const normalizePersonnelType = (val, title = '', description = '') => {
  const strip = s => (s || '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, ''); // supprime les accents

  const v = strip(val);
  const txt = `${strip(title)} ${strip(description)}`;

  if (v === 'maladie' || txt.includes('maladie') || txt.includes('arret maladie')) return 'maladie';
  if (v === 'absence' || txt.includes('absence')) return 'absence';
  if (v === 'cp' || txt.includes('conge')) return 'CP';
  if (v === 'teletravail' || v === 'télétravail' || txt.includes('teletravail') || txt.includes('télétravail')) return 'télétravail';
  return 'autre';
};

/**
 * Normalisation robuste des rôles (accents, typos)
 */
const normalizeRole = (role) => {
  const r = (role || '')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  // Harmonise les graphies les plus courantes
  if (r.includes('billeterie') || r.includes('billetterie') || r.includes('ticket')) return 'billetterie';
  if (r.includes('permanence')) return 'permanence';
  if (r.includes('regie')) return 'regie';
  if (r.includes('tech')) return 'technique';
  if (r.includes('admin')) return 'administrateur';
  return r;
};

const normalizeIban = (value = '') => value.toString().replace(/[^0-9a-z]/gi, '').toUpperCase();

const formatIban = (value = '') => {
  const normalized = normalizeIban(value);
  return normalized ? normalized.replace(/(.{4})/g, '$1 ').trim() : '';
};

// ---- Résumé "heures du personnel" : calculs ----
export function computePersonnelSummary(events, HOURS_PER_DAY = 7) {
  // events : la liste déjà filtrée (période, personne/role, etc.)
  // Convention de données utilisée chez toi :
  //   - e.type === 'personnel' pour la gestion (CP, maladie, absence, télétravail)
  //   - e.personnelType ∈ {'CP','maladie','absence','télétravail'}
  //   - les autres e.type (répétition, spectacle, etc.) = "hors gestion"

  const fmtH = (h) => (Math.round(h * 10) / 10); // 1 décimale

  let gestionCount = 0;
  const cat = {
    CP:         { count: 0, hours: 0 },
    maladie:    { count: 0, hours: 0 },
    absence:    { count: 0, hours: 0 },
    télétravail:{ count: 0, hours: 0 },
  };

  let horsGestionHours = 0;

  for (const e of events) {
    const start = new Date(e.start);
    const end   = e.end ? new Date(e.end) : start;
    const durH  = Math.max(0, (end - start) / 36e5); // en heures

    const pt = normalizePersonnelType(e.personnelType, e.title, e.description);
    if ((e.type || '').toLowerCase() === 'personnel' && pt !== 'autre') {
      gestionCount += 1;
      cat[pt].count += 1;
      cat[pt].hours += durH;
    } else {
      // tout le reste est "hors gestion"
      horsGestionHours += durH;
    }
  }

  // Conversion jours pour CP
  const cpDays = cat.CP.hours / HOURS_PER_DAY;

  return {
    gestionCount,
    CP:         { events: cat.CP.count,         hours: fmtH(cat.CP.hours),         days: Math.round(cpDays * 10) / 10 },
    maladie:    { events: cat.maladie.count,    hours: fmtH(cat.maladie.hours) },
    absence:    { events: cat.absence.count,    hours: fmtH(cat.absence.hours) },
    télétravail:{ events: cat.télétravail.count,hours: fmtH(cat.télétravail.hours) },
    horsGestionHours: fmtH(horsGestionHours),
  };
}

// ---- Résumé "heures du personnel" : rendu PDF ----
export function renderPersonnelSummaryPDF(doc, summary, startY = null) {
  // doc = jsPDF
  const PAGE_W = doc.internal.pageSize.getWidth();
  const PAGE_H = doc.internal.pageSize.getHeight();
  const BOTTOM_MARGIN = 20;
  const FOOTER_H = 15;

  let y = startY ?? (doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 15 : 45);
  const need = 60; // ~hauteur nécessaire

  const usableBottom = PAGE_H - BOTTOM_MARGIN - FOOTER_H;
  if (y + need > usableBottom) {
    doc.addPage();
    y = 20;
  }

  // Titre "RÉSUMÉ"
  doc.setFillColor(245, 245, 245);
  doc.rect(14, y, PAGE_W - 28, 10, 'F');
  doc.setTextColor(200, 50, 50); // rouge doux (tu peux remplacer par ton style.primaryColor)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('RÉSUMÉ', 16, y + 7);
  y += 16;

  // Corps
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);

  // 1) Nombre total d'événements de gestion
  doc.text(`Nombre total d'événements de gestion: ${summary.gestionCount}`, 16, y); y += 6;

  // 2) Congés payés (jours)
  doc.text(`Congés payés: ${summary.CP.events} évènements (${summary.CP.days} jours)`, 16, y); y += 6;

  // 3) Maladie (jours) - Conversion des heures en jours
  const maladieDays = Math.round((summary.maladie.hours / 7) * 10) / 10; // 7h = 1 jour, arrondi à 1 décimale
  doc.text(`Maladie: ${summary.maladie.events} évènements (${maladieDays} jours)`, 16, y); y += 6;
  
  // 4) Absence et Télétravail (heures)
  doc.text(`Absences: ${summary.absence.events} évènements (${summary.absence.hours}h)`, 16, y); y += 6;
  doc.text(`Télétravail: ${summary.télétravail.events} évènements (${summary.télétravail.hours}h)`, 16, y); y += 10;

  // 4) Total des heures (hors gestion)
  doc.setFont('helvetica', 'bold');
  doc.text(`Total des heures (hors gestion): ${summary.horsGestionHours}h`, 16, y);
  doc.setFont('helvetica', 'normal');

  return y + 10;
}

/**
 * Composant Dashboard - Affiche les statistiques mensuelles des événements
 * et des performances des artistesProds et du personnel
 */
const Dashboard = () => {
  const navigate = useNavigate();
  const { api, user } = useContext(AuthContext);
  
  // États pour gérer le chargement et les erreurs
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // État pour le mois actuellement affiché - initialisation avec le mois courant
  const [currentMonth, setCurrentMonth] = useState(() => {
    const today = new Date();
    // Réinitialiser l'heure pour éviter des problèmes de comparaison de dates
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  
  // État pour contrôler la surbrillance de la première colonne dans l'export année
  const [highlightFirstColumn, setHighlightFirstColumn] = useState(true);
  
  // États pour stocker les différentes statistiques
  const [stats, setStats] = useState({
    total: 0,
    shows: 0,
    production: 0,
    coproduction: 0,
    one: 0,
    rehearsals: 0,
    rentals: 0,
    event: 0
  });
  const [artistStats, setArtistStats] = useState([]);
  const [staffStats, setStaffStats] = useState([]);
  const [absenceStats, setAbsenceStats] = useState([]);
  const [unavailabilityStats, setUnavailabilityStats] = useState([]); // Nouvel état pour les indisponibilités


// Type de découpage temporel : 'month' | 'quarter' | 'year'
const [periodType, setPeriodType] = useState('month');
  
  // État pour les statistiques des spectacles par statut (pour le camembert)
  const [showStatusStats, setShowStatusStats] = useState({
    confirmed: 0,
    provisional: 0,
    ticketsOpen: 0,
    cancelled: 0,
  });
  
  // État pour le filtre de rôle du personnel
  const [roleFilter, setRoleFilter] = useState('');
  
  // État pour le filtre de type d'absence
  const [absenceTypeFilter, setAbsenceTypeFilter] = useState('');
  
  // Nouveaux états pour le filtrage par salle
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(() => {
    const savedRoomFilter = localStorage.getItem('dashboardSelectedRoom');
    return savedRoomFilter || 'all';
  });
  const [originalEvents, setOriginalEvents] = useState([]);

  // Renvoie toujours le bon nom de salle (ou "Non assignée" si réellement aucune)
  const resolveRoomName = (evt) => {
    if (!evt) return 'Non assignée';
    // 1) Relation embarquée
    if (evt.room?.name) return evt.room.name;
    if (evt.Room?.name) return evt.Room.name;
    // 2) Fallback via l'id
    const id = evt.roomId ?? evt.RoomId ?? evt.room?.id ?? evt.Room?.id;
    const r = Array.isArray(rooms) ? rooms.find(x => x.id === id) : null;
    return r?.name ?? 'Non assignée';
  };
  
  // Ajout d'un état pour le modal de détails
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [personEvents, setPersonEvents] = useState([]);
  
  // États pour l'objectif mensuel global
  const [monthlyTarget, setMonthlyTarget] = useState(null);
  const [targetInput, setTargetInput] = useState('');

   // Formatage du mois et calcul de la plage de dates pour le mois sélectionné
   // Libellé dynamiquement « Mois », « Tn » ou « Année »
   const formattedPeriod = periodType === 'month'
     ? format(currentMonth, 'MMMM yyyy', { locale: fr })
     : periodType === 'quarter'
       ? `${format(currentMonth, 'MMMM', { locale: fr })} - ${format(addMonths(currentMonth, 2), 'MMMM yyyy', { locale: fr })}`
       : `${currentMonth.getFullYear()}`;
  
   // Bornes de la période sélectionnée
   let startDate, endDate;
   if (periodType === 'month') {
    startDate = startOfMonth(currentMonth);
    endDate   = endOfMonth(currentMonth);
   } else if (periodType === 'quarter') {
    // Trimestre basé sur le mois actuel + 2 mois suivants
    startDate = startOfMonth(currentMonth);
    endDate   = endOfMonth(addMonths(currentMonth, 2));
   } else { // 'year'
    startDate = startOfYear(currentMonth);
    endDate   = endOfYear(currentMonth);
   }

  // Récupération de la liste des salles
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const response = await api.get('/rooms');
        if (response.data && response.data.data && response.data.data.rooms) {
          setRooms(response.data.data.rooms);
        }
      } catch (err) {
        console.error('Erreur lors de la récupération des salles:', err);
      }
    };
    
    fetchRooms();
  }, [api]);

  /**
   * Récupère les statistiques depuis l'API chaque fois que le mois change
   */
  useEffect(() => {
    const fetchStats = async () => {
      // Réinitialisation des statistiques au début de chaque récupération
      setStats({        total: 0,        shows: 0,        production: 0,        coproduction: 0,        one: 0,        rehearsals: 0,        rentals: 0,        events: 0      });
      setArtistStats([]);
      setStaffStats([]);
      setAbsenceStats([]);
      setShowStatusStats({        confirmed: 0,        provisional: 0,        ticketsOpen: 0,        cancelled: 0,      });
      
      setLoading(true);
      try {

        // Formatage des dates pour la requête API
        const formattedStartDate = format(startDate, 'yyyy-MM-dd');
        // Ajouter 23:59:59 à la date de fin pour inclure tous les événements du dernier jour
        const endDateWithTime = new Date(endDate);
        endDateWithTime.setHours(23, 59, 59, 999);
        const formattedEndDate = format(endDateWithTime, 'yyyy-MM-dd HH:mm:ss');
        
        console.log('Récupération des événements du', formattedStartDate, 'au', formattedEndDate);
        
        // Appel à l'API pour récupérer les événements avec filtrage par date et inclusion des PDFs
        const response = await api.get('/events', {
          params: {
            start: formattedStartDate,
            end: formattedEndDate,
            include: 'Pdfs,Users,Room'
          }
        });
        
        // Extraction des événements de la réponse
        let events = [];
        if (response.data && typeof response.data === 'object') {
          if (Array.isArray(response.data)) {
            events = response.data;
          } else if (response.data.data && Array.isArray(response.data.data.events)) {
            events = response.data.data.events;
          } else if (response.data.events && Array.isArray(response.data.events)) {
            events = response.data.events;
          } else if (response.data.data && Array.isArray(response.data.data)) {
            events = response.data.data;
          }
        }
        
        console.log(`${events.length} événements trouvés pour ${formattedPeriod}`);
        
        // Stocker les événements originaux pour le filtrage
        setOriginalEvents(events);
        
        // Filtrer les événements si une salle est sélectionnée
        const filteredEvents = selectedRoom === 'all' 
          ? events 
          : events.filter(event => event.roomId === parseInt(selectedRoom));
        
        // Traiter les événements filtrés
        processEvents(filteredEvents);
        
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors de la récupération des données:', err);
        setError('Erreur lors du chargement des statistiques');
        setLoading(false);
      }
    };

    fetchStats();
  }, [api, currentMonth, periodType, selectedRoom]); // Ajout de selectedRoom comme dépendance

  // Chargement de l'objectif mensuel global
  useEffect(() => {
    const fetchMonthlyTarget = async () => {
      try {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth() + 1;
        const response = await api.get(`/monthly-targets?year=${year}&month=${month}`);
        
        if (response.data && response.data.data && response.data.data.target) {
          const target = response.data.data.target;
          setMonthlyTarget(target.targetHours);
          setTargetInput(target.targetHours.toString());
        } else {
          setMonthlyTarget(null);
          setTargetInput('');
        }
      } catch (err) {
        console.error('Erreur lors du chargement de l\'objectif mensuel:', err);
        setMonthlyTarget(null);
        setTargetInput('');
      }
    };

    fetchMonthlyTarget();
  }, [api, currentMonth]);

  // Fonction pour traiter les événements et calculer les statistiques
  const processEvents = (events) => {
    // Initialisation des statistiques
    const newStats = {
      total: 0, // Initialisé à 0, sera incrémenté uniquement pour les événements de type 'event'
      shows: 0, // Nombre total de spectacles non annulés
      showsTotal: 0, // Nombre total de spectacles (y compris annulés)
      production: 0,
      coproduction: 0,
      one: 0,
      rehearsals: 0,
      rentals: 0,
      events: 0
    };
    
    // Suivi des spectacles par artistesProd et des heures par membre du personnel
    const artistShowCount = {};
    const staffHoursCount = {};
    
    // Suivi des absences du personnel
    const absenceCount = {};
    
    // Suivi des indisponibilités des utilisateurs
    const unavailabilityCount = {};
    
    // Statistiques pour le statut des spectacles (pour le camembert)
    // Nous allons compter les spectacles confirmés et annulés séparément
    // pour pouvoir calculer le nombre net (confirmés - annulés)
    const newShowStatusStats = {
      confirmed: 0,
      provisional: 0,
      ticketsOpen: 0,
      cancelled: 0,
    };
    
    // Comptage des types d'événements
    events.forEach(event => {
      // Vérification du type d'événement
      const eventType = event.type ? event.type.toLowerCase() : '';
      const eventTitle = event.title ? event.title.toLowerCase() : '';
      
      // Calcul de la durée de l'événement en heures
      const start = new Date(event.start);
      const end = new Date(event.end);
      const durationHours = (end - start) / (1000 * 60 * 60);
      
      // Traitement selon le type d'événement
     // Traitement de tous les spectacles
  if (eventType === 'show'|| eventType.includes('spectacle')|| eventTitle.includes('spectacle')) {
        // Comptage total des spectacles (y compris annulés)
        newStats.showsTotal++;
        
        // Comptage des spectacles non annulés
        if (event.showStatus !== 'cancelled') {
          // Incrémenter le compteur de spectacles non annulés
          newStats.shows++;
          
          // Comptage des spectacles par type
          if (event.showType === 'production') {
            newStats.production++;
          } else if (event.showType === 'coproduction') {
            newStats.coproduction++;
          } else if (event.showType === 'one') {
            newStats.one++;
          }
        }
        
        // Comptage des spectacles par statut pour le camembert (tous les statuts)
        if (event.showStatus) {
          newShowStatusStats[event.showStatus]++;
        } else {
          // Par défaut, considérer comme provisoire si non spécifié
          newShowStatusStats.provisional++;
        }
        
        // Comptage des spectacles par artistesProd et des heures pour le personnel
        if (event.Users && Array.isArray(event.Users)) {
          event.Users.forEach(user => {
            // Comptage pour les artistesProds
            if (user.role === 'artistesProd') {
              const artistName = `${user.firstName} ${user.lastName}`;
              
              // Si l'artiste n'existe pas encore dans le comptage, l'initialiser avec son ID
              if (!artistShowCount[artistName]) {
                artistShowCount[artistName] = {
                  id: user.id,
                  name: artistName,
                  count: 0,
                  shows: [] // Ajouter un tableau pour stocker les détails des spectacles
                };
              }
              
              // Ajouter le spectacle aux détails et incrémenter le compteur seulement si le spectacle est confirmé ou billetterie ouverte
              if (event.showStatus === 'confirmed' || event.showStatus === 'ticketsOpen') {
                artistShowCount[artistName].count += 1;
                artistShowCount[artistName].shows.push({
                  title: event.title,
                  date: event.start,
                  showType: event.showType,
                  showStatus: event.showStatus,
                  // --> ajoute la salle sur l'objet
                  room: event.room || event.Room || rooms.find(r => r.id === event.roomId) || null
                });
              }
            }
            
            // Comptage des heures pour le personnel (billetterie, permanence, régie, administrateur) - exclure les congés payés
            if (user.role === 'billeterie' || user.role === 'permanence' || user.role === 'regie' || user.role === 'technique' || user.role === 'administrateur' || user.role === 'consultation') {
              const staffName = `${user.firstName} ${user.lastName}`;
              const staffRole = user.role;
              
              // Vérifier si c'est un congé payé
              const isCP = user.through?.isCP || eventType === 'cp' || eventTitle.includes('congé');
              
              if (!isCP) {
                if (!staffHoursCount[staffName]) {
                  staffHoursCount[staffName] = { 
                    id: user.id, // Ajout de l'ID de l'utilisateur
                    name: staffName, 
                    role: staffRole, 
                    hours: 0,
                    shows: [] // Ajouter un tableau pour stocker les détails des spectacles
                  };
                }
                
                // Utiliser les heures spécifiques si disponibles, sinon utiliser la durée calculée
                const userHours = user.through?.hours ? parseFloat(user.through.hours) : durationHours;
                staffHoursCount[staffName].hours += userHours;
                
                // Ajouter les détails du spectacle
                staffHoursCount[staffName].shows.push({
                  title: event.title,
                  date: event.start,
                  startTime: event.start,
                  endTime: event.end,
                  showType: event.showType,
                  showStatus: event.showStatus,
                  hours: userHours,
                  eventRole: staffRole, // Ajouter le rôle de l'utilisateur pour cet événement
                  // --> ajoute la salle sur l'objet
                  room: event.room || event.Room || rooms.find(r => r.id === event.roomId) || null
                });
              }
            }
          });
        }
      } else if (eventType === 'rehearsal' || eventType.includes('calage') || eventType.includes('répétition') || eventTitle.includes('calage')) {
        // Calages et répétitions
        newStats.rehearsals++;
        
        // Comptage des heures pour le personnel lors des répétitions - exclure les congés payés
        if (event.Users && Array.isArray(event.Users)) {
          event.Users.forEach(user => {
            if (user.role === 'billeterie' || user.role === 'permanence' || user.role === 'regie' || user.role === 'technique' || user.role === 'administrateur' || user.role === 'consultation') {
              const staffName = `${user.firstName} ${user.lastName}`;
              const staffRole = user.role;
              
              // Vérifier si c'est un congé payé
              const isCP = user.through?.isCP || eventType === 'cp' || eventTitle.includes('congé');
              
              if (!isCP) {
                if (!staffHoursCount[staffName]) {
                  staffHoursCount[staffName] = { 
                    id: user.id, // Ajout de l'ID de l'utilisateur
                    name: staffName, 
                    role: staffRole, 
                    hours: 0,
                    shows: [] // Ajouter un tableau pour stocker les détails des spectacles
                  };
                }
                
                // Utiliser les heures spécifiques si disponibles, sinon utiliser la durée calculée
                const userHours = user.through?.hours ? parseFloat(user.through.hours) : durationHours;
                staffHoursCount[staffName].hours += userHours;
                
                // Ajouter les détails du spectacle
                staffHoursCount[staffName].shows.push({
                  title: event.title,
                  date: event.start,
                  startTime: event.start,
                  endTime: event.end,
                  showType: event.showType,
                  showStatus: event.showStatus,
                  hours: userHours,
                  type: 'rehearsal',
                  eventRole: staffRole, // Ajouter le rôle de l'utilisateur pour cet événement
                  // --> ajoute la salle sur l'objet
                  room: event.room || event.Room || rooms.find(r => r.id === event.roomId) || null
                });
              }
            }
          });
        }
      } else if (eventType === 'rental' || eventType === 'maintenance' || eventType.includes('location') || eventType.includes('salle') || eventTitle.includes('location')) {
        // Locations de salle
        newStats.rentals++;
        
        // Comptage des heures pour le personnel lors des locations
        if (event.Users && Array.isArray(event.Users)) {
          event.Users.forEach(user => {
            if (user.role === 'billeterie' || user.role === 'permanence' || user.role === 'regie' || user.role === 'technique' || user.role === 'administrateur' || user.role === 'consultation') {
              const staffName = `${user.firstName} ${user.lastName}`;
              const staffRole = user.role;
              
              if (!staffHoursCount[staffName]) {
                staffHoursCount[staffName] = { 
                  id: user.id, // Ajout de l'ID de l'utilisateur
                  name: staffName, 
                  role: staffRole, 
                  hours: 0,
                  shows: [] // Ajouter un tableau pour stocker les détails des spectacles
                };
              }
              
              // Utiliser les heures spécifiques si disponibles, sinon utiliser la durée calculée
              const userHours = user.through?.hours ? parseFloat(user.through.hours) : durationHours;
              staffHoursCount[staffName].hours += userHours;
              
              // Ajouter les détails du spectacle
              staffHoursCount[staffName].shows.push({
                title: event.title,
                date: event.start,
                startTime: event.start,
                endTime: event.end,
                showType: event.showType,
                showStatus: event.showStatus,
                hours: userHours,
                type: 'rental',
                eventRole: staffRole, // Ajouter le rôle de l'utilisateur pour cet événement
                // --> ajoute la salle sur l'objet
                room: event.room || event.Room || rooms.find(r => r.id === event.roomId) || null
              });
            }
          });
        }
      } else if (eventType === 'personnel') {
        // Traitement des événements de type 'personnel' (absences)
        const personnelType = normalizePersonnelType(
          event.personnelType,
          event.title,
          event.description
        );
        
        // Comptage des absences pour le personnel et les artistes
        if (event.Users && Array.isArray(event.Users)) {
          event.Users.forEach(user => {
            const userName = `${user.firstName} ${user.lastName}`;
            const userRole = user.role;
            
            if (!absenceCount[userName]) {
              absenceCount[userName] = {
                id: user.id,
                name: userName,
                role: userRole,
                absences: [],
                totalHours: 0,
                types: {
                  maladie: 0,
                  absence: 0,
                  CP: 0,
                  télétravail: 0,
                  autre: 0
                }
              };
            }
            
            // Utiliser directement les heures d'absence au lieu des jours
            const hours = durationHours;
            
            // Identifier si c'est un congé payé pour l'affichage
            const isCP = personnelType === 'CP';
            
            // Comptabiliser toutes les absences, y compris les congés payés
            absenceCount[userName].totalHours = (absenceCount[userName].totalHours || 0) + hours;
            absenceCount[userName].types[personnelType] = (absenceCount[userName].types[personnelType] || 0) + hours;
            
            // Ajouter les détails de l'absence
            absenceCount[userName].absences.push({
              title: event.title,
              start: event.start,
              end: event.end,
              personnelType,
              hours: hours,
              displayValue: hours,
              isCP: isCP,
              eventRole: userRole // Ajouter le rôle de l'utilisateur pour cet événement
            });
            
            // Ajouter tous les événements de type 'personnel' aux heures du personnel, mais ne compter les heures que pour le télétravail
            if (userRole === 'billeterie' || userRole === 'permanence' || userRole === 'regie' || userRole === 'technique' || userRole === 'administrateur' || userRole === 'consultation') {
              if (!staffHoursCount[userName]) {
                staffHoursCount[userName] = { 
                  id: user.id,
                  name: userName, 
                  role: userRole, 
                  hours: 0,
                  shows: []
                };
              }
              
              // Exclure la "gestion du personnel" du calcul des heures, ne compter que le "télétravail"
              let hoursToAdd = 0;
              if (personnelType === 'télétravail') {
                hoursToAdd = hours;
                staffHoursCount[userName].hours += hoursToAdd;
              }
              // Pour la "gestion du personnel", on n'ajoute pas d'heures (hoursToAdd reste 0)
              
              // Ajouter les détails de l'événement personnel (tous types)
              staffHoursCount[userName].shows.push({
                title: event.title || (personnelType === 'télétravail' ? 'Télétravail' : personnelType === 'gestion du personnel' ? 'Gestion du personnel' : 'Absence'),
                date: event.start,
                startTime: event.start,
                endTime: event.end,
                showType: event.showType || 'personnel',
                personnelType: personnelType,
                showStatus: 'confirmed',
                hours: hoursToAdd, // Utiliser hoursToAdd au lieu de la condition ternaire
                type: 'personnel',
                eventRole: userRole,
                // --> ajoute la salle sur l'objet
                room: event.room || event.Room || rooms.find(r => r.id === event.roomId) || null
              });
            }
          });
        }
      } else if (eventType === 'non-disponible') {
        // Traitement des événements de type 'non-disponible'
        if (event.Users && Array.isArray(event.Users)) {
          event.Users.forEach(user => {
            // Exclure les utilisateurs avec le rôle 'non-assigné'
            if (user.role !== 'non-assigné') {
              const userName = `${user.firstName} ${user.lastName}`;
              const userRole = user.role;
              
              if (!unavailabilityCount[userName]) {
                unavailabilityCount[userName] = {
                  id: user.id,
                  name: userName,
                  role: userRole,
                  unavailabilities: [],
                  totalHours: 0
                };
              }
              
              // Utiliser directement les heures d'indisponibilité
              unavailabilityCount[userName].totalHours += durationHours;
              
              // Ajouter les détails de l'indisponibilité
              unavailabilityCount[userName].unavailabilities.push({
                title: event.title || "Indisponibilité",
                start: event.start,
                end: event.end,
                hours: durationHours,
                type: 'non-disponible'
              });
            }
          });
        }
      } else {
        // Autres événements (type 'event')
        newStats.events++;
        
        // Incrémenter le total uniquement pour les événements de type 'event'
        if (eventType === 'event') {
          newStats.total++;
        }
        
        // Comptage des heures pour le personnel lors des événements
        if (event.Users && Array.isArray(event.Users)) {
          event.Users.forEach(user => {
            // Exclure les utilisateurs avec le rôle 'non-assigné'
            if ((user.role === 'billeterie' || user.role === 'permanence' || user.role === 'regal' || user.role === 'regie' || user.role === 'administrateur' || user.role === 'consultation') && user.role !== 'non-assigné') {
              const staffName = `${user.firstName} ${user.lastName}`;
              const staffRole = user.role;
              
              if (!staffHoursCount[staffName]) {
                staffHoursCount[staffName] = { 
                  id: user.id, // Ajout de l'ID de l'utilisateur
                  name: staffName, 
                  role: staffRole, 
                  hours: 0,
                  shows: [] // Ajouter un tableau pour stocker les détails des spectacles
                };
              }
              
              // Utiliser les heures spécifiques si disponibles, sinon utiliser la durée calculée
              const userHours = user.through?.hours ? parseFloat(user.through.hours) : durationHours;
              staffHoursCount[staffName].hours += userHours;
              
              // Ajouter les détails du spectacle
              staffHoursCount[staffName].shows.push({
                title: event.title,
                date: event.start,
                startTime: event.start,
                endTime: event.end,
                showType: event.showType,
                showStatus: event.showStatus,
                hours: userHours,
                type: 'event',
                eventRole: staffRole, // Ajouter le rôle de l'utilisateur pour cet événement
                // --> ajoute la salle sur l'objet
                room: event.room || event.Room || rooms.find(r => r.id === event.roomId) || null
              });
            }
          });
        }
      }
    });
    
    
    // Conversion des statistiques d'artistesProds en tableau pour l'affichage
    const artistStatsArray = Object.values(artistShowCount)
      .sort((a, b) => b.count - a.count);
    
    // Conversion des heures du personnel en tableau pour l'affichage
    const staffStatsArray = Object.values(staffHoursCount)
      .map(staff => ({
        ...staff,
        hours: parseFloat(staff.hours.toFixed(1)), // Arrondi à 1 décimale
        monthlyTarget: monthlyTarget || 0 // Utiliser l'objectif mensuel global
      }))
      .sort((a, b) => b.hours - a.hours);
      
    // Conversion des absences en tableau pour l'affichage
    const absenceStatsArray = Object.values(absenceCount)
      .map(absence => ({
        ...absence,
        totalHours: parseFloat(absence.totalHours.toFixed(1)) // Arrondi à 1 décimale
      }))
      .sort((a, b) => b.totalHours - a.totalHours);
    
    // Conversion des indisponibilités en tableau pour l'affichage
    const unavailabilityStatsArray = Object.values(unavailabilityCount)
      .map(unavailability => ({
        ...unavailability,
        totalHours: parseFloat(unavailability.totalHours.toFixed(1)) // Arrondi à 1 décimale
      }))
      .sort((a, b) => b.totalHours - a.totalHours);
    
    // Mise à jour des états avec les nouvelles données
    setStats(newStats);
    setArtistStats(artistStatsArray);
    setStaffStats(staffStatsArray);
    setAbsenceStats(absenceStatsArray);
    setUnavailabilityStats(unavailabilityStatsArray);
    setShowStatusStats(newShowStatusStats);
  };

  // Fonction pour filtrer les événements par salle
  const handleRoomChange = (e) => {
    const roomId = e.target.value;
    setSelectedRoom(roomId);
    
    // Sauvegarder la sélection dans localStorage
    localStorage.setItem('dashboardSelectedRoom', roomId);
    
    // Le filtrage est géré dans l'effet useEffect qui dépend de selectedRoom
  };

  // Fonctions pour gérer les objectifs mensuels
  const handleMonthlyTargetChange = (value) => {
    setTargetInput(value);
  };

  const saveMonthlyTarget = async (value) => {
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      const targetHours = parseFloat(value) || 0;

      if (targetHours === 0) {
        // Supprimer l'objectif si la valeur est 0
        await api.delete(`/monthly-targets/${year}/${month}`);
        setMonthlyTarget(null);
      } else {
        // Créer ou mettre à jour l'objectif global
        await api.post('/monthly-targets', {
          year,
          month,
          targetHours
        });
        setMonthlyTarget(targetHours);
      }
    } catch (err) {
      console.error('Erreur lors de la sauvegarde de l\'objectif mensuel:', err);
    }
  };

  // Fonction pour déterminer la couleur des heures en fonction de l'objectif
  const getHoursColor = (hours, target) => {
    if (!target || target === 0) return '#000000'; // Noir par défaut
    
    const ratio = hours / target;
    
    if (ratio < 0.8) {
      return '#dc2626'; // Rouge si moins de 80% de l'objectif
    } else if (ratio < 1.0) {
      return '#ea580c'; // Orange si entre 80% et 100%
    } else {
      return '#000000'; // Noir si objectif atteint ou dépassé
    }
  };
  
  // Fonction pour ouvrir le modal avec les détails d'une personne
  const handlePersonClick = async (person, isArtist = false) => {
    // L'ID devrait maintenant être directement disponible dans l'objet person
    // pour les artistes et le personnel
    const personId = person.id;
    
    if (!personId) {
      console.error('ID de personne non disponible:', person);
      setError('Impossible de récupérer les détails: ID non disponible');
      return;
    }
    
    // Vérifier si la personne a des absences
    const hasAbsences = absenceStats.find(a => a.id === personId);
    const isAbsence = hasAbsences && person === hasAbsences;
    
    // Vérifier si la personne a des indisponibilités
    const hasUnavailabilities = unavailabilityStats.find(u => u.id === personId);
    const isUnavailability = hasUnavailabilities && person === hasUnavailabilities;
    
    setSelectedPerson({
      ...person, 
      isArtist,
      isAbsence,
      isUnavailability
    });
    setShowDetailsModal(true);
    
    try {
      // Si c'est une personne avec des indisponibilités et qu'on a cliqué depuis la section indisponibilités
      if (isUnavailability) {
        // Utiliser directement les indisponibilités stockées dans les statistiques
        setPersonEvents(hasUnavailabilities.unavailabilities.map(unavailability => ({
          ...unavailability,
          type: 'non-disponible',
          isUnavailability: true
        })));
        return;
      }
      
      // Si c'est une personne avec des absences et qu'on a cliqué depuis la section absences
      if (isAbsence) {
        // Utiliser directement les absences stockées dans les statistiques
        setPersonEvents(hasAbsences.absences.map(absence => ({
          ...absence,
          type: 'personnel',
          personnelType: normalizePersonnelType(absence.personnelType ?? absence.type, absence.title, absence.description),
          isAbsence: true
        })));
        return;
      }
      
      // Sinon, récupérer les événements associés à cette personne
      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      // Ajouter 23:59:59 à la date de fin pour inclure tous les événements du dernier jour
      const endDateWithTime = new Date(endDate);
      endDateWithTime.setHours(23, 59, 59, 999);
      const formattedEndDate = format(endDateWithTime, 'yyyy-MM-dd HH:mm:ss');
      
      const endpoint = isArtist 
        ? `/events/artist/${personId}` 
        : `/events/staff/${personId}`;
      
      console.log(`Récupération des événements pour ${isArtist ? 'artiste' : 'personnel'} ID:`, personId);
      
      const response = await api.get(endpoint, {
        params: {
          start: formattedStartDate,
          end: formattedEndDate
        }
      });
      
      let events = [];
      if (response.data && response.data.data && response.data.data.events) {
        events = response.data.data.events;
        console.log('Événements récupérés:', events.length);
      } else {
        console.log('Aucun événement trouvé ou format de réponse inattendu:', response.data);
      }

      // ←—— NOUVEAU : si on a un filtre de salle actif, on ne garde que ses événements
      if (selectedRoom && selectedRoom !== 'all') {
        events = events.filter(ev => ev.roomId === parseInt(selectedRoom, 10));
      }

      
      setPersonEvents(events);
    } catch (err) {
      console.error('Erreur lors de la récupération des événements:', err);
      setError('Erreur lors du chargement des événements');
    }
  };
  
  // Fonction pour ouvrir le modal avec les détails d'une salle
  const handleRoomClick = (room) => {
    // Filtrer les événements pour cette salle
    const roomEvents = originalEvents.filter(event => event.roomId === room.id);
    
    // Mettre à jour les états pour le modal
    setPersonEvents(roomEvents);
    setSelectedPerson({
      name: room.name,
      isRoom: true,
      capacity: room.capacity
    });
    setShowDetailsModal(true);
  };
  
  // Fonction pour exporter les événements d'une personne en Excel
  const exportPersonEvents = async () => {
    if (!selectedPerson || personEvents.length === 0) return;
    
    // Préparer les données pour l'export
    const isArtist = selectedPerson.isArtist;
    const isRoom = selectedPerson.isRoom;
    const isAbsence = selectedPerson.isAbsence;
    
    // Créer un nouveau classeur
    const wb = XLSX.utils.book_new();
    
    // Préparer les données selon le type (salle, artistesProd, personnel ou absence)
    if (isAbsence) {
      // Regrouper les événements récurrents
      const groupedEvents = {};
      const nonRecurringEvents = [];
      
      personEvents.forEach(event => {
        const recurrenceId = event.recurrenceId;
        
        // Si l'événement fait partie d'une série récurrente
        if (recurrenceId && event.isRecurring) {
          if (!groupedEvents[recurrenceId]) {
            groupedEvents[recurrenceId] = {
              title: event.title || 'Absence',
              events: [],
              personnelType: event.personnelType,
              description: event.description
            };
          }
          groupedEvents[recurrenceId].events.push(event);
        } else {
          // Événement non récurrent
          nonRecurringEvents.push(event);
        }
      });
      
      // Préparer les données pour l'export
      const exportData = [];
      
      // Ajouter les événements non récurrents
      nonRecurringEvents.forEach(event => {
        exportData.push({
          'Titre': event.title || 'Absence',
          'Date début': event.start ? format(new Date(event.start), 'dd/MM/yyyy') : 'N/A',
          'Date fin': event.end ? format(new Date(event.end), 'dd/MM/yyyy') : 'N/A',
          'Type d\'absence': event.personnelType === 'maladie' ? 'Maladie' : 
                           event.personnelType === 'absence' ? 'Absence' : 
                           event.personnelType === 'CP' ? 'Congé payé' : 
                           event.personnelType === 'télétravail' ? 'Télétravail' : 
                           event.personnelType || 'Autre',
          'Durée': event.personnelType === 'CP' ? (event.hours ? (event.hours / 7).toFixed(1) + ' jours' : 'N/A') : (event.hours ? event.hours.toFixed(1) + ' heures' : 'N/A'),
          'Commentaire': event.description || '',
          'Récurrence': 'Non'
        });
      });
      
      // Ajouter les événements récurrents (regroupés)
      Object.values(groupedEvents).forEach(group => {
        // Trier les événements par date
        const sortedEvents = group.events.sort((a, b) => new Date(a.start) - new Date(b.start));
        
        // Prendre le premier et le dernier événement de la série
        const firstEvent = sortedEvents[0];
        const lastEvent = sortedEvents[sortedEvents.length - 1];
        
        // Calculer la durée totale
        const totalHours = sortedEvents.reduce((sum, event) => sum + (event.hours || 0), 0);
        
        exportData.push({
          'Titre': group.title,
          'Date début': firstEvent.start ? format(new Date(firstEvent.start), 'dd/MM/yyyy') : 'N/A',
          'Date fin': lastEvent.end ? format(new Date(lastEvent.end), 'dd/MM/yyyy') : 'N/A',
          'Type d\'absence': firstEvent.personnelType === 'maladie' ? 'Maladie' : 
                           firstEvent.personnelType === 'absence' ? 'Absence' : 
                           firstEvent.personnelType === 'CP' ? 'Congé payé' : 
                           firstEvent.personnelType === 'télétravail' ? 'Télétravail' : 
                           firstEvent.personnelType || 'Autre',
          'Durée': firstEvent.personnelType === 'CP' ? (totalHours / 7).toFixed(1) + ' jours' : totalHours.toFixed(1) + ' heures',
          'Commentaire': group.description || '',
          'Récurrence': `Oui (${sortedEvents.length} occurrences)`
        });
      });
      
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Amélioration du design: ajout de styles pour l'en-tête
      const range = XLSX.utils.decode_range(ws['!ref']);
      
      // Style pour les en-têtes
      const headerStyle = {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: '4472C4' } }, // Bleu foncé
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } }
        }
      };
      
      // Style pour les cellules de données
      const dataStyle = {
        alignment: { vertical: 'center', horizontal: 'center', wrapText: true },
        border: {
          top: { style: 'thin', color: { rgb: 'D9D9D9' } },
          bottom: { style: 'thin', color: { rgb: 'D9D9D9' } },
          left: { style: 'thin', color: { rgb: 'D9D9D9' } },
          right: { style: 'thin', color: { rgb: 'D9D9D9' } }
        }
      };
      
      // Style pour les lignes alternées
      const altRowStyle = {
        ...dataStyle,
        fill: { fgColor: { rgb: 'E9EFF8' } } // Bleu très clair
      };
      
      // Appliquer les styles aux en-têtes
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_col(C) + '1';
        if (!ws[address]) continue;
        ws[address].s = headerStyle;
      }
      
      // Amélioration du design: alternance de couleurs pour les lignes
      for (let R = 2; R <= range.e.r + 1; ++R) {
        const rowStyle = R % 2 === 0 ? altRowStyle : dataStyle;
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const address = XLSX.utils.encode_col(C) + R;
          if (!ws[address]) continue;
          ws[address].s = { ...rowStyle };
          
          // Alignement spécial pour certaines colonnes
          if (C === 0) { // Titre
            ws[address].s.alignment = { ...ws[address].s.alignment, horizontal: 'left' };
          } else if (C === 4) { // Durée (heures)
            ws[address].s.alignment = { ...ws[address].s.alignment, horizontal: 'right' };
          } else if (C === 5) { // Commentaire
            ws[address].s.alignment = { ...ws[address].s.alignment, horizontal: 'left', wrapText: true };
          }
        }
      }
      
      // Définir la hauteur de la ligne d'en-tête
      if (!ws['!rows']) ws['!rows'] = [];
      ws['!rows'][0] = { hidden: false, hpt: 28 }; // Hauteur de la première ligne (en-tête)
      
      // Ajuster la largeur des colonnes
      const colWidths = [
        { wch: 30 }, // Titre
        { wch: 12 }, // Date début
        { wch: 12 }, // Date fin
        { wch: 15 }, // Type d'absence
        { wch: 15 }, // Durée (heures)
        { wch: 40 }, // Commentaire
        { wch: 25 }  // Récurrence
      ];
      ws['!cols'] = colWidths;
      
      XLSX.utils.book_append_sheet(wb, ws, 'Absences');
      
      // Ajouter une feuille avec les statistiques par type d'absence
      const absenceTypes = {};
      personEvents.forEach(event => {
        const type = event.personnelType === 'maladie' ? 'Maladie' : 
                    event.personnelType === 'absence' ? 'Absence' : 
                    event.personnelType === 'CP' ? 'Congé payé' : 
                    event.personnelType === 'télétravail' ? 'Télétravail' : 
                    event.personnelType || 'Autre';
        absenceTypes[type] = (absenceTypes[type] || 0) + (event.hours || 0);
      });
      
      const statsData = Object.entries(absenceTypes).map(([type, hours]) => ({
        'Type d\'absence': type,
        'Nombre': type === 'Congé payé' ? (hours / 7).toFixed(1) + ' jours' : hours.toFixed(1) + ' heures'
      }));
      
      if (statsData.length > 0) {
        const wsStats = XLSX.utils.json_to_sheet(statsData);
        
        // Amélioration du design pour la feuille de statistiques
        const statsRange = XLSX.utils.decode_range(wsStats['!ref']);
        
        // Appliquer les styles aux en-têtes
        for (let C = statsRange.s.c; C <= statsRange.e.c; ++C) {
          const address = XLSX.utils.encode_col(C) + '1';
          if (!wsStats[address]) continue;
          wsStats[address].s = {
            font: { bold: true, color: { rgb: 'FFFFFF' } },
            fill: { fgColor: { rgb: '4472C4' } }, // Bleu foncé
            alignment: { horizontal: 'center', vertical: 'center' },
            border: {
              top: { style: 'thin', color: { rgb: '000000' } },
              bottom: { style: 'thin', color: { rgb: '000000' } },
              left: { style: 'thin', color: { rgb: '000000' } },
              right: { style: 'thin', color: { rgb: '000000' } }
            }
          };
        }
        
        // Appliquer des styles alternés aux lignes de données
        for (let R = 2; R <= statsRange.e.r + 1; ++R) {
          const rowColor = R % 2 === 0 ? 'E9EFF8' : 'FFFFFF'; // Alternance bleu clair/blanc
          for (let C = statsRange.s.c; C <= statsRange.e.c; ++C) {
            const address = XLSX.utils.encode_col(C) + R;
            if (!wsStats[address]) continue;
            wsStats[address].s = {
              alignment: { vertical: 'center' },
              border: {
                top: { style: 'thin', color: { rgb: 'D9D9D9' } },
                bottom: { style: 'thin', color: { rgb: 'D9D9D9' } },
                left: { style: 'thin', color: { rgb: 'D9D9D9' } },
                right: { style: 'thin', color: { rgb: 'D9D9D9' } }
              },
              fill: { fgColor: { rgb: rowColor } }
            };
            
            // Alignement spécial pour les colonnes
            if (C === 0) { // Type d'absence
              wsStats[address].s.alignment = { ...wsStats[address].s.alignment, horizontal: 'left' };
            } else if (C === 1) { // Nombre d'heures
              wsStats[address].s.alignment = { ...wsStats[address].s.alignment, horizontal: 'right' };
            }
          }
        }
        
        // Définir la hauteur de la ligne d'en-tête
        if (!wsStats['!rows']) wsStats['!rows'] = [];
        wsStats['!rows'][0] = { hidden: false, hpt: 28 }; // Hauteur de la première ligne (en-tête)
        
        // Ajuster la largeur des colonnes
        wsStats['!cols'] = [{ wch: 25 }, { wch: 20 }];
        
        XLSX.utils.book_append_sheet(wb, wsStats, 'Statistiques');
      }
    } else if (isRoom) {
      // Regrouper les événements récurrents
      const groupedEvents = {};
      const nonRecurringEvents = [];
      
      personEvents.forEach(event => {
        const recurrenceId = event.recurrenceId;
        
        // Si l'événement fait partie d'une série récurrente
        if (recurrenceId && event.isRecurring) {
          if (!groupedEvents[recurrenceId]) {
            groupedEvents[recurrenceId] = {
              title: event.title,
              events: [],
              type: event.type,
              showStatus: event.showStatus
            };
          }
          groupedEvents[recurrenceId].events.push(event);
        } else {
          // Événement non récurrent
          nonRecurringEvents.push(event);
        }
      });
      
      // Préparer les données pour l'export
      const exportData = [];
      
      // Ajouter les événements non récurrents
      nonRecurringEvents.forEach(event => {
        exportData.push({
          'Titre': event.title,
          'Date': format(new Date(event.start), 'dd/MM/yyyy'),
          'Heure': format(new Date(event.start), 'HH:mm'),
          'Type': event.type === 'show' ? 'Spectacle' : 
                  event.type === 'repetition' ? 'Répétition' : 
                  event.type === 'rental' ? 'Location' : event.type,
          'Statut': event.showStatus === 'confirmed' ? 'Confirmé' :
                   event.showStatus === 'provisional' ? 'Provisoire' :
                   event.showStatus === 'ticketsOpen' ? 'Billetterie ouverte' :
                   event.showStatus === 'cancelled' ? 'Annulé' : 'Non spécifié',
          'Récurrence': 'Non'
        });
      });
      
      // Ajouter les événements récurrents (regroupés)
      Object.values(groupedEvents).forEach(group => {
        // Trier les événements par date
        const sortedEvents = group.events.sort((a, b) => new Date(a.start) - new Date(b.start));
        
        // Prendre le premier et le dernier événement de la série
        const firstEvent = sortedEvents[0];
        const lastEvent = sortedEvents[sortedEvents.length - 1];
        
        exportData.push({
          'Titre': group.title,
          'Date': `${format(new Date(firstEvent.start), 'dd/MM/yyyy')} au ${format(new Date(lastEvent.start), 'dd/MM/yyyy')}`,
          'Heure': format(new Date(firstEvent.start), 'HH:mm'),
          'Type': firstEvent.type === 'show' ? 'Spectacle' : 
                  firstEvent.type === 'repetition' ? 'Répétition' : 
                  firstEvent.type === 'rental' ? 'Location' : firstEvent.type,
          'Statut': firstEvent.showStatus === 'confirmed' ? 'Confirmé' :
                   firstEvent.showStatus === 'provisional' ? 'Provisoire' :
                   firstEvent.showStatus === 'ticketsOpen' ? 'Billetterie ouverte' :
                   firstEvent.showStatus === 'cancelled' ? 'Annulé' : 'Non spécifié',
          'Récurrence': `Oui (${sortedEvents.length} occurrences)`
        });
      });
      
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Amélioration du design: ajout de styles pour l'en-tête
      const range = XLSX.utils.decode_range(ws['!ref']);
      
      // Définir la hauteur de la ligne d'en-tête
      if (!ws['!rows']) ws['!rows'] = [];
      ws['!rows'][0] = { hidden: false, hpt: 28 }; // Hauteur de la première ligne (en-tête)
      
      // Appliquer des styles à toutes les cellules
      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const address = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[address]) continue;
          
          // Déterminer la couleur de fond en fonction de la ligne (alternance)
          const rowColor = R === 0 ? '1F4E79' : // Bleu foncé pour l'en-tête
                          R % 2 === 1 ? 'FFFFFF' : // Blanc pour les lignes impaires
                          'EFF6FC'; // Bleu très clair pour les lignes paires
          
          // Style de base pour toutes les cellules
          ws[address].s = {
            font: {
              name: 'Arial',
              sz: 10,
              color: { rgb: R === 0 ? 'FFFFFF' : '000000' }, // Blanc pour l'en-tête, noir pour les données
              bold: R === 0 // Gras pour l'en-tête uniquement
            },
            alignment: {
              vertical: 'center',
              wrapText: true
            },
            border: {
              top: { style: 'thin', color: { rgb: R === 0 ? '000000' : 'D9D9D9' } },
              bottom: { style: 'thin', color: { rgb: R === 0 ? '000000' : 'D9D9D9' } },
              left: { style: 'thin', color: { rgb: R === 0 ? '000000' : 'D9D9D9' } },
              right: { style: 'thin', color: { rgb: R === 0 ? '000000' : 'D9D9D9' } }
            },
            fill: { fgColor: { rgb: rowColor } }
          };
          
          // Alignement horizontal spécifique pour l'en-tête
          if (R === 0) {
            ws[address].s.alignment.horizontal = 'center';
          }
          
          // Alignement horizontal spécifique pour les colonnes de données
          if (R > 0) {
            if (C === 0) { // Titre
              ws[address].s.alignment.horizontal = 'left';
            } else if (C === 1 || C === 2) { // Date et Heure
              ws[address].s.alignment.horizontal = 'center';
            } else { // Autres colonnes
              ws[address].s.alignment.horizontal = 'center';
            }
          }
        }
      }
      
      // Ajuster la largeur des colonnes
      const colWidths = [{ wch: 30 }, { wch: 25 }, { wch: 10 }, { wch: 15 }, { wch: 20 }, { wch: 25 }];
      ws['!cols'] = colWidths;
      
      XLSX.utils.book_append_sheet(wb, ws, 'Événements');
      
      // Ajouter une feuille avec les statistiques par type d'événement
      const eventTypes = {};
      personEvents.forEach(event => {
        const type = event.type === 'show' ? 'Spectacle' : 
                    event.type === 'repetition' ? 'Répétition' : 
                    event.type === 'rental' ? 'Location' : event.type;
        eventTypes[type] = (eventTypes[type] || 0) + 1;
      });
      
      const statsData = Object.entries(eventTypes).map(([type, count]) => ({
        'Type d\'événement': type,
        'Nombre': count
      }));
      
      if (statsData.length > 0) {
        const wsStats = XLSX.utils.json_to_sheet(statsData);
        
        // Amélioration du design pour la feuille de statistiques
        const statsRange = XLSX.utils.decode_range(wsStats['!ref']);
        
        // Définir la hauteur de la ligne d'en-tête
        if (!wsStats['!rows']) wsStats['!rows'] = [];
        wsStats['!rows'][0] = { hidden: false, hpt: 28 }; // Hauteur de la première ligne (en-tête)
        
        // Appliquer des styles à toutes les cellules
        for (let R = statsRange.s.r; R <= statsRange.e.r; ++R) {
          for (let C = statsRange.s.c; C <= statsRange.e.c; ++C) {
            const address = XLSX.utils.encode_cell({ r: R, c: C });
            if (!wsStats[address]) continue;
            
            // Déterminer la couleur de fond en fonction de la ligne (alternance)
            const rowColor = R === 0 ? '1F4E79' : // Bleu foncé pour l'en-tête
                            R % 2 === 1 ? 'FFFFFF' : // Blanc pour les lignes impaires
                            'EFF6FC'; // Bleu très clair pour les lignes paires
            
            // Style de base pour toutes les cellules
            wsStats[address].s = {
              font: {
                name: 'Arial',
                sz: 10,
                color: { rgb: R === 0 ? 'FFFFFF' : '000000' }, // Blanc pour l'en-tête, noir pour les données
                bold: R === 0 // Gras pour l'en-tête uniquement
              },
              alignment: {
                vertical: 'center',
                wrapText: true
              },
              border: {
                top: { style: 'thin', color: { rgb: R === 0 ? '000000' : 'D9D9D9' } },
                bottom: { style: 'thin', color: { rgb: R === 0 ? '000000' : 'D9D9D9' } },
                left: { style: 'thin', color: { rgb: R === 0 ? '000000' : 'D9D9D9' } },
                right: { style: 'thin', color: { rgb: R === 0 ? '000000' : 'D9D9D9' } }
              },
              fill: { fgColor: { rgb: rowColor } }
            };
            
            // Alignement horizontal spécifique pour l'en-tête
            if (R === 0) {
              wsStats[address].s.alignment.horizontal = 'center';
            }
            
            // Alignement horizontal spécifique pour les colonnes de données
            if (R > 0) {
              if (C === 0) { // Type d'événement
                wsStats[address].s.alignment.horizontal = 'left';
              } else if (C === 1) { // Nombre
                wsStats[address].s.alignment.horizontal = 'right';
              }
            }
          }
        }
        
        // Ajuster la largeur des colonnes
        wsStats['!cols'] = [{ wch: 25 }, { wch: 15 }];
        
        XLSX.utils.book_append_sheet(wb, wsStats, 'Statistiques');
      }
    } else {
      // Export pour un artistesProd ou membre du personnel
      
      // Préparer les données pour l'export - TOUTES les occurrences individuelles
      const exportData = [];
      
      // Fonction pour créer un objet d'export en fonction du type d'utilisateur
      const createExportObject = (event) => {
        // Déterminer le type de spectacle (production, co-production, ONE)
        const isProd = event.showType === 'production';
        const isCoProd = event.showType === 'coproduction';
        const isOne = event.showType === 'one';
        
        // Récupérer les détails de l'artiste/utilisateur si disponibles
        const personDetails = event.Users && event.Users.find(u => u.id === parseInt(selectedPerson.id));
        
        // Calculer la durée
        const duration = event.start && event.end ? 
          ((new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60)).toFixed(2) : 'N/A';
        
        // Structure de base commune
        // Nouveau bloc
        const baseData = {
          'Titre':             event.title,
          'Type d’événement':  (event.showType || event.type || 'N/A'),
          'Date début':        event.start ? format(new Date(event.start), 'dd/MM/yyyy') : 'N/A',
          'Heure début':       event.start ? format(new Date(event.start), 'HH:mm')     : 'N/A',
          'Date fin':          event.end   ? format(new Date(event.end),   'dd/MM/yyyy') : 'N/A',
          'Heure fin':         event.end   ? format(new Date(event.end),   'HH:mm')     : 'N/A',
          'Durée (h)':         duration,
          'Salle':             resolveRoomName(event),
          'Rôle':              personDetails?.role || 'N/A',
          'Récurrence':        event.recurrenceId && event.isRecurring ? 'Oui' : 'Non'
        };

        if (isArtist) {
          // Pour les artistes : supprimer Durée et Récurrence, ajouter Séances annulées
          const artistData = {
            'Titre':             event.title,
            'Type dévénement':  (event.showType || event.type || 'N/A'),
            'Date début':        event.start ? format(new Date(event.start), 'dd/MM/yyyy') : 'N/A',
            'Heure début':       event.start ? format(new Date(event.start), 'HH:mm')     : 'N/A',
            'Date fin':          event.end   ? format(new Date(event.end),   'dd/MM/yyyy') : 'N/A',
            'Heure fin':         event.end   ? format(new Date(event.end),   'HH:mm')     : 'N/A',
            'Salle':             resolveRoomName(event),
            'Séances annulées':  event.showStatus === 'cancelled' ? 'Oui' : 'Non',
            'Nom artiste':       personDetails?.name || selectedPerson.name || 'N/A'
          };
          return artistData;
        } else {
          // Pour le personnel : ajouter le nom de l'utilisateur
          return {
            ...baseData,
            'Nom utilisateur': personDetails?.name || selectedPerson.name || 'N/A'
          };
        }
      };
      
      // Ajouter TOUTES les occurrences individuelles (pas de regroupement)
      // Trier tous les événements par date
      const sortedEvents = personEvents.sort((a, b) => new Date(a.start) - new Date(b.start));
      
      sortedEvents.forEach(event => {
        exportData.push(createExportObject(event));
      });
      
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Amélioration du design: ajout de styles pour l'en-tête
      const range = XLSX.utils.decode_range(ws['!ref']);
      
      // Définir la hauteur de la ligne d'en-tête
      if (!ws['!rows']) ws['!rows'] = [];
      ws['!rows'][0] = { hidden: false, hpt: 28 }; // Hauteur de la première ligne (en-tête)
      
      // Appliquer des styles à toutes les cellules
      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const address = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[address]) continue;
          
          // Déterminer la couleur de fond en fonction de la ligne (alternance)
          const rowColor = R === 0 ? '1F4E79' : // Bleu foncé pour l'en-tête
                          R % 2 === 1 ? 'FFFFFF' : // Blanc pour les lignes impaires
                          'EFF6FC'; // Bleu très clair pour les lignes paires
          
          // Style de base pour toutes les cellules
          ws[address].s = {
            font: {
              name: 'Arial',
              sz: 10,
              color: { rgb: R === 0 ? 'FFFFFF' : '000000' }, // Blanc pour l'en-tête, noir pour les données
              bold: R === 0 // Gras pour l'en-tête uniquement
            },
            alignment: {
              vertical: 'center',
              wrapText: true
            },
            border: {
              top: { style: 'thin', color: { rgb: R === 0 ? '000000' : 'D9D9D9' } },
              bottom: { style: 'thin', color: { rgb: R === 0 ? '000000' : 'D9D9D9' } },
              left: { style: 'thin', color: { rgb: R === 0 ? '000000' : 'D9D9D9' } },
              right: { style: 'thin', color: { rgb: R === 0 ? '000000' : 'D9D9D9' } }
            },
            fill: { fgColor: { rgb: rowColor } }
          };
          
          // Alignement horizontal spécifique pour l'en-tête
          if (R === 0) {
            ws[address].s.alignment.horizontal = 'center';
          }
          
          // Alignement horizontal spécifique pour les colonnes de données
          if (R > 0) {
            if (C === 0) { // Titre
              ws[address].s.alignment.horizontal = 'left';
            } else if (C === 1) { // Description
              ws[address].s.alignment.horizontal = 'left';
            } else if (C === 2 || C === 3 || C === 4) { // Date, Heure début, Heure fin
              ws[address].s.alignment.horizontal = 'center';
            } else if (C === 5) { // Durée
              ws[address].s.alignment.horizontal = 'right';
            } else { // Autres colonnes
              ws[address].s.alignment.horizontal = 'center';
            }
          }
          
          // Mettre en gras les cellules avec 'X'
          if (ws[address].v === 'X') {
            ws[address].s.font.bold = true;
          }
        }
      }
      
      // Largeurs de colonnes adaptées selon le type d'utilisateur
      let colWidths;
      if (isArtist) {
        // Pour les artistes : A-I (9 colonnes)
        colWidths = [
          { wch: 30 }, // Titre
          { wch: 40 }, // Description
          { wch: 25 }, // Date
          { wch: 12 }, // Heure début
          { wch: 12 }, // Heure fin
          { wch: 10 }, // Durée
          { wch: 20 }, // Salle
          { wch: 25 }, // Récurrence
          { wch: 25 }  // Nom artiste
        ];
      } else {
        // Pour le personnel : A-I (9 colonnes)
        colWidths = [
          { wch: 30 }, // Titre
          { wch: 40 }, // Description
          { wch: 25 }, // Date
          { wch: 12 }, // Heure début
          { wch: 12 }, // Heure fin
          { wch: 10 }, // Durée
          { wch: 20 }, // Salle
          { wch: 25 }, // Récurrence
          { wch: 25 }  // Nom utilisateur
        ];
      }
      ws['!cols'] = colWidths;
      
      XLSX.utils.book_append_sheet(wb, ws, 'Événements');
      
      // Ajouter une ligne avec le total des heures pour le personnel
      if (!isArtist) {
        // Calculer le total des heures en excluant les congés payés
        let totalHours = 0;
        personEvents.forEach(event => {
          const userDetails = event.Users && event.Users.find(u => u.id === parseInt(selectedPerson.id));
          
          // Exclure les congés payés du calcul des heures
          const isCP = userDetails?.through?.isCP || event.type === 'CP' || event.title?.toLowerCase().includes('congé');
          
          if (!isCP) {
            // Calculer la durée de l'événement en heures comme valeur par défaut
            const start = new Date(event.start);
            const end = new Date(event.end);
            const durationHours = ((end - start) / (1000 * 60 * 60));
            
            // Utiliser les heures spécifiques si disponibles, sinon utiliser la durée calculée
            const userHours = userDetails?.through?.hours ? parseFloat(userDetails.through.hours) : durationHours;
            totalHours += userHours;
          }
        });
        
        // Ajouter une ligne vide
        const emptyRow = {};
        Object.keys(exportData[0] || {}).forEach(key => emptyRow[key] = '');
        XLSX.utils.sheet_add_json(ws, [emptyRow], {skipHeader: true, origin: -1});
        
        // Ajouter la ligne de total
        const totalRow = {};
        Object.keys(exportData[0] || {}).forEach(key => totalRow[key] = '');
        totalRow['Titre'] = 'TOTAL HEURES';
        totalRow['Durée (h)'] = totalHours.toFixed(1) + ' h';
        XLSX.utils.sheet_add_json(ws, [totalRow], {skipHeader: true, origin: -1});
        
        // Appliquer le style à la ligne de total
        const newRange = XLSX.utils.decode_range(ws['!ref']);
        const totalRowIndex = newRange.e.r;
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const address = XLSX.utils.encode_col(C) + totalRowIndex;
          if (!ws[address]) continue;
          ws[address].s = {
            font: { bold: true, color: { rgb: 'FFFFFF' } },
            fill: { patternType: 'solid', fgColor: { rgb: 'A683CC' } },
            alignment: { horizontal: 'center' }
          };
        }
      }
      
      // Si c'est un membre du personnel (non artistesProd), ajouter une feuille pour les jours de travail
      if (!isArtist) {
        // Récupérer les jours de travail regroupés par mois
        const workDays = getWorkDays();
        
        if (workDays.length > 0) {
          // Préparer les données pour l'export des jours de travail
          const workDaysData = [];
          
          // Pour chaque mois, ajouter une ligne avec le nom du mois
          let totalMonthlyHours = 0;
          
          workDays.forEach(monthData => {
            // Calculer le total des heures pour ce mois
            let monthlyHours = 0;
            if (monthData.locations && monthData.locations.length > 0) {
              monthlyHours = monthData.locations.reduce((sum, location) => sum + location.hours, 0);
              totalMonthlyHours += monthlyHours;
            }
            
            // Ajouter une ligne pour le mois avec le total des heures
            workDaysData.push({
              'Mois': monthData.month,
              'Jours travaillés': `${monthData.days.join(', ')} - Total: ${monthlyHours.toFixed(1)} h`
            });
            
            // Ajouter les détails par lieu si disponibles
            if (monthData.locations && monthData.locations.length > 0) {
              monthData.locations.forEach(location => {
                workDaysData.push({
                  'Mois': `  - ${location.name}`,
                  'Jours travaillés': `${location.count} événement(s) (${location.hours} h)`
                });
              });
            }
          });
          
          // Ajouter une ligne pour le total général des heures
          workDaysData.push({
            'Mois': 'TOTAL GÉNÉRAL',
            'Jours travaillés': `${totalMonthlyHours.toFixed(1)} h`
          });
          
          // Créer une feuille pour les jours de travail
          const wsWorkDays = XLSX.utils.json_to_sheet(workDaysData);
          
          // Amélioration du design pour la feuille des jours de travail
          const workDaysRange = XLSX.utils.decode_range(wsWorkDays['!ref']);
          
          // Style pour les en-têtes
          for (let C = workDaysRange.s.c; C <= workDaysRange.e.c; ++C) {
            const address = XLSX.utils.encode_col(C) + '1';
            if (!wsWorkDays[address]) continue;
            wsWorkDays[address].s = {
              font: { bold: true, color: { rgb: 'FFFFFF' } },
              fill: { patternType: 'solid', fgColor: { rgb: '4F46E5' } },
              alignment: { horizontal: 'center' }
            };
          }
          
          // Style pour la ligne du total général (dernière ligne)
          const totalRowIndex = workDaysData.length;
          for (let C = workDaysRange.s.c; C <= workDaysRange.e.c; ++C) {
            const address = XLSX.utils.encode_col(C) + totalRowIndex;
            if (!wsWorkDays[address]) continue;
            wsWorkDays[address].s = {
              font: { bold: true, color: { rgb: 'FFFFFF' } },
              fill: { patternType: 'solid', fgColor: { rgb: 'A683CC' } }, // Violet (couleur régie)
              alignment: { horizontal: 'center', vertical: 'center' },
              border: {
                top: { style: 'thin', color: { rgb: '000000' } },
                left: { style: 'thin', color: { rgb: '000000' } },
                bottom: { style: 'thin', color: { rgb: '000000' } },
                right: { style: 'thin', color: { rgb: '000000' } }
              }
            };
          }
          
          // Ajuster la largeur des colonnes
          wsWorkDays['!cols'] = [{ wch: 20 }, { wch: 50 }];
          
          // Ajouter la feuille au classeur
          XLSX.utils.book_append_sheet(wb, wsWorkDays, 'Jours travaillés');
          
          // Ajouter la feuille "Informations Utilisateurs" après "Jours travaillés"
          try {
            // Récupérer la liste des utilisateurs
            const response = await api.get('/users');
            const users = response.data.data.users;
            
            // Trouver l'utilisateur correspondant au nom sélectionné
            const selectedUser = users.find(user => 
              `${user.firstName} ${user.lastName}` === selectedPerson.name ||
              `${user.lastName} ${user.firstName}` === selectedPerson.name ||
              user.firstName === selectedPerson.name ||
              user.lastName === selectedPerson.name
            );
            
            if (selectedUser) {
              // Préparer les données pour l'export (seulement l'utilisateur sélectionné)
              const userData = [
                ['Nom', 'Prénom', 'Email', 'Téléphone', 'Adresse', 'Date de naissance', 'Lieu de naissance', 'Numéro de sécurité sociale', 'IBAN', 'Rôle'],
                [
                  selectedUser.lastName || '',
                  selectedUser.firstName || '',
                  selectedUser.email || '',
                  selectedUser.phone || '',
                  selectedUser.address || '',
                  selectedUser.birthDate ? (() => {
                    try {
                      const date = new Date(selectedUser.birthDate);
                      if (isNaN(date.getTime())) return '';
                      return format(date, 'dd/MM/yyyy');
                    } catch (error) {
                      console.warn('Erreur lors du formatage de la date de naissance:', selectedUser.birthDate, error);
                      return '';
                    }
                  })() : '',
                  selectedUser.birthPlace || '',
                  selectedUser.socialSecurityNumber || '',
                  formatIban(selectedUser.iban || ''),
                  selectedUser.role || ''
                ]
              ];
            
              // Créer la feuille de calcul
              const wsUsers = XLSX.utils.aoa_to_sheet(userData);
              
              // Définir la largeur des colonnes
              wsUsers['!cols'] = [
                { wch: 15 }, // Nom
                { wch: 15 }, // Prénom
                { wch: 25 }, // Email
                { wch: 15 }, // Téléphone
                { wch: 30 }, // Adresse
                { wch: 15 }, // Date de naissance
                { wch: 20 }, // Lieu de naissance
                { wch: 20 }, // Numéro de sécurité sociale
                { wch: 25 }, // IBAN
                { wch: 15 }  // Rôle
              ];
              
              // Appliquer des styles à l'en-tête
              const usersRange = XLSX.utils.decode_range(wsUsers['!ref']);
              for (let C = usersRange.s.c; C <= usersRange.e.c; ++C) {
                const address = XLSX.utils.encode_col(C) + '1';
                if (!wsUsers[address]) continue;
                wsUsers[address].s = {
                  font: { bold: true, color: { rgb: 'FFFFFF' } },
                  fill: { patternType: 'solid', fgColor: { rgb: '2F7A4B' } }, // Vert
                  alignment: { horizontal: 'center' }
                };
              }
              
              // Appliquer des styles aux données
              for (let R = 1; R <= usersRange.e.r; ++R) {
                for (let C = usersRange.s.c; C <= usersRange.e.c; ++C) {
                  const address = XLSX.utils.encode_col(C) + (R + 1);
                  if (!wsUsers[address]) continue;
                  
                  // Alignement spécifique par colonne
                  let alignment = { horizontal: 'left' };
                  if (C === 2 || C === 4) { // Email et Adresse
                    alignment = { horizontal: 'left' };
                  } else if (C === 5 || C === 9) { // Date de naissance et Rôle
                    alignment = { horizontal: 'center' };
                  }
                  
                  wsUsers[address].s = {
                    alignment: alignment,
                    border: {
                      top: { style: 'thin', color: { rgb: 'D9D9D9' } },
                      bottom: { style: 'thin', color: { rgb: 'D9D9D9' } },
                      left: { style: 'thin', color: { rgb: 'D9D9D9' } },
                      right: { style: 'thin', color: { rgb: 'D9D9D9' } }
                    }
                  };
                }
              }
              
              // Ajouter la feuille au classeur
              XLSX.utils.book_append_sheet(wb, wsUsers, 'Informations Utilisateurs');
            }
            
          } catch (error) {
            console.error('Erreur lors de l\'ajout des informations utilisateurs:', error);
          }
        }
      }
    }
    
    // Si c'est un artiste (artistesProd), ajouter une feuille avec les informations utilisateur
    if (isArtist) {
      try {
        // Récupérer la liste des utilisateurs
        const response = await api.get('/users');
        const users = response.data.data.users;
        
        // Trouver l'utilisateur correspondant au nom sélectionné
        const selectedUser = users.find(user => 
          `${user.firstName} ${user.lastName}` === selectedPerson.name ||
          `${user.lastName} ${user.firstName}` === selectedPerson.name ||
          user.firstName === selectedPerson.name ||
          user.lastName === selectedPerson.name
        );
        
        if (selectedUser) {
          // Préparer les données pour l'export (seulement l'utilisateur sélectionné)
          const userData = [
            ['Nom', 'Prénom', 'Email', 'Téléphone', 'Adresse', 'Date de naissance', 'Lieu de naissance', 'Numéro de sécurité sociale', 'IBAN', 'Rôle'],
            [
              selectedUser.lastName || '',
              selectedUser.firstName || '',
              selectedUser.email || '',
              selectedUser.phone || '',
              selectedUser.address || '',
              selectedUser.birthDate ? (() => {
                try {
                  const date = new Date(selectedUser.birthDate);
                  if (isNaN(date.getTime())) return '';
                  return format(date, 'dd/MM/yyyy');
                } catch (error) {
                  console.warn('Erreur lors du formatage de la date de naissance:', selectedUser.birthDate, error);
                  return '';
                }
              })() : '',
              selectedUser.birthPlace || '',
              selectedUser.socialSecurityNumber || '',
              formatIban(selectedUser.iban || ''),
              selectedUser.role || ''
            ]
          ];
        
          // Créer la feuille de calcul
          const wsUsers = XLSX.utils.aoa_to_sheet(userData);
          
          // Définir la largeur des colonnes
          wsUsers['!cols'] = [
            { wch: 15 }, // Nom
            { wch: 15 }, // Prénom
            { wch: 25 }, // Email
            { wch: 15 }, // Téléphone
            { wch: 30 }, // Adresse
            { wch: 15 }, // Date de naissance
            { wch: 20 }, // Lieu de naissance
            { wch: 20 }, // Numéro de sécurité sociale
            { wch: 25 }, // IBAN
            { wch: 15 }  // Rôle
          ];
          
          // Appliquer des styles à l'en-tête
          const usersRange = XLSX.utils.decode_range(wsUsers['!ref']);
          for (let C = usersRange.s.c; C <= usersRange.e.c; ++C) {
            const address = XLSX.utils.encode_col(C) + '1';
            if (!wsUsers[address]) continue;
            wsUsers[address].s = {
              font: { bold: true, color: { rgb: 'FFFFFF' } },
              fill: { patternType: 'solid', fgColor: { rgb: '2F7A4B' } }, // Vert
              alignment: { horizontal: 'center' }
            };
          }
          
          // Appliquer des styles aux données
          for (let R = 1; R <= usersRange.e.r; ++R) {
            for (let C = usersRange.s.c; C <= usersRange.e.c; ++C) {
              const address = XLSX.utils.encode_col(C) + (R + 1);
              if (!wsUsers[address]) continue;
              
              // Alignement spécifique par colonne
              let alignment = { horizontal: 'left' };
              if (C === 2 || C === 4) { // Email et Adresse
                alignment = { horizontal: 'left' };
              } else if (C === 5 || C === 9) { // Date de naissance et Rôle
                alignment = { horizontal: 'center' };
              }
              
              wsUsers[address].s = {
                alignment: alignment,
                border: {
                  top: { style: 'thin', color: { rgb: 'D9D9D9' } },
                  bottom: { style: 'thin', color: { rgb: 'D9D9D9' } },
                  left: { style: 'thin', color: { rgb: 'D9D9D9' } },
                  right: { style: 'thin', color: { rgb: 'D9D9D9' } }
                }
              };
            }
          }
          
          // Ajouter la feuille au classeur
          XLSX.utils.book_append_sheet(wb, wsUsers, 'Informations Utilisateur');
        }
        
      } catch (error) {
        console.error('Erreur lors de l\'ajout des informations utilisateur:', error);
      }
    }
    
    // Générer le nom du fichier
    const fileName = `${isRoom ? 'Salle' : isArtist ? 'artistesProd' : isAbsence ? 'Absences' : 'Personnel'}_${selectedPerson.name.replace(/ /g, '_')}_${format(new Date(), 'yyyy')}.xlsx`;
    
    // Télécharger le fichier
    XLSX.writeFile(wb, fileName);
  };

  // Nouvelle fonction pour exporter les statistiques générales en PDF (heures totales)
  const exportGeneralStatsToPDF = () => {
    const doc = new jsPDF();
    
    // Couleurs du thème
    const primaryColor = [159, 21, 21]; // Rouge du site #9F1515
    const secondaryColor = [31, 78, 121]; // Bleu foncé
    const accentColor = [248, 250, 252]; // Gris très clair
    const textColor = [55, 65, 81]; // Gris foncé
    
    // En-tête avec design moderne
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 35, 'F');
    
    // Titre principal
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('TABLEAU DE BORD - STATISTIQUES GÉNÉRALES', 20, 20);
    
    // Sous-titre
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Comédie des Suds', 20, 28);
    
    // Informations de période et date
    doc.setTextColor(...textColor);
    doc.setFontSize(11);
    doc.text(`Période: ${formattedPeriod}`, 20, 45);
    doc.text(`Généré le: ${format(new Date(), 'dd/MM/yyyy à HH:mm')}`, 20, 52);
    
    let yPosition = 70;
    
    // Calculer les statistiques à partir des données existantes
    const filteredEvents = selectedRoom === 'all' 
      ? originalEvents 
      : originalEvents.filter(event => event.roomId === parseInt(selectedRoom));
    
    const totalEvents = filteredEvents.length;
    const showsCount = filteredEvents.filter(event => {
      const eventType = event.type ? event.type.toLowerCase() : '';
      const eventTitle = event.title ? event.title.toLowerCase() : '';
      return eventType === 'show' || eventType.includes('spectacle') || eventTitle.includes('spectacle');
    }).length;
    const repetitionsCount = filteredEvents.filter(event => event.type === 'repetition').length;
    const rentalsCount = filteredEvents.filter(event => event.type === 'rental').length;
    const otherEventsCount = totalEvents - showsCount - repetitionsCount - rentalsCount;
    
    // 1. Statistiques générales avec design amélioré
    doc.setTextColor(...primaryColor);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('STATISTIQUES GENERALES', 20, yPosition);
    yPosition += 5;
    
    // Ligne de séparation
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.5);
    doc.line(20, yPosition, 190, yPosition);
    yPosition += 10;
    
    const generalStats = [
      ['Total événements', totalEvents.toString()],
      ['Spectacles', showsCount.toString()],
      ['Répétitions', repetitionsCount.toString()],
      ['Locations', rentalsCount.toString()],
      ['Autres événements', otherEventsCount.toString()]
    ];
    
    autoTable(doc, {
      startY: yPosition,
      head: [['Type d\'evenement', 'Nombre']],
      body: generalStats,
      theme: 'striped',
      headStyles: { 
        fillColor: primaryColor,
        textColor: [255, 255, 255],
        fontSize: 12,
        fontStyle: 'bold',
        halign: 'center'
      },
      bodyStyles: {
        fontSize: 11,
        textColor: textColor
      },
      alternateRowStyles: {
        fillColor: accentColor
      },
      columnStyles: {
        0: { halign: 'left', cellWidth: 120 },
        1: { halign: 'center', cellWidth: 50, fontStyle: 'bold' }
      },
      margin: { left: 20, right: 20 }
    });
    
    yPosition = doc.lastAutoTable.finalY + 20;
    
    // 2. Statuts des spectacles avec design amélioré
    if (yPosition > 220) {
      doc.addPage();
      yPosition = 30;
    }
    
    doc.setTextColor(...primaryColor);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('STATUTS DES SPECTACLES', 20, yPosition);
    yPosition += 5;
    
    // Ligne de séparation
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.5);
    doc.line(20, yPosition, 190, yPosition);
    yPosition += 10;
    
    // Calculer les statuts des spectacles
    const showEvents = filteredEvents.filter(event => {
      const eventType = event.type ? event.type.toLowerCase() : '';
      const eventTitle = event.title ? event.title.toLowerCase() : '';
      return eventType === 'show' || eventType.includes('spectacle') || eventTitle.includes('spectacle');
    });
    
    const confirmedShows = showEvents.filter(event => event.showStatus === 'confirmed').length;
    const provisionalShows = showEvents.filter(event => event.showStatus === 'provisional').length;
    const ticketsOpenShows = showEvents.filter(event => event.showStatus === 'ticketsOpen').length;
    const cancelledShows = showEvents.filter(event => event.showStatus === 'cancelled').length;
    
    const statusStats = [
      ['Confirmes', confirmedShows.toString()],
      ['Provisoires', provisionalShows.toString()],
      ['Billetterie ouverte', ticketsOpenShows.toString()],
      ['Annules', cancelledShows.toString()]
    ];
    
    autoTable(doc, {
      startY: yPosition,
      head: [['Statut du spectacle', 'Nombre']],
      body: statusStats,
      theme: 'striped',
      headStyles: { 
        fillColor: secondaryColor,
        textColor: [255, 255, 255],
        fontSize: 12,
        fontStyle: 'bold',
        halign: 'center'
      },
      bodyStyles: {
        fontSize: 11,
        textColor: textColor
      },
      alternateRowStyles: {
        fillColor: accentColor
      },
      columnStyles: {
        0: { halign: 'left', cellWidth: 120 },
        1: { halign: 'center', cellWidth: 50, fontStyle: 'bold' }
      },
      margin: { left: 20, right: 20 }
    });
    
    yPosition = doc.lastAutoTable.finalY + 20;
    
    // 3. Statistiques des artistes avec design amélioré
    if (artistStats.length > 0) {
      if (yPosition > 180) {
        doc.addPage();
        yPosition = 30;
      }
      
      doc.setTextColor(...primaryColor);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('STATISTIQUES DES ARTISTES', 20, yPosition);
      yPosition += 5;
      
      // Ligne de séparation
      doc.setDrawColor(...primaryColor);
      doc.setLineWidth(0.5);
      doc.line(20, yPosition, 190, yPosition);
      yPosition += 10;
      
      const artistData = artistStats.map(artist => [
        artist.name,
        artist.count.toString()
      ]);
      
      autoTable(doc, {
         startY: yPosition,
         head: [['Nom de l\'artiste', 'Nombre de spectacles']],
         body: artistData,
         theme: 'striped',
         headStyles: { 
           fillColor: [159, 21, 21], // Rouge du site #9F1515
           textColor: [255, 255, 255],
           fontSize: 12,
           fontStyle: 'bold',
           halign: 'center'
         },
         bodyStyles: {
           fontSize: 11,
           textColor: textColor
         },
         alternateRowStyles: {
           fillColor: accentColor
         },
         columnStyles: {
           0: { halign: 'left', cellWidth: 120 },
           1: { halign: 'center', cellWidth: 50, fontStyle: 'bold' }
         },
         margin: { left: 20, right: 20 }
       });
       
       yPosition = doc.lastAutoTable.finalY + 20;
    }
    
    // 4. Heures totales du personnel (sans gestion du personnel)
    if (staffStats.length > 0) {
      if (yPosition > 120) {
        doc.addPage();
        yPosition = 30;
      }
      
      doc.setTextColor(...primaryColor);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('HEURES TOTALES DU PERSONNEL', 20, yPosition);
      yPosition += 5;
      
      // Ligne de séparation
      doc.setDrawColor(...primaryColor);
      doc.setLineWidth(0.5);
      doc.line(20, yPosition, 190, yPosition);
      yPosition += 10;
      
      // Note importante avec design amélioré
      doc.setFillColor(255, 243, 205); // Fond jaune clair
      doc.rect(20, yPosition, 170, 20, 'F');
      doc.setDrawColor(255, 193, 7); // Bordure jaune
      doc.setLineWidth(0.5);
      doc.rect(20, yPosition, 170, 20, 'S');
      
      doc.setTextColor(180, 83, 9); // Orange foncé
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('IMPORTANT:', 25, yPosition + 7);
      doc.setFont('helvetica', 'normal');
      doc.text('Exclut la gestion du personnel, inclut uniquement le télétravail.', 25, yPosition + 14);
      yPosition += 30;
      
      // Données du personnel avec heures calculées (sans gestion du personnel)
      const staffData = staffStats.map(staff => [
        staff.name,
        staff.role,
        staff.hours.toString() + ' h'
      ]);
      
      autoTable(doc, {
          startY: yPosition,
          head: [['Membre du personnel', 'Rôle', 'Heures travaillées']],
          body: staffData,
          theme: 'striped',
          headStyles: { 
            fillColor: [159, 21, 21], // Rouge du site #9F1515
            textColor: [255, 255, 255],
            fontSize: 12,
            fontStyle: 'bold',
            halign: 'center'
          },
          bodyStyles: {
            fontSize: 11,
            textColor: textColor
          },
          alternateRowStyles: {
            fillColor: accentColor
          },
          columnStyles: {
            0: { halign: 'left', cellWidth: 70 },
            1: { halign: 'center', cellWidth: 60 },
            2: { halign: 'center', cellWidth: 40, fontStyle: 'bold' }
          },
          margin: { left: 20, right: 20 }
        });
    }
    
    // Ajouter un pied de page professionnel
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      // Ligne de séparation en bas
      doc.setDrawColor(...primaryColor);
      doc.setLineWidth(0.3);
      doc.line(20, 280, 190, 280);
      
      // Informations du pied de page
      doc.setTextColor(128, 128, 128);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('Comédie des Suds - Tableau de bord', 20, 285);
      doc.text(`Page ${i} sur ${pageCount}`, 190, 285, { align: 'right' });
      doc.text(`Genere le ${format(new Date(), 'dd/MM/yyyy a HH:mm')}`, 105, 290, { align: 'center' });
    }
    
    // Générer le nom du fichier PDF
    const fileName = `Statistiques_Dashboard_${formattedPeriod.replace(/ /g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
    
    // Télécharger le fichier PDF
    doc.save(fileName);
  };

  // Nouvelle fonction pour exporter uniquement les statistiques du personnel en PDF
  const exportPersonnelStatsToPDF = () => {
    const doc = new jsPDF();
    
    // Couleurs du thème
    const primaryColor = [159, 21, 21]; // Rouge du site #9F1515
    const secondaryColor = [31, 78, 121]; // Bleu foncé
    const accentColor = [248, 250, 252]; // Gris très clair
    const textColor = [55, 65, 81]; // Gris foncé
    
    // En-tête avec design moderne
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 35, 'F');
    
    // Titre principal
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('RAPPORT DÉTAILLÉ DU PERSONNEL', 20, 20);
    
    // Sous-titre
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Comédie des Suds', 20, 28);
    
    // Informations de période et date
    doc.setTextColor(...textColor);
    doc.setFontSize(11);
    doc.text(`Période: ${formattedPeriod}`, 20, 45);
    doc.text(`Généré le: ${format(new Date(), 'dd/MM/yyyy à HH:mm')}`, 20, 52);
    
    let yPosition = 70;
    
    // 1. Heures du personnel avec design amélioré
    if (staffStats.length > 0) {
      doc.setTextColor(...primaryColor);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('HEURES DU PERSONNEL', 20, yPosition);
      yPosition += 5;
      
      // Ligne de séparation
      doc.setDrawColor(...primaryColor);
      doc.setLineWidth(0.5);
      doc.line(20, yPosition, 190, yPosition);
      yPosition += 10;
      
      // Note importante avec design amélioré
      doc.setFillColor(255, 243, 205); // Fond jaune clair
      doc.rect(20, yPosition, 170, 20, 'F');
      doc.setDrawColor(255, 193, 7); // Bordure jaune
      doc.setLineWidth(0.5);
      doc.rect(20, yPosition, 170, 20, 'S');
      
      doc.setTextColor(180, 83, 9); // Orange foncé
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('IMPORTANT:', 25, yPosition + 7);
      doc.setFont('helvetica', 'normal');
      doc.text('Inclut la gestion du personnel et le télétravail.', 25, yPosition + 14);
      yPosition += 30;
      
      // Créer une version des stats du personnel incluant la gestion du personnel
      const staffStatsWithManagement = staffStats.map(staff => ({
        ...staff,
        // Inclure toutes les heures y compris gestion du personnel
        hours: staff.hours
      }));
      
      // Données du personnel avec heures calculées (incluant gestion du personnel)
      const staffData = staffStatsWithManagement.map(staff => [
        staff.name,
        staff.role,
        staff.hours.toString() + ' h'
      ]);
      
      autoTable(doc, {
          startY: yPosition,
          head: [['Membre du personnel', 'Rôle', 'Heures travaillées']],
          body: staffData,
          theme: 'striped',
          headStyles: { 
            fillColor: [159, 21, 21], // Rouge du site #9F1515
            textColor: [255, 255, 255],
            fontSize: 12,
            fontStyle: 'bold',
            halign: 'center'
          },
          bodyStyles: {
            fontSize: 11,
            textColor: textColor
          },
          alternateRowStyles: {
            fillColor: accentColor
          },
          columnStyles: {
            0: { halign: 'left', cellWidth: 70 },
            1: { halign: 'center', cellWidth: 60 },
            2: { halign: 'center', cellWidth: 40, fontStyle: 'bold' }
          },
          margin: { left: 20, right: 20 }
        });        
        yPosition = doc.lastAutoTable.finalY + 20;
        
        // Ajouter le résumé standardisé des heures du personnel
        const allPersonnelEvents = originalEvents.filter(event => {
          const eventStart = new Date(event.start);
          return eventStart >= startDate && eventStart <= endDate;
        });
        
        const summary = computePersonnelSummary(allPersonnelEvents, 7);
        yPosition = renderPersonnelSummaryPDF(doc, summary, yPosition);
    }
    
    // 2. Absences du personnel
    if (yPosition > 200) {
      doc.addPage();
      yPosition = 30;
    }
    
    doc.setTextColor(...primaryColor);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('ABSENCES DU PERSONNEL', 20, yPosition);
    yPosition += 5;
    
    // Ligne de séparation
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.5);
    doc.line(20, yPosition, 190, yPosition);
    yPosition += 10;
    
    // Calculer les absences
    const absenceEvents = originalEvents.filter(event => 
      event.type === 'personnel' && 
      event.title && 
      (event.title.toLowerCase().includes('absence') || 
       event.title.toLowerCase().includes('congé') ||
       event.title.toLowerCase().includes('maladie'))
    );
    
    if (absenceEvents.length > 0) {
      const absenceData = absenceEvents.map(event => [
        event.assignedTo || 'Non assigné',
        event.title,
        format(new Date(event.start), 'dd/MM/yyyy'),
        format(new Date(event.end), 'dd/MM/yyyy')
      ]);
      
      autoTable(doc, {
        startY: yPosition,
        head: [['Personnel', 'Type d\'absence', 'Date début', 'Date fin']],
        body: absenceData,
        theme: 'striped',
        headStyles: { 
          fillColor: secondaryColor,
          textColor: [255, 255, 255],
          fontSize: 12,
          fontStyle: 'bold',
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 10,
          textColor: textColor
        },
        alternateRowStyles: {
          fillColor: accentColor
        },
        columnStyles: {
          0: { halign: 'left', cellWidth: 50 },
          1: { halign: 'left', cellWidth: 60 },
          2: { halign: 'center', cellWidth: 40 },
          3: { halign: 'center', cellWidth: 40 }
        },
        margin: { left: 20, right: 20 }
      });
      
      yPosition = doc.lastAutoTable.finalY + 20;
    } else {
      doc.setTextColor(128, 128, 128);
      doc.setFontSize(11);
      doc.text('Aucune absence enregistrée pour cette période.', 20, yPosition);
      yPosition += 20;
    }
    
    // 3. Indisponibilités des utilisateurs
    if (yPosition > 200) {
      doc.addPage();
      yPosition = 30;
    }
    
    doc.setTextColor(...primaryColor);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('INDISPONIBILITÉS DES UTILISATEURS', 20, yPosition);
    yPosition += 5;
    
    // Ligne de séparation
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.5);
    doc.line(20, yPosition, 190, yPosition);
    yPosition += 10;
    
    // Calculer les indisponibilités
    const unavailabilityEvents = originalEvents.filter(event => 
      event.type === 'personnel' && 
      event.title && 
      event.title.toLowerCase().includes('indisponibilité')
    );
    
    if (unavailabilityEvents.length > 0) {
      const unavailabilityData = unavailabilityEvents.map(event => [
        event.assignedTo || 'Non assigné',
        event.title,
        format(new Date(event.start), 'dd/MM/yyyy'),
        format(new Date(event.end), 'dd/MM/yyyy')
      ]);
      
      autoTable(doc, {
        startY: yPosition,
        head: [['Personnel', 'Type d\'indisponibilité', 'Date début', 'Date fin']],
        body: unavailabilityData,
        theme: 'striped',
        headStyles: { 
          fillColor: secondaryColor,
          textColor: [255, 255, 255],
          fontSize: 12,
          fontStyle: 'bold',
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 10,
          textColor: textColor
        },
        alternateRowStyles: {
          fillColor: accentColor
        },
        columnStyles: {
          0: { halign: 'left', cellWidth: 50 },
          1: { halign: 'left', cellWidth: 60 },
          2: { halign: 'center', cellWidth: 40 },
          3: { halign: 'center', cellWidth: 40 }
        },
        margin: { left: 20, right: 20 }
      });
      
      yPosition = doc.lastAutoTable.finalY + 20;
    } else {
      doc.setTextColor(128, 128, 128);
      doc.setFontSize(11);
      doc.text('Aucune indisponibilité enregistrée pour cette période.', 20, yPosition);
      yPosition += 20;
    }
    
    // 4. Cachets des artistes
    if (artistStats.length > 0) {
      if (yPosition > 180) {
        doc.addPage();
        yPosition = 30;
      }
      
      doc.setTextColor(...primaryColor);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('CACHETS DES ARTISTES', 20, yPosition);
      yPosition += 5;
      
      // Ligne de séparation
      doc.setDrawColor(...primaryColor);
      doc.setLineWidth(0.5);
      doc.line(20, yPosition, 190, yPosition);
      yPosition += 10;
      
      const artistData = artistStats.map(artist => [
        artist.name,
        artist.count.toString() + ' spectacle(s)'
      ]);
      
      autoTable(doc, {
         startY: yPosition,
         head: [['Nom de l\'artiste', 'Nombre de cachets']],
         body: artistData,
         theme: 'striped',
         headStyles: { 
           fillColor: [159, 21, 21], // Rouge du site #9F1515
           textColor: [255, 255, 255],
           fontSize: 12,
           fontStyle: 'bold',
           halign: 'center'
         },
         bodyStyles: {
           fontSize: 11,
           textColor: textColor
         },
         alternateRowStyles: {
           fillColor: accentColor
         },
         columnStyles: {
           0: { halign: 'left', cellWidth: 120 },
           1: { halign: 'center', cellWidth: 50, fontStyle: 'bold' }
         },
         margin: { left: 20, right: 20 }
       });
    }
    
    // Ajouter un pied de page professionnel
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      // Ligne de séparation en bas
      doc.setDrawColor(...primaryColor);
      doc.setLineWidth(0.3);
      doc.line(20, 280, 190, 280);
      
      // Informations du pied de page
      doc.setTextColor(128, 128, 128);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('Comédie des Suds - Rapport Personnel', 20, 285);
      doc.text(`Page ${i} sur ${pageCount}`, 190, 285, { align: 'right' });
      doc.text(`Généré le ${format(new Date(), 'dd/MM/yyyy à HH:mm')}`, 105, 290, { align: 'center' });
    }
    
    // Générer le nom du fichier PDF
    const fileName = `Rapport_Personnel_${formattedPeriod.replace(/ /g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
    
    // Télécharger le fichier PDF
    doc.save(fileName);
  };
  
  // Fonction pour exporter le rapport de gestion du personnel (billetterie)
  const exportPersonnelManagementPDF = (selectedPerson) => {
    if (!selectedPerson) {
      alert('Veuillez sélectionner une personne.');
      return;
    }

    // Filtrer les événements pour la personne sélectionnée
    const personEvents = originalEvents.filter(event => {
      if (event.Users && Array.isArray(event.Users)) {
        return event.Users.some(user => user.id === selectedPerson.id);
      }
      return false;
    });

    if (personEvents.length === 0) {
      alert('Aucun événement trouvé pour cette personne.');
      return;
    }

    // Séparer les événements de travail et de gestion du personnel
    const workEvents = personEvents.filter(event => {
      const eventType = event.type?.toLowerCase() || '';
      return eventType !== 'personnel';
    });

    const managementEvents = personEvents.filter(event => {
      const eventType = event.type?.toLowerCase() || '';
      return eventType === 'personnel';
    });

    // Créer le document PDF
    const doc = new jsPDF();
    const primaryColor = [165, 42, 42]; // Rouge bordeaux
    const secondaryColor = [245, 245, 245]; // Gris clair
    
    // En-tête avec logo et titre
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 40, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('RAPPORT DE GESTION DU PERSONNEL', 105, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`${selectedPerson.firstName} ${selectedPerson.lastName} - ${selectedPerson.role}`, 105, 30, { align: 'center' });
    
    // Informations de période
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    const formattedPeriod = `${format(startDate, 'MMMM yyyy', { locale: fr })}`;
    doc.text(`Période: ${formattedPeriod}`, 20, 50);
    doc.text(`Généré le: ${format(new Date(), 'dd/MM/yyyy à HH:mm')}`, 20, 56);
    
    // Calculer les statistiques de gestion du personnel
    let totalCongesJours = 0;
    let totalMaladieHeures = 0;
    let totalAbsenceHeures = 0;
    let totalTeletravailHeures = 0;
    
    const managementStats = {
      conges: 0,
      maladie: 0,
      absence: 0,
      teletravail: 0
    };
    
    managementEvents.forEach(event => {
      const eventType = event.eventType?.toLowerCase() || '';
      const title = event.title?.toLowerCase() || '';
      const description = event.description?.toLowerCase() || '';
      
      const duration = event.end ? 
        (new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60) : 0;
      
      if (eventType === 'cp' || title.includes('congé')) {
        managementStats.conges++;
        // Convertir les heures en jours (8h = 1 jour)
        totalCongesJours += duration / 7;
      } else if (eventType === 'maladie' || title.includes('maladie') || description.includes('maladie')) {
        managementStats.maladie++;
        totalMaladieHeures += duration;
      } else if (eventType === 'absence' || title.includes('absence') || description.includes('absence')) {
        managementStats.absence++;
        totalAbsenceHeures += duration;
      } else if (eventType === 'télétravail' || title.includes('télétravail') || description.includes('télétravail')) {
        managementStats.teletravail++;
        totalTeletravailHeures += duration;
      }
    });
    
    // Section Détails des événements
    let yPosition = 70;
    doc.setFillColor(...secondaryColor);
    doc.rect(20, yPosition, 170, 8, 'F');
    
    doc.setTextColor(...primaryColor);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('DÉTAILS DES ÉVÉNEMENTS', 25, yPosition + 5);
    
    // Préparer les données du tableau avec TOUS les événements
    const tableData = [];
    
    // Calculer les totaux
    let totalWorkHours = 0;
    let totalManagementEvents = managementEvents.length;
    let totalWorkEvents = workEvents.length;
    
    // Trier tous les événements par date
    const allSortedEvents = personEvents.sort((a, b) => new Date(a.start) - new Date(b.start));
    
    allSortedEvents.forEach(event => {
      const duration = event.end ? 
        (new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60) : 0;
      
      let eventTypeText = 'Autre';
      let eventCategory = 'Travail';
      const eventType = event.type?.toLowerCase() || '';
      const title = event.title?.toLowerCase() || '';
      const description = event.description?.toLowerCase() || '';
      
      // Déterminer le type d'événement
      if (eventType === 'personnel') {
        eventCategory = 'Gestion';
        if (eventType === 'cp' || title.includes('congé')) {
          eventTypeText = 'Congés payés';
        } else if (title.includes('maladie') || description.includes('maladie')) {
          eventTypeText = 'Maladie';
        } else if (title.includes('absence') || description.includes('absence')) {
          eventTypeText = 'Absence';
        } else if (title.includes('télétravail') || description.includes('télétravail')) {
          eventTypeText = 'Télétravail';
        } else {
          eventTypeText = 'Gestion du personnel';
        }
      } else {
        // Événements de travail
        totalWorkHours += duration;
        if (eventType === 'show' || eventType.includes('spectacle') || title.includes('spectacle')) {
          eventTypeText = 'Spectacle';
        } else if (eventType === 'rehearsal' || eventType.includes('répétition') || title.includes('répétition')) {
          eventTypeText = 'Répétition';
        } else if (eventType === 'rental' || eventType.includes('location') || title.includes('location')) {
          eventTypeText = 'Location';
        } else if (eventType === 'event') {
          eventTypeText = 'Événement';
        } else {
          eventTypeText = eventType || 'Autre';
        }
      }
      
      // Obtenir le nom de la salle
      const roomName = event.Room ? event.Room.name : (event.roomName || 'Non spécifiée');
      
      tableData.push([
        event.title || 'Sans titre',
        format(new Date(event.start), 'dd/MM/yyyy'),
        format(new Date(event.start), 'HH:mm'),
        event.end ? format(new Date(event.end), 'HH:mm') : 'N/A',
        `${duration.toFixed(1)}h`,
        roomName,
        eventTypeText,
        eventCategory
      ]);
    });
    
    // Créer le tableau avec autoTable
    yPosition += 10;
    autoTable(doc, {
      startY: yPosition,
      head: [['Événement', 'Date', 'Début', 'Fin', 'Durée', 'Salle', 'Type', 'Catégorie']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: primaryColor,
        textColor: [255, 255, 255],
        fontSize: 9,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 8,
        cellPadding: 2
      },
      columnStyles: {
        0: { cellWidth: 35 },
        1: { cellWidth: 20 },
        2: { cellWidth: 15 },
        3: { cellWidth: 15 },
        4: { cellWidth: 15 },
        5: { cellWidth: 25 },
        6: { cellWidth: 25 },
        7: { cellWidth: 20 }
      },
      margin: { left: 20, right: 20 },
      didDrawCell: function(data) {
        // Colorer différemment les lignes selon la catégorie
        if (data.section === 'body' && data.column.index === 7) {
          if (data.cell.text[0] === 'Gestion') {
            data.cell.styles.fillColor = [255, 248, 220]; // Beige clair pour gestion
          } else {
            data.cell.styles.fillColor = [240, 248, 255]; // Bleu très clair pour travail
          }
        }
      }
    });
    
    // Section Résumé standardisé
    const finalY = doc.lastAutoTable.finalY || yPosition + 50;
    
    // Ajouter le résumé standardisé des heures du personnel
    const summary = computePersonnelSummary(personEvents, 7);
    renderPersonnelSummaryPDF(doc, summary, finalY + 10);
    
    // Ajouter un pied de page
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      doc.setDrawColor(...primaryColor);
      doc.setLineWidth(0.3);
      doc.line(20, 280, 190, 280);
      
      doc.setTextColor(128, 128, 128);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('Comédie des Suds - Gestion du Personnel', 20, 285);
      doc.text(`Page ${i} sur ${pageCount}`, 190, 285, { align: 'right' });
      doc.text(`Généré le ${format(new Date(), 'dd/MM/yyyy à HH:mm')}`, 105, 290, { align: 'center' });
    }
    
    // Générer le nom du fichier avec le nom de l'utilisateur
    const year = format(new Date(), 'yyyy');
    const month = format(new Date(), 'MM-dd-yyyy');
    const userName = `${selectedPerson.firstName}_${selectedPerson.lastName}`.replace(/\s+/g, '_');
    const fileName = `activite_user_${year}_${userName}_mois_${month}.pdf`;
    
    // Télécharger le fichier
    doc.save(fileName);
  };

  // Fonction pour exporter toutes les statistiques du tableau de bord
  const exportDashboardStats = () => {
    // Créer un nouveau classeur
    const wb = XLSX.utils.book_new();
    
    // 1. Feuille des statistiques générales
    const generalStats = [
      { 'Statistique': 'Nombre total d\'événements', 'Valeur': stats.events },
      { 'Statistique': 'Nombre total de spectacles', 'Valeur': stats.shows },
      { 'Statistique': 'Production', 'Valeur': stats.production },
      { 'Statistique': 'Co-production', 'Valeur': stats.coproduction },
      { 'Statistique': 'ONE', 'Valeur': stats.one },
      { 'Statistique': 'Répétitions', 'Valeur': stats.rehearsals },
      { 'Statistique': 'Locations', 'Valeur': stats.rentals },
      { 'Statistique': 'Événements (type event)', 'Valeur': stats.total }
    ];
    
    const wsGeneral = XLSX.utils.json_to_sheet(generalStats);
    
    // Amélioration du design: ajout de styles pour l'en-tête
    const generalRange = XLSX.utils.decode_range(wsGeneral['!ref']);
    for (let C = generalRange.s.c; C <= generalRange.e.c; ++C) {
      const address = XLSX.utils.encode_col(C) + '1';
      if (!wsGeneral[address]) continue;
      wsGeneral[address].s = {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: '4F46E5' } },
        alignment: { horizontal: 'center' }
      };
    }
    
    // Ajuster la largeur des colonnes
    wsGeneral['!cols'] = [{ wch: 30 }, { wch: 10 }];
    
    XLSX.utils.book_append_sheet(wb, wsGeneral, 'Statistiques générales');
    
    // 2. Feuille des statuts des spectacles
    const showStatusData = [
      { 'Statut': 'Confirmé', 'Nombre': showStatusStats.confirmed },
      { 'Statut': 'Provisoire', 'Nombre': showStatusStats.provisional },
      { 'Statut': 'Billetterie ouverte', 'Nombre': showStatusStats.ticketsOpen },
      { 'Statut': 'Annulé', 'Nombre': showStatusStats.cancelled }
    ];
    
    const wsShowStatus = XLSX.utils.json_to_sheet(showStatusData);
    
    // Amélioration du design pour la feuille des statuts
    const statusRange = XLSX.utils.decode_range(wsShowStatus['!ref']);
    for (let C = statusRange.s.c; C <= statusRange.e.c; ++C) {
      const address = XLSX.utils.encode_col(C) + '1';
      if (!wsShowStatus[address]) continue;
      wsShowStatus[address].s = {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { fgColor: { rgb: '4F46E5' } },
        alignment: { horizontal: 'center' }
      };
    }
    
    // Ajuster la largeur des colonnes
    wsShowStatus['!cols'] = [{ wch: 20 }, { wch: 10 }];
    
    XLSX.utils.book_append_sheet(wb, wsShowStatus, 'Statuts des spectacles');
    
    // 3. Feuille des statistiques des artistes avec détails
    if (artistStats.length > 0) {
      const artistDetailedData = [];
      
      artistStats.forEach(artist => {
        // Ajouter une ligne de titre pour l'artiste
        artistDetailedData.push({
          'Artiste': artist.name,
          'Spectacle': '',
          'Date': '',
          'Type': '',
          'Statut': '',
          'Jauge': '',
          'Adresses': '',
          'Total': artist.count
        });
        
        // Ajouter les détails de chaque spectacle (triés par date)
        if (artist.shows && artist.shows.length > 0) {
          // Trier les spectacles par date
          const sortedShows = [...artist.shows].sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateA - dateB;
          });
          
          sortedShows.forEach(show => {
            artistDetailedData.push({
              'Artiste': '',
              'Spectacle': show.title,
              'Date': new Date(show.date).toLocaleDateString('fr-FR'),
              'Type': show.showType === 'production' ? 'Production' :
                     show.showType === 'coproduction' ? 'Co-production' :
                     show.showType === 'one' ? 'ONE' : show.showType,
              'Statut': show.showStatus === 'confirmed' ? 'Confirmé' :
                       show.showStatus === 'ticketsOpen' ? 'Billetterie ouverte' :
                       show.showStatus === 'provisional' ? 'Provisoire' :
                       show.showStatus === 'cancelled' ? 'Annulé' : show.showStatus,
              'Jauge': show.jauge || '',
              'Adresses': show.addresses || '',
              'Total': ''
            });
          });
        }
        
        // Ajouter une ligne vide pour séparer les artistes
        artistDetailedData.push({
          'Artiste': '',
          'Spectacle': '',
          'Date': '',
          'Type': '',
          'Statut': '',
          'Jauge': '',
          'Adresses': '',
          'Total': ''
        });
      });
      
      const wsArtists = XLSX.utils.json_to_sheet(artistDetailedData);
      
      // Amélioration du design pour la feuille des artistes
      const artistRange = XLSX.utils.decode_range(wsArtists['!ref']);
      for (let C = artistRange.s.c; C <= artistRange.e.c; ++C) {
        const address = XLSX.utils.encode_col(C) + '1';
        if (!wsArtists[address]) continue;
        wsArtists[address].s = {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '4F46E5' } },
          alignment: { horizontal: 'center' }
        };
      }
      
      // Ajuster la largeur des colonnes
      wsArtists['!cols'] = [{ wch: 25 }, { wch: 30 }, { wch: 12 }, { wch: 15 }, { wch: 18 }, { wch: 10 }, { wch: 20 }, { wch: 8 }];
      
      XLSX.utils.book_append_sheet(wb, wsArtists, 'artistesProd');
    }
    
    // 4. Feuille des statistiques du personnel avec détails
    if (staffStats.length > 0) {
      const staffDetailedData = [];
      
      staffStats.forEach(staff => {
        // Ajouter une ligne de titre pour le membre du personnel
        staffDetailedData.push({
          'Membre du personnel': staff.name,
          'Rôle': staff.role === 'billeterie' ? 'Billetterie' : 
                  staff.role === 'permanence' ? 'Permanence' : 
                  staff.role === 'regie' ? 'Régie' : 
                  staff.role === 'technique' ? 'Technique' : staff.role,
          'Spectacle': '',
          'Date': '',
          'Horaires': '',
          'Rôle sur événement': '',
          'Type': '',
          'Statut': '',
          'Jauge': '',
          'Adresses': '',
          'Heures': '',
          'Total Heures': staff.hours + ' h'
        });
        
        // Récupérer les absences pour ce membre du personnel
        const staffAbsences = absenceStats.find(absence => absence.name === staff.name);
        
        // Créer un tableau combiné d'événements et d'absences
        let combinedEvents = [];
        
        // Ajouter les événements de spectacle
        if (staff.shows && staff.shows.length > 0) {
          combinedEvents = [...staff.shows.map(show => ({
            ...show,
            isAbsence: false
          }))];
        }
        
        // Ajouter les absences si elles existent
        if (staffAbsences && staffAbsences.absences && staffAbsences.absences.length > 0) {
          combinedEvents = [...combinedEvents, ...staffAbsences.absences.map(absence => ({
            title: absence.title,
            date: absence.start,
            start: absence.start,
            end: absence.end,
            hours: absence.hours,
            isAbsence: true,
            type: absence.type,
            eventRole: absence.eventRole // Ajouter le rôle de l'utilisateur pour cette absence
          }))];
        }
        
        // Trier tous les événements par date
        const sortedEvents = combinedEvents.sort((a, b) => new Date(a.date) - new Date(b.date));
        
        // Ajouter les détails de chaque événement (spectacle ou absence)
        sortedEvents.forEach(event => {
          if (event.isAbsence) {
            // C'est une absence
            // Formater les heures de début et de fin
            const startTime = event.start ? new Date(event.start) : null;
            const endTime = event.end ? new Date(event.end) : null;
            const formattedStartTime = startTime ? startTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
            const formattedEndTime = endTime ? endTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
            const timeRange = formattedStartTime && formattedEndTime ? `${formattedStartTime} - ${formattedEndTime}` : '';
            
            staffDetailedData.push({
              'Membre': '',
              'Rôle principal': '',
              'Spectacle': event.title,
              'Date': new Date(event.date).toLocaleDateString('fr-FR'),
              'Horaires': timeRange,
              'Rôle sur événement': (event.eventRole || '') + 
                                    (event.type ? ' - ' + (event.type === 'maladie' ? 'Maladie' :
                                    event.type === 'absence' ? 'Absence' :
                                    event.type === 'CP' ? 'Congés payés' :
                                    event.type === 'télétravail' ? 'Télétravail' : event.type) : ''),
              'Type': event.type === 'maladie' ? 'Maladie' :
                     event.type === 'absence' ? 'Absence' :
                     event.type === 'CP' ? 'Congés payés' :
                     event.type === 'télétravail' ? 'Télétravail' : 'Autre',
              'Statut': 'Absence',
              'Jauge': '',
              'Adresses': '',
              'Heures': event.hours !== undefined && event.hours !== null ? 
                      (event.hours === 0 ? '0' : 
                       event.type === 'CP' ? (event.hours / 7).toFixed(1) + ' j' : 
                       event.hours + ' h') : '',
              'Total Heures': ''
            });
          } else {
            // C'est un événement normal
            // Formater les heures de début et de fin
            const startTime = event.startTime ? new Date(event.startTime) : null;
            const endTime = event.endTime ? new Date(event.endTime) : null;
            const formattedStartTime = startTime ? startTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
            const formattedEndTime = endTime ? endTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
            const timeRange = formattedStartTime && formattedEndTime ? `${formattedStartTime} - ${formattedEndTime}` : '';
            
            staffDetailedData.push({
              'Membre': '',
              'Rôle principal': '',
              'Spectacle': event.title,
              'Date': new Date(event.date).toLocaleDateString('fr-FR'),
              'Horaires': timeRange,
              'Rôle sur événement': (event.eventRole || (staff.role === 'billeterie' ? 'Billetterie' : 
                                    staff.role === 'permanence' ? 'Permanence' : 
                                    staff.role === 'regie' ? 'Régie' : 
                                    staff.role === 'technique' ? 'Technique' : staff.role)) + 
                                    (event.type ? ' - ' + (event.type.charAt(0).toUpperCase() + event.type.slice(1)) : '') +
                                    (event.showType ? ' - ' + (event.showType === 'production' ? 'Production' :
                                    event.showType === 'coproduction' ? 'Co-production' :
                                    event.showType === 'one' ? 'ONE' : event.showType) : ''),
              'Type': event.type === 'personnel' && event.personnelType ? 
                     (event.personnelType.charAt(0).toUpperCase() + event.personnelType.slice(1)) :
                     event.showType === 'production' ? 'Production' :
                     event.showType === 'coproduction' ? 'Co-production' :
                     event.showType === 'one' ? 'ONE' : 
                     event.type ? (event.type.charAt(0).toUpperCase() + event.type.slice(1)) : 'Autre',
              'Statut': event.showStatus === 'confirmed' ? 'Confirmé' :
                       event.showStatus === 'ticketsOpen' ? 'Billetterie ouverte' :
                       event.showStatus === 'provisional' ? 'Provisoire' :
                       event.showStatus === 'cancelled' ? 'Annulé' : event.showStatus,
              'Jauge': event.jauge || '',
              'Adresses': event.addresses || '',
              'Heures': event.hours !== undefined && event.hours !== null ? (event.hours === 0 ? '0' : event.hours) : '',
              'Total Heures': ''
            });
          }
        });
        
        // Ajouter une ligne vide pour séparer les membres
        staffDetailedData.push({
          'Membre': '',
          'Rôle principal': '',
          'Spectacle': '',
          'Date': '',
          'Rôle sur événement': '',
          'Type': '',
          'Statut': '',
          'Jauge': '',
          'Adresses': '',
          'Heures': '',
          'Total Heures': ''
        });
      });
      
      // Déclaration de la variable wsStaff qui sera utilisée plus tard
      let wsStaff;
      
      // Ajouter le récapitulatif des absences à la feuille du personnel
      if (absenceStats.length > 0) {
        // Ajouter des lignes vides pour séparer
        for (let i = 0; i < 3; i++) {
          staffDetailedData.push({
            'Membre': '',
            'Rôle principal': '',
            'Spectacle': '',
            'Date': '',
            'Rôle sur événement': '',
            'Type': '',
            'Statut': '',
            'Heures': '',
            'Total Heures': ''
          });
        }
        
        // Ajouter un titre pour la section des absences
        staffDetailedData.push({
          'Membre': 'RÉCAPITULATIF DES ABSENCES',
          'Rôle principal': '',
          'Spectacle': '',
          'Date': '',
          'Rôle sur événement': '',
          'Type': '',
          'Statut': '',
          'Heures': '',
          'Total Heures': ''
        });
        
        // Ajouter une ligne vide après le titre
        staffDetailedData.push({
          'Membre': '',
          'Rôle principal': '',
          'Spectacle': '',
          'Date': '',
          'Rôle sur événement': '',
          'Type': '',
          'Statut': '',
          'Heures': '',
          'Total Heures': ''
        });
        
        // Ajouter les en-têtes pour le récapitulatif des absences
        staffDetailedData.push({
          'Membre': 'Membre',
          'Rôle principal': 'Rôle',
          'Spectacle': 'Total Heures',
          'Date': 'Maladie',
          'Rôle sur événement': 'Congés Payés',
          'Type': 'Télétravail',
          'Statut': 'Absence',
          'Heures': 'Autre',
          'Total Heures': ''
        });
        
        // Ajouter les données pour chaque membre du personnel
        absenceStats.forEach(absence => {
          staffDetailedData.push({
            'Membre': absence.name,
            'Rôle principal': absence.role === 'billeterie' ? 'Billetterie' : 
                     absence.role === 'permanence' ? 'Permanence' : 
                     absence.role === 'regie' ? 'Régie' : 
                     absence.role === 'technique' ? 'Technique' : absence.role,
            'Spectacle': absence.totalHours + ' h',
            'Date': absence.types.maladie + ' h',
            'Rôle sur événement': absence.types.CP + ' h',
            'Type': absence.types.télétravail + ' h',
            'Statut': absence.types.absence + ' h',
            'Heures': absence.types.autre + ' h',
            'Total Heures': ''
          });
        });
        
        // Ajouter une ligne vide
        staffDetailedData.push({
          'Membre': '',
          'Rôle principal': '',
          'Spectacle': '',
          'Date': '',
          'Rôle sur événement': '',
          'Type': '',
          'Statut': '',
          'Heures': '',
          'Total Heures': ''
        });
        
        // Ajouter un titre pour la section détaillée des absences
        staffDetailedData.push({
          'Membre': 'DÉTAIL DES ABSENCES',
          'Rôle principal': '',
          'Spectacle': '',
          'Date': '',
          'Rôle sur événement': '',
          'Type': '',
          'Statut': '',
          'Heures': '',
          'Total Heures': ''
        });
        
        // Ajouter une ligne vide après le titre
        staffDetailedData.push({
          'Membre': '',
          'Rôle principal': '',
          'Spectacle': '',
          'Date': '',
          'Rôle sur événement': '',
          'Type': '',
          'Statut': '',
          'Heures': '',
          'Total Heures': ''
        });
        
        // Détails des absences pour chaque membre
        absenceStats.forEach(absence => {
          // Ligne avec le nom du membre
          staffDetailedData.push({
            'Membre': absence.name,
            'Rôle principal': absence.role === 'billeterie' ? 'Billetterie' : 
                   absence.role === 'permanence' ? 'Permanence' : 
                   absence.role === 'regie' ? 'Régie' : 
                   absence.role === 'technique' ? 'Technique' : absence.role,
            'Spectacle': absence.totalHours + ' h',
            'Date': '',
            'Rôle sur événement': '',
            'Type': '',
            'Statut': '',
            'Heures': '',
            'Total Heures': ''
          });
          
          // Détails de chaque absence
          if (absence.absences && absence.absences.length > 0) {
            // Trier les absences par date
            const sortedAbsences = [...absence.absences].sort((a, b) => new Date(a.start) - new Date(b.start));
            
            sortedAbsences.forEach(abs => {
              const absDate = new Date(abs.start).toLocaleDateString('fr-FR');
              const absType = abs.type;
              
              // Formater le type d'absence pour l'affichage
              const formattedType = absType === 'maladie' ? 'Maladie' : 
                                  absType === 'CP' ? 'Congés Payés' : 
                                  absType === 'télétravail' ? 'Télétravail' : 
                                  absType === 'absence' ? 'Absence' : 'Autre';
              
              staffDetailedData.push({
                'Membre': '',
                'Rôle principal': '',
                'Spectacle': abs.title,
                'Date': absDate,
                'Rôle sur événement': formattedType,
                'Type': '',
                'Statut': '',
                'Heures': abs.hours + ' h',
                'Total Heures': ''
              });
            });
          }
          
          // Ligne vide après chaque membre
          staffDetailedData.push({
            'Membre': '',
            'Rôle principal': '',
            'Spectacle': '',
            'Date': '',
            'Rôle sur événement': '',
            'Type': '',
            'Statut': '',
            'Heures': '',
            'Total Heures': ''
          });
        });
      }
      
      // Création de la feuille de personnel à partir des données
      wsStaff = XLSX.utils.json_to_sheet(staffDetailedData);
      
      // Amélioration du design pour la feuille du personnel
      const staffRange = XLSX.utils.decode_range(wsStaff['!ref']);
      for (let C = staffRange.s.c; C <= staffRange.e.c; ++C) {
        const address = XLSX.utils.encode_col(C) + '1';
        if (!wsStaff[address]) continue;
        wsStaff[address].s = {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '4F46E5' } },
          alignment: { horizontal: 'center' }
        };
      }
      
      // Ajuster la largeur des colonnes
      wsStaff['!cols'] = [{ wch: 25 }, { wch: 15 }, { wch: 30 }, { wch: 12 }, { wch: 15 }, { wch: 35 }, { wch: 18 }, { wch: 8 }, { wch: 18 }];
      
      // Mettre en évidence les titres des sections
      const titleRows = [];
      staffDetailedData.forEach((row, index) => {
        if (row['Membre'] === 'RÉCAPITULATIF DES ABSENCES' || row['Membre'] === 'DÉTAIL DES ABSENCES') {
          titleRows.push(index + 1); // +1 car les lignes Excel commencent à 1
        }
      });
      
      titleRows.forEach(rowIndex => {
        const cell = 'A' + rowIndex;
        if (wsStaff[cell]) {
          wsStaff[cell].s = {
            font: { bold: true, size: 14 },
            fill: { fgColor: { rgb: 'E5E7EB' } }
          };
        }
      });
      
      XLSX.utils.book_append_sheet(wb, wsStaff, 'Personnel');
    }
    
    // 5. Feuille des absences et congés payés
    if (absenceStats.length > 0) {
      const absenceData = [];
      
      // Titre de la feuille
      absenceData.push({
        'Membre': 'RÉCAPITULATIF DES ABSENCES',
        'Rôle': '',
        'Total Heures': '',
        'Maladie': '',
        'Congés Payés': '',
        'Télétravail': '',
        'Absence': '',
        'Autre': ''
      });
      
      // Ligne vide après le titre
      absenceData.push({
        'Membre': '',
        'Rôle': '',
        'Total Heures': '',
        'Maladie': '',
        'Congés Payés': '',
        'Télétravail': '',
        'Absence': '',
        'Autre': ''
      });
      
      // Données pour chaque membre du personnel
      absenceStats.forEach(absence => {
        absenceData.push({
          'Membre': absence.name,
          'Rôle': absence.role === 'billeterie' ? 'Billetterie' : 
                 absence.role === 'permanence' ? 'Permanence' : 
                 absence.role === 'regie' ? 'Régie' : 
                 absence.role === 'technique' ? 'Technique' : absence.role,
          'Total Heures': absence.totalHours + ' h',
          'Maladie': absence.types.maladie + ' h',
          'Congés Payés': absence.types.CP / 7 + ' j',
          'Télétravail': absence.types.télétravail + ' h',
          'Absence': absence.types.absence + ' h',
          'Autre': absence.types.autre + ' h'
        });
      });
      
      // Ajouter une ligne vide
      absenceData.push({
        'Membre': '',
        'Rôle': '',
        'Total Heures': '',
        'Maladie': '',
        'Congés Payés': '',
        'Télétravail': '',
        'Absence': '',
        'Autre': ''
      });
      
      // Titre pour la section détaillée
      absenceData.push({
        'Membre': 'DÉTAIL DES ABSENCES',
        'Rôle': '',
        'Total Heures': '',
        'Maladie': '',
        'Congés Payés': '',
        'Télétravail': '',
        'Absence': '',
        'Autre': ''
      });
      
      // Ligne vide après le titre
      absenceData.push({
        'Membre': '',
        'Rôle': '',
        'Total Heures': '',
        'Maladie': '',
        'Congés Payés': '',
        'Télétravail': '',
        'Absence': '',
        'Autre': ''
      });
      
      // Détails des absences pour chaque membre
      absenceStats.forEach(absence => {
        // Ligne avec le nom du membre
        absenceData.push({
          'Membre': absence.name,
          'Rôle': absence.role === 'billeterie' ? 'Billetterie' : 
                 absence.role === 'permanence' ? 'Permanence' : 
                 absence.role === 'regie' ? 'Régie' : 
                 absence.role === 'technique' ? 'Technique' : absence.role,
          'Total Heures': absence.totalHours + ' h',
          'Maladie': '',
          'Congés Payés': '',
          'Télétravail': '',
          'Absence': '',
          'Autre': ''
        });
        
        // Détails de chaque absence
        if (absence.absences && absence.absences.length > 0) {
          // Trier les absences par date
          const sortedAbsences = [...absence.absences].sort((a, b) => new Date(a.start) - new Date(b.start));
          
          sortedAbsences.forEach(abs => {
            const absDate = new Date(abs.start).toLocaleDateString('fr-FR');
            const absType = abs.type;
            
            // Formater le type d'absence pour l'affichage
            const formattedType = absType === 'maladie' ? 'Maladie' : 
                                absType === 'CP' ? 'Congés Payés' : 
                                absType === 'télétravail' ? 'Télétravail' : 
                                absType === 'absence' ? 'Absence' : 'Autre';
            
            absenceData.push({
              'Membre': '',
              'Rôle': '',
              'Total Heures': '',
              'Maladie': absType === 'maladie' ? `${abs.title} (${formattedType}) - ${absDate} - ${abs.hours} h` : '',
              'Congés Payés': absType === 'CP' ? `${abs.title} (${formattedType}) - ${absDate} - ${abs.displayValue.toFixed(1)} j` : '',
              'Télétravail': absType === 'télétravail' ? `${abs.title} (${formattedType}) - ${absDate} - ${abs.hours} h` : '',
              'Absence': absType === 'absence' ? `${abs.title} (${formattedType}) - ${absDate} - ${abs.hours} h` : '',
              'Autre': absType === 'autre' ? `${abs.title} (${formattedType}) - ${absDate} - ${abs.hours} h` : ''
            });
          });
        }
        
        // Ligne vide après chaque membre
        absenceData.push({
          'Membre': '',
          'Rôle': '',
          'Total Heures': '',
          'Maladie': '',
          'Congés Payés': '',
          'Télétravail': '',
          'Absence': '',
          'Autre': ''
        });
      });
      
      const wsAbsences = XLSX.utils.json_to_sheet(absenceData);
      
      // Amélioration du design pour la feuille des absences
      const absencesRange = XLSX.utils.decode_range(wsAbsences['!ref']);
      for (let C = absencesRange.s.c; C <= absencesRange.e.c; ++C) {
        const address = XLSX.utils.encode_col(C) + '1';
        if (!wsAbsences[address]) continue;
        wsAbsences[address].s = {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '4F46E5' } },
          alignment: { horizontal: 'center' }
        };
      }
      
      // Mettre en évidence les titres des sections
      ['A1', 'A5'].forEach(cell => {
        if (wsAbsences[cell]) {
          wsAbsences[cell].s = {
            font: { bold: true, size: 14 },
            fill: { fgColor: { rgb: 'E5E7EB' } }
          };
        }
      });
      
      // Ajuster la largeur des colonnes
      wsAbsences['!cols'] = [
        { wch: 25 }, // Membre
        { wch: 15 }, // Rôle
        { wch: 15 }, // Total Heures
        { wch: 40 }, // Maladie
        { wch: 40 }, // Congés Payés
        { wch: 40 }, // Télétravail
        { wch: 40 }, // Absence
        { wch: 40 }  // Autre
      ];
      
      XLSX.utils.book_append_sheet(wb, wsAbsences, 'Absences et Congés');
    }
    
    // 6. Feuille des statistiques des salles
    if (rooms.length > 0) {
      const roomData = rooms.map(room => {
        // Compter les événements pour cette salle
        const roomEvents = originalEvents.filter(event => event.roomId === room.id);
        const roomShows = roomEvents.filter(event => {
          const eventType = event.type ? event.type.toLowerCase() : '';
          const eventTitle = event.title ? event.title.toLowerCase() : '';
          return eventType === 'show' || eventType.includes('spectacle') || eventTitle.includes('spectacle');
        });
        
        return {
          'Salle': room.name,
          'Capacité': room.capacity || 'N/A',
          'Événements': roomEvents.length,
          'Spectacles': roomShows.length
        };
      });
      
      const wsRooms = XLSX.utils.json_to_sheet(roomData);
      
      // Amélioration du design pour la feuille des salles
      const roomsRange = XLSX.utils.decode_range(wsRooms['!ref']);
      for (let C = roomsRange.s.c; C <= roomsRange.e.c; ++C) {
        const address = XLSX.utils.encode_col(C) + '1';
        if (!wsRooms[address]) continue;
        wsRooms[address].s = {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '4F46E5' } },
          alignment: { horizontal: 'center' }
        };
      }
      
      // Ajuster la largeur des colonnes
      wsRooms['!cols'] = [{ wch: 30 }, { wch: 10 }, { wch: 12 }, { wch: 12 }];
      
      XLSX.utils.book_append_sheet(wb, wsRooms, 'Salles');
    }
    
    // 6. Feuille des événements détaillés avec colonnes adaptées
    if (originalEvents.length > 0) {
      // Créer des feuilles séparées pour artistes et personnel
      
      // Feuille pour les artistes (colonnes A-H)
      const artistEventsData = originalEvents.map(event => {
        // Récupérer les détails de l'artiste
        const artistDetails = event.Users && event.Users.find(u => u.role === 'artistesProd');
        
        return {
          'Titre': event.title,
          'Description': event.description || '',
          'Date': event.start ? format(new Date(event.start), 'dd/MM/yyyy') : 'N/A',
          'Heure début': event.start ? format(new Date(event.start), 'HH:mm') : 'N/A',
          'Heure fin': event.end ? format(new Date(event.end), 'HH:mm') : 'N/A',
          'Durée (h)': event.start && event.end ? 
            ((new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60)).toFixed(2) : 'N/A',
          'Salle': resolveRoomName(event),
          'Nom artiste': artistDetails?.name || 'N/A'
        };
      });
      
      const wsArtistEvents = XLSX.utils.json_to_sheet(artistEventsData);
      
      // Design pour la feuille des événements artistes
      const artistEventsRange = XLSX.utils.decode_range(wsArtistEvents['!ref']);
      for (let C = artistEventsRange.s.c; C <= artistEventsRange.e.c; ++C) {
        const address = XLSX.utils.encode_col(C) + '1';
        if (!wsArtistEvents[address]) continue;
        wsArtistEvents[address].s = {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '4F46E5' } },
          alignment: { horizontal: 'center' }
        };
      }
      
      // Largeurs de colonnes pour artistes (A-H)
      wsArtistEvents['!cols'] = [
        { wch: 30 }, // Titre
        { wch: 40 }, // Description
        { wch: 12 }, // Date
        { wch: 12 }, // Heure début
        { wch: 12 }, // Heure fin
        { wch: 10 }, // Durée
        { wch: 20 }, // Salle
        { wch: 25 }  // Nom artiste
      ];
      
      XLSX.utils.book_append_sheet(wb, wsArtistEvents, 'Événements Artistes');
      
      // Feuille pour le personnel (colonnes A-H)
const staffEventsData = originalEvents.map(event => {
  // 1) Trouver l'utilisateur « personnel » (hors artistesProd)
  const staffDetails = event.Users?.find(u => u.role !== 'artistesProd');

  // 2) Dates/horaires
  const start = event.start ? new Date(event.start) : null;
  const end   = event.end   ? new Date(event.end)   : null;

  // 5) Construction de l'objet exporté
   return {
     'Titre':             event.title,
     'Type d événement':  event.type || 'N/A',  // Utiliser event.type au lieu de event.showType
     'Date début':        start ? format(start, 'dd/MM/yyyy') : 'N/A',
     'Date fin':          end   ? format(end,   'dd/MM/yyyy') : 'N/A',
     'Heure début':       start ? format(start, 'HH:mm')     : 'N/A',
     'Heure fin':         end   ? format(end,   'HH:mm')     : 'N/A',
     'Durée (h)':         (start && end) 
       ? ((end - start) / (1000 * 60 * 60)).toFixed(2) 
       : 'N/A',
     'Rôle':              staffDetails?.role || 'N/A',
     'Salle':             resolveRoomName(event),
     'Nom utilisateur':   staffDetails?.name || 'N/A'
   };
 });

      
  const wsStaffEvents = XLSX.utils.json_to_sheet(staffEventsData, {
   header: [
     'Titre',
     'Type d’événement',
     'Date début',
     'Date fin',
     'Heure début',
     'Heure fin',
   'Durée (h)',
     'Rôle',
     'Salle',
     'Nom utilisateur'
   ]
 });
      
      // Design pour la feuille des événements personnel
      const staffEventsRange = XLSX.utils.decode_range(wsStaffEvents['!ref']);
      for (let C = staffEventsRange.s.c; C <= staffEventsRange.e.c; ++C) {
        const address = XLSX.utils.encode_col(C) + '1';
        if (!wsStaffEvents[address]) continue;
        wsStaffEvents[address].s = {
          font: { bold: true, color: { rgb: 'FFFFFF' } },
          fill: { fgColor: { rgb: '4F46E5' } },
          alignment: { horizontal: 'center' }
        };
      }
      
      // Largeurs de colonnes pour personnel (A-H)
      wsStaffEvents['!cols'] = [
        { wch: 30 }, // Titre
        { wch: 40 }, // Description
        { wch: 12 }, // Date
        { wch: 12 }, // Heure début
        { wch: 12 }, // Heure fin
        { wch: 10 }, // Durée
        { wch: 20 }, // Salle
        { wch: 25 }  // Nom utilisateur
      ];
      
      XLSX.utils.book_append_sheet(wb, wsStaffEvents, 'Événements Personnel');
    }
    
    // Générer le nom du fichier
    const fileName = `Statistiques_${formattedPeriod.replace(/ /g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
    
    // Télécharger le fichier
    XLSX.writeFile(wb, fileName);
  };

  // Fonction pour extraire et regrouper les jours de travail
  const getWorkDays = () => {
    if (!personEvents || personEvents.length === 0) return [];
    
    // Créer un objet pour stocker les jours de travail par mois
    const workDaysByMonth = {};
    
    // Parcourir tous les événements pour extraire les jours de travail
    personEvents.forEach(event => {
      if (!event.start) return;
      
      // Pour le personnel, vérifier si la personne est assignée à cet événement
      const isAssigned = !selectedPerson.isArtist && !selectedPerson.isRoom && 
        event.Users && event.Users.some(u => u.id === parseInt(selectedPerson.id));
      
      // Si c'est un artiste ou une salle, ou si le membre du personnel est assigné à l'événement
      if (selectedPerson.isArtist || selectedPerson.isRoom || isAssigned) {
        const eventDate = new Date(event.start);
        const monthKey = format(eventDate, 'MMMM yyyy', { locale: fr });
        const dayKey = format(eventDate, 'dd/MM/yyyy');
        
        // Initialiser le mois s'il n'existe pas encore
        if (!workDaysByMonth[monthKey]) {
          workDaysByMonth[monthKey] = {
            days: new Set(),
            locations: {}
          };
        }
        
        // Ajouter le jour au mois correspondant
        workDaysByMonth[monthKey].days.add(dayKey);
        
        // Récupérer le lieu (salle) de l'événement
        const location = resolveRoomName(event);
        
        // Récupérer les heures pour cet événement (pour le personnel)
        let hours = 0;
        if (!selectedPerson.isArtist && !selectedPerson.isRoom && event.Users) {
          const userEvent = event.Users.find(u => u.id === parseInt(selectedPerson.id));
          hours = userEvent?.through?.hours || 0;
        }
        
        // Initialiser le lieu s'il n'existe pas encore pour ce mois
        if (!workDaysByMonth[monthKey].locations[location]) {
          workDaysByMonth[monthKey].locations[location] = {
            count: 0,
            hours: 0
          };
        }
        
        // Incrémenter le compteur pour ce lieu
        workDaysByMonth[monthKey].locations[location].count++;
        
        // Ajouter les heures pour ce lieu (pour le personnel)
        if (!selectedPerson.isArtist && !selectedPerson.isRoom) {
          workDaysByMonth[monthKey].locations[location].hours += parseFloat(hours) || 0;
        }
      }
    });
    
    // Convertir l'objet en tableau pour l'affichage
    return Object.entries(workDaysByMonth).map(([month, data]) => ({
      month,
      days: Array.from(data.days).sort((a, b) => {
        const dateA = new Date(a.split('/').reverse().join('-'));
        const dateB = new Date(b.split('/').reverse().join('-'));
        return dateA - dateB;
      }),
      locations: Object.entries(data.locations).map(([name, stats]) => ({
        name,
        count: stats.count,
        hours: parseFloat(stats.hours.toFixed(1)) // Arrondi à 1 décimale
      })).sort((a, b) => b.count - a.count) // Trier par nombre d'événements décroissant
    }));
  };

  /**
   * Configuration des données pour le graphique camembert
   */
  const pieChartData = {
    labels: ['Confirmés', 'Provisoires', 'Billetterie ouverte', 'Annulés'],
    datasets: [
      {
        label: 'Nombre de spectacles',
        data: [
          showStatusStats.confirmed,
          showStatusStats.provisional,
          showStatusStats.ticketsOpen,
          showStatusStats.cancelled
        ],
        backgroundColor: [
          '#3A6BCF', // Bleu pour confirmés
          '#9ED2F4', // Bleu clair pour provisoires
          '#0038FF',  // Bleu foncé pour billetterie ouverte
          '#000000', // Noir pour annulés
        ],
        borderColor: [
          '#ffffff',
          '#ffffff',
          '#ffffff',
          '#ffffff'
        ],
        borderWidth: 1,
      },
    ],
  };

  /**
   * Options pour le graphique camembert
   */
  const pieChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.raw || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  /**
   * Composant de carte de statistique amélioré avec animation et meilleure hiérarchie visuelle
   * Ajout de la fonctionnalité de pop-up au clic avec détails des événements
   */
  const StatCard = ({ title, value, icon: Icon, color, description }) => {
    const [showPopup, setShowPopup] = useState(false);
    const [detailEvents, setDetailEvents] = useState([]);
    
    // Fonction pour récupérer les événements détaillés associés à cette statistique
    const fetchDetailEvents = () => {
      // Filtrer les événements originaux en fonction du type de statistique
      let filteredEvents = [];
      
      // Appliquer d'abord le filtre par type de statistique
      if (title.includes('spectacles') || title === 'Nombre total de spectacles') {
        filteredEvents = originalEvents.filter(event => event.type === 'show');
      } else if (title === 'Production') {
        filteredEvents = originalEvents.filter(event => event.type === 'show' && event.showType === 'production');
      } else if (title === 'Co-production') {
        filteredEvents = originalEvents.filter(event => event.type === 'show' && event.showType === 'coproduction');
      } else if (title === 'ONE') {
        filteredEvents = originalEvents.filter(event => event.type === 'show' && event.showType === 'one');
      } else if (title.includes('Locations') || title === 'Locations') {
        filteredEvents = originalEvents.filter(event => event.type === 'rental');
      } else if (title.includes('événements') || title === 'Nombre total d\'événements') {
        filteredEvents = originalEvents;
      }
      
      // Appliquer ensuite le filtre par salle si une salle spécifique est sélectionnée
      if (selectedRoom !== 'all') {
        filteredEvents = filteredEvents.filter(event => event.roomId === parseInt(selectedRoom));
      }
      
      // Trier les événements par date (du plus récent au plus ancien)
      filteredEvents.sort((a, b) => new Date(a.start) - new Date(b.start));
      
      // Afficher tous les événements sans limitation
      setDetailEvents(filteredEvents);
    };
    
    return (
      <>
        <div 
          className={`bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1 cursor-pointer`}
          onClick={() => {
            setShowPopup(true);
            fetchDetailEvents();
          }}
        >
          <div className={`p-5 ${color} text-white flex justify-between items-center`}>
            <div className="flex items-center">
              <Icon className="h-7 w-7 mr-3" />
              <h2 className="text-lg font-semibold">{title}</h2>
            </div>
            <div className="text-3xl font-bold">{value}</div>
          </div>
          {description && (
            <div className="p-3 text-sm text-gray-600 bg-gray-50">
              {description}
            </div>
          )}
        </div>
        
        {/* Pop-up détaillé avec liste des événements */}
        {showPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 overflow-hidden max-h-[90vh] flex flex-col">
              <div className={`${color} p-5 text-white`}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <Icon className="h-8 w-8 mr-3" />
                    <h2 className="text-xl font-bold">{title}</h2>
                  </div>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowPopup(false);
                    }}
                    className="text-white hover:text-gray-200"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                <div className="mt-2 text-4xl font-bold">{value}</div>
              </div>
              
              <div className="p-5 overflow-y-auto flex-grow">
                <h3 className="text-lg font-semibold mb-3">Détails</h3>
                <p className="text-gray-700 mb-4">{description}</p>
                
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Informations supplémentaires</h4>
                  <p className="text-sm text-gray-600">
                    Ces statistiques sont calculées pour la période: {formattedPeriod}
                    {selectedRoom !== 'all' && ` - ${rooms.find(r => r.id == selectedRoom)?.name || ''}`}
                  </p>
                </div>
                
                {/* Liste des événements associés */}
                <div className="mt-4">
                  <h4 className="text-md font-semibold mb-2">Liste des événements ({detailEvents.length} sur {value})</h4>
                  
                  {detailEvents.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                            <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Heure</th>
                            <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salle</th>
                            <th className="py-2 px-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {detailEvents.map((event, index) => {
                            const eventDate = new Date(event.start);
                            return (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="py-2 px-3 text-sm">{event.title}</td>
                                <td className="py-2 px-3 text-sm">{format(eventDate, 'dd/MM/yyyy')}</td>
                                <td className="py-2 px-3 text-sm">{format(eventDate, 'HH:mm')}</td>
                                <td className="py-2 px-3 text-sm">{rooms.find(r => r.id === event.roomId)?.name || 'Non spécifiée'}</td>
                                <td className="py-2 px-3 text-sm">
                                  {event.type === 'show' ? (
                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                      {event.showType || 'Spectacle'}
                                    </span>
                                  ) : event.type === 'rental' ? (
                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800">
                                      Location
                                    </span>
                                  ) : (
                                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                      {event.type || 'Autre'}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500 italic">Aucun événement détaillé disponible.</p>
                  )}
                </div>
                
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowPopup(false);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  /**
   * Fonctions de navigation entre les mois
   */
   const goToPreviousPeriod = () => {
       if (periodType === 'month')    setCurrentMonth(subMonths(currentMonth,    1));
       else if (periodType === 'quarter') setCurrentMonth(subMonths(currentMonth, 3));
       else                             setCurrentMonth(subYears(currentMonth,     1));
     };
     const goToNextPeriod = () => {
       if (periodType === 'month')    setCurrentMonth(addMonths(currentMonth,    1));
       else if (periodType === 'quarter') setCurrentMonth(addMonths(currentMonth, 3));
       else                             setCurrentMonth(addYears(currentMonth,     1));
     };
     const goToCurrentPeriod = () => {
       const now = new Date();
       if (periodType === 'month')    setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
       else if (periodType === 'quarter') setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
       else                             setCurrentMonth(startOfYear(now));
     };

  /**
   * Navigation vers le calendrier avec le mois sélectionné
   */
  const navigateToCalendar = () => {
    // Formatage de la date à passer comme paramètre de requête
    const formattedDate = format(currentMonth, 'yyyy-MM-dd');
    navigate(`/calendar?date=${formattedDate}`);
  };

  /**
   * Exportation des informations utilisateurs au format Excel
   */
  const exportUsersInfo = async () => {
    try {
      // Récupérer la liste des utilisateurs
      const response = await api.get('/users');
      const users = response.data.data.users;
      
      // Trier les utilisateurs par ordre alphabétique (nom puis prénom)
      const sortedUsers = users.sort((a, b) => {
        const lastNameComparison = (a.lastName || '').localeCompare(b.lastName || '', 'fr', { sensitivity: 'base' });
        if (lastNameComparison !== 0) {
          return lastNameComparison;
        }
        return (a.firstName || '').localeCompare(b.firstName || '', 'fr', { sensitivity: 'base' });
      });
      
      // Créer un nouveau classeur
      const wb = XLSX.utils.book_new();
      
      // Préparer les données pour l'export principal (tous les utilisateurs)
      const userData = [
        ['Nom', 'Prénom', 'Email', 'Téléphone', 'Adresse', 'Date de naissance', 'Lieu de naissance', 'Numéro de sécurité sociale', 'IBAN', 'Rôle']
      ];
      

      
      sortedUsers.forEach(user => {
        // Fonction pour formater la date de naissance de manière sécurisée
        const formatBirthDate = (birthDate) => {
          if (!birthDate) return '';
          try {
            const date = new Date(birthDate);
            // Vérifier si la date est valide
            if (isNaN(date.getTime())) {
              return '';
            }
            return format(date, 'dd/MM/yyyy');
          } catch (error) {
            console.warn('Erreur lors du formatage de la date de naissance:', birthDate, error);
            return '';
          }
        };

        // Ajouter à la feuille principale (tous les utilisateurs)
        userData.push([
          user.lastName || '',
          user.firstName || '',
          user.email || '',
          user.phone || '',
          user.address || '',
          formatBirthDate(user.birthDate),
          user.birthPlace || '',
          user.socialSecurityNumber || '',
          formatIban(user.iban || ''),
          user.role || ''
        ]);
        

      });
      
      // Créer la feuille principale (tous les utilisateurs)
      const ws = XLSX.utils.aoa_to_sheet(userData);
      
      // Définir la largeur des colonnes pour la feuille principale
      ws['!cols'] = [
        { wch: 15 }, // Nom
        { wch: 15 }, // Prénom
        { wch: 25 }, // Email
        { wch: 15 }, // Téléphone
        { wch: 30 }, // Adresse
        { wch: 15 }, // Date de naissance
        { wch: 20 }, // Lieu de naissance
        { wch: 20 }, // Numéro de sécurité sociale
        { wch: 25 }, // IBAN
        { wch: 15 }  // Rôle
      ];
      

      
      // Ajouter la feuille au classeur
      XLSX.utils.book_append_sheet(wb, ws, 'Informations Utilisateurs');
      
      // Générer le nom du fichier avec la date
      const fileName = `informations_utilisateurs_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      
      // Télécharger le fichier
      XLSX.writeFile(wb, fileName);
      
    } catch (error) {
      console.error('Erreur lors de l\'export des informations utilisateurs:', error);
      setError('Erreur lors de l\'export des informations utilisateurs');
    }
  };

  // Fonctions utilitaires pour le résumé des artistes
  function normalize(str) {
    return (str || '')
      .toString()
      .trim()
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  // Est-ce un spectacle ?
  function isShow(ev) {
    const t = normalize(ev.type);
    const lab = normalize(ev.label || ev.eventType || ev.eventTypeName);
    const title = normalize(ev.title);
    return t === 'show' || lab.includes('spectacle') || title.includes('spectacle');
  }

  // Est-ce annulé ?
  function isCancelled(ev) {
    const s1 = normalize(ev.showStatus);
    const s2 = normalize(ev.status);
    const title = normalize(ev.title);
    return s1 === 'cancelled' || s2 === 'cancelled' || s1 === 'annule' || s2 === 'annule' || title.includes('annule');
  }

  function computeArtistShowSummary(events) {
    const shows = events.filter(isShow);
    const totalPrevus = shows.length;
    const totalAnnules = shows.filter(isCancelled).length;
    const totalCachets = totalPrevus - totalAnnules;
    return { totalPrevus, totalAnnules, totalCachets };
  }

  function renderArtistSummaryPDF(doc, yStart, summary) {
    let y = yStart;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('RÉSUMÉ', 20, y);
    y += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text(`Total spectacles prévus : ${summary.totalPrevus}`, 20, y);
    y += 6.5;
    doc.text(`Total annulés : ${summary.totalAnnules}`, 20, y);
    y += 6.5;
    doc.text(`Total cachets : ${summary.totalCachets}`, 20, y);

    return y;
  }

  // Nouvelle fonction d'export PDF contextuelle
  const exportContextualPDF = () => {
    if (!selectedPerson || personEvents.length === 0) return;
    
    // Détection robuste des artistes
    const isArtist = 
      selectedPerson?.isArtist === true || 
      (selectedPerson?.type || '').toLowerCase() === 'artist' || 
      (selectedPerson?.role || '').toLowerCase().includes('artiste') ||
      selectedPerson?.role === 'artistesProd';
    
    const doc = new jsPDF();
    
    // Couleurs du thème
    const primaryColor = [159, 21, 21]; // Rouge du site #9F1515
    const secondaryColor = [31, 78, 121]; // Bleu foncé
    const accentColor = [248, 250, 252]; // Gris très clair
    const textColor = [55, 65, 81]; // Gris foncé
    
    // En-tête avec design moderne
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 35, 'F');
    
    // Titre principal selon le type d'utilisateur
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    
    let titleText = '';
    let fileName = '';
    
    if (selectedPerson.isAbsence) {
      titleText = 'RAPPORT D\'ABSENCES';
      fileName = `absences_${selectedPerson.name}_${format(new Date(), 'yyyy')}.pdf`;
    } else if (selectedPerson.isRoom) {
      titleText = 'PLANNING DE SALLE';
      fileName = `planning_${selectedPerson.name}_${format(new Date(), 'yyyy')}.pdf`;
    } else {
      titleText = 'RAPPORT D\'ACTIVITÉ';
      if (selectedPerson.role === 'artistesProd') {
        fileName = `activite_ArtistesUser_${format(new Date(), 'yyyy')}.pdf`;
      } else {
        fileName = `activite_${selectedPerson.name}_${format(new Date(), 'yyyy')}.pdf`;
      }
    }
    
    doc.text(titleText, 20, 18);
    
    // Sous-titre avec nom de l'utilisateur/salle
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`${selectedPerson.name} - Comédie des Suds`, 20, 28);
    
    // Informations de période et date
    doc.setTextColor(...textColor);
    doc.setFontSize(11);
    doc.text(`Période: ${formattedPeriod}`, 20, 45);
    doc.text(`Généré le: ${format(new Date(), 'dd/MM/yyyy à HH:mm')}`, 20, 52);
    
    let yPosition = 70;
    
    // Préparer les données selon le type
    let tableData = [];
    let headers = [];
    
    if (selectedPerson.isAbsence) {
      // Traitement des absences
      headers = ['Type', 'Date début', 'Date fin', 'Durée', 'Commentaire'];
      
      // Regrouper les événements récurrents
      const groupedEvents = {};
      const nonRecurringEvents = [];
      
      personEvents.forEach(event => {
        if (event.recurrenceId && event.isRecurring) {
          if (!groupedEvents[event.recurrenceId]) {
            groupedEvents[event.recurrenceId] = {
              title: event.title || 'Absence',
              events: [],
              personnelType: event.personnelType,
              description: event.description
            };
          }
          groupedEvents[event.recurrenceId].events.push(event);
        } else {
          nonRecurringEvents.push(event);
        }
      });
      
      // Ajouter les événements non récurrents
      nonRecurringEvents.forEach(event => {
        const typeText = event.personnelType === 'maladie' ? 'Maladie' : 
                        event.personnelType === 'absence' ? 'Absence' : 
                        event.personnelType === 'CP' ? 'Congé payé' : 
                        event.personnelType === 'télétravail' ? 'Télétravail' : 
                        event.personnelType || 'Autre';
        
        const duration = event.personnelType === 'CP' ? 
                        (event.hours ? (event.hours / 7).toFixed(1) + ' jours' : 'N/A') : 
                        (event.hours ? event.hours.toFixed(1) + ' heures' : 'N/A');
        
        tableData.push([
          typeText,
          event.start ? format(new Date(event.start), 'dd/MM/yyyy') : 'N/A',
          event.end ? format(new Date(event.end), 'dd/MM/yyyy') : 'N/A',
          duration,
          event.description || ''
        ]);
      });
      
      // Ajouter les événements récurrents
      Object.values(groupedEvents).forEach(group => {
        const sortedEvents = group.events.sort((a, b) => new Date(a.start) - new Date(b.start));
        const firstEvent = sortedEvents[0];
        const lastEvent = sortedEvents[sortedEvents.length - 1];
        const totalHours = sortedEvents.reduce((sum, event) => sum + (event.hours || 0), 0);
        
        const typeText = firstEvent.personnelType === 'maladie' ? 'Maladie' : 
                        firstEvent.personnelType === 'absence' ? 'Absence' : 
                        firstEvent.personnelType === 'CP' ? 'Congé payé' : 
                        firstEvent.personnelType === 'télétravail' ? 'Télétravail' : 
                        firstEvent.personnelType || 'Autre';
        
        const duration = firstEvent.personnelType === 'CP' ? 
                        (totalHours / 7).toFixed(1) + ' jours' : 
                        totalHours.toFixed(1) + ' heures';
        
        tableData.push([
          typeText + ` (${sortedEvents.length} occ.)`,
          firstEvent.start ? format(new Date(firstEvent.start), 'dd/MM/yyyy') : 'N/A',
          lastEvent.end ? format(new Date(lastEvent.end), 'dd/MM/yyyy') : 'N/A',
          duration,
          group.description || ''
        ]);
      });
      
    } else if (selectedPerson.isRoom) {
      // Traitement des salles
      headers = ['Événement', 'Date', 'Heure début', 'Heure fin', 'Type'];
      
      personEvents.forEach(event => {
        const typeText = event.type === 'show' ? 'Spectacle' : 
                        event.type === 'repetition' ? 'Répétition' : 
                        event.type === 'rental' ? 'Location' : event.type;
        
        tableData.push([
          event.title || 'Sans titre',
          format(new Date(event.start), 'dd/MM/yyyy'),
          format(new Date(event.start), 'HH:mm'),
          event.end ? format(new Date(event.end), 'HH:mm') : 'N/A',
          typeText
        ]);
      });
      
    } else {
       // Traitement des utilisateurs (artistes/personnel)
       // Adapter les colonnes selon le rôle de l'utilisateur
       if (selectedPerson.role === 'artistesProd') {
         headers = ['Événement', 'Date', 'Heure début', 'Heure fin', 'Durée', 'Salle', 'Type', 'Montant', 'Statut'];
         
         personEvents.forEach(event => {
           const duration = event.start && event.end ? 
             ((new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60)).toFixed(1) + 'h' : 'N/A';
           
           const typeText = event.type === 'show' ? 'Spectacle' : 
                           event.type === 'repetition' ? 'Répétition' : 
                           event.type === 'rental' ? 'Location' : 
                           event.type === 'ticketing' ? 'Billetterie' : 
                           event.type === 'permanence' ? 'Permanence' : 
                           event.type === 'event' ? 'Événement' : event.type;
           
           // Afficher le montant uniquement si la facture est acceptée ou payée
           let montantText = 'N/A';
           let statutText = event.invoiceStatus || 'Devis';
           
           if (event.invoiceStatus === 'accepted' || event.invoiceStatus === 'paid') {
             montantText = event.amount ? `${event.amount}€` : 'N/A';
           }
           
           // Traduire le statut en français
           if (statutText === 'accepted') statutText = 'Accepté';
           else if (statutText === 'paid') statutText = 'Payé';
           else if (statutText === 'pending') statutText = 'En attente';
           else if (statutText === 'draft') statutText = 'Brouillon';
           
           tableData.push([
             event.title || 'Sans titre',
             format(new Date(event.start), 'dd/MM/yyyy'),
             format(new Date(event.start), 'HH:mm'),
             event.end ? format(new Date(event.end), 'HH:mm') : 'N/A',
             duration,
             event.room?.name || event.roomName || 'Non spécifiée',
             typeText,
             montantText,
             statutText
           ]);
         });
       } else if (selectedPerson.role === 'billeterie') {
         headers = ['Événement', 'Date', 'Heure début', 'Heure fin', 'Durée', 'Salle', 'Type'];
         
         personEvents.forEach(event => {
           const duration = event.start && event.end ? 
             ((new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60)).toFixed(1) + 'h' : 'N/A';
           
           // Pour les événements de gestion du personnel, afficher le type exact
           let typeText;
           if (event.type === 'gestion' || event.type === 'personnel') {
             if (event.personnelType === 'maladie') {
               typeText = 'Maladie';
             } else if (event.personnelType === 'CP') {
               typeText = 'Congés payés';
             } else if (event.personnelType === 'télétravail') {
               typeText = 'Télétravail';
             } else if (event.personnelType === 'absence') {
               typeText = 'Absence';
             } else {
               typeText = 'Autre';
             }
           } else {
             typeText = event.type === 'show' ? 'Spectacle' : 
                       event.type === 'repetition' ? 'Répétition' : 
                       event.type === 'rental' ? 'Location' : 
                       event.type === 'ticketing' ? 'Billetterie' : 
                       event.type === 'permanence' ? 'Permanence' : 
                       event.type === 'event' ? 'Événement' : event.type;
           }
           
           tableData.push([
             event.title || 'Sans titre',
             format(new Date(event.start), 'dd/MM/yyyy'),
             format(new Date(event.start), 'HH:mm'),
             event.end ? format(new Date(event.end), 'HH:mm') : 'N/A',
             duration,
             event.room?.name || event.roomName || 'Non spécifiée',
             typeText
           ]);
         });
       } else {
         headers = ['Événement', 'Date', 'Heure début', 'Heure fin', 'Durée', 'Salle', 'Type', 'Annulé'];
         
         personEvents.forEach(event => {
           const duration = event.start && event.end ? 
             ((new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60)).toFixed(1) + 'h' : 'N/A';
           
           // Pour les événements de gestion du personnel, afficher le type exact
           let typeText;
           if (event.type === 'gestion' || event.type === 'personnel') {
             if (event.personnelType === 'maladie') {
               typeText = 'Maladie';
             } else if (event.personnelType === 'CP') {
               typeText = 'Congés payés';
             } else if (event.personnelType === 'télétravail') {
               typeText = 'Télétravail';
             } else if (event.personnelType === 'absence') {
               typeText = 'Absence';
             } else {
               typeText = 'Autre';
             }
           } else {
             typeText = event.type === 'show' ? 'Spectacle' : 
                       event.type === 'repetition' ? 'Répétition' : 
                       event.type === 'rental' ? 'Location' : 
                       event.type === 'ticketing' ? 'Billetterie' : 
                       event.type === 'permanence' ? 'Permanence' : 
                       event.type === 'régie' ? 'Régie' : 
                       event.type === 'event' ? 'Événement' : event.type;
           }
           
           // Déterminer si le spectacle est annulé
           const isCancelled = event.showStatus === 'cancelled' || 
                              (event.title && event.title.toLowerCase().includes('annulé')) ||
                              (event.description && event.description.toLowerCase().includes('annulé'));
           
           tableData.push([
             event.title || 'Sans titre',
             format(new Date(event.start), 'dd/MM/yyyy'),
             format(new Date(event.start), 'HH:mm'),
             event.end ? format(new Date(event.end), 'HH:mm') : 'N/A',
             duration,
             event.room?.name || event.roomName || 'Non spécifiée',
             typeText,
             isCancelled ? 'Oui' : 'Non'
           ]);
         });
       }
     }
    
    // Titre de section
    doc.setTextColor(...primaryColor);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('DÉTAILS DES ÉVÉNEMENTS', 20, yPosition);
    yPosition += 5;
    
    // Ligne de séparation
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(0.5);
    doc.line(20, yPosition, 190, yPosition);
    yPosition += 10;
    
    // Tableau principal
    autoTable(doc, {
      startY: yPosition,
      head: [headers],
      body: tableData,
      theme: 'striped',
      headStyles: { 
        fillColor: primaryColor,
        textColor: [255, 255, 255],
        fontSize: 10,
        fontStyle: 'bold',
        halign: 'center'
      },
      bodyStyles: {
        fontSize: 9,
        textColor: textColor
      },
      alternateRowStyles: {
        fillColor: accentColor
      },
      columnStyles: selectedPerson.role === 'artistesProd' ? {
         0: { halign: 'left', cellWidth: 'auto' },
         1: { halign: 'center', cellWidth: 20 },
         2: { halign: 'center', cellWidth: 16 },
         3: { halign: 'center', cellWidth: 16 },
         4: { halign: 'center', cellWidth: 16 },
         5: { halign: 'left', cellWidth: 'auto' },
         6: { halign: 'center', cellWidth: 20 },
         7: { halign: 'right', cellWidth: 20 },
         8: { halign: 'center', cellWidth: 20 }
       } : selectedPerson.role === 'billeterie' ? {
         0: { halign: 'left', cellWidth: 'auto' },
         1: { halign: 'center', cellWidth: 20 },
         2: { halign: 'center', cellWidth: 16 },
         3: { halign: 'center', cellWidth: 16 },
         4: { halign: 'center', cellWidth: 16 },
         5: { halign: 'left', cellWidth: 'auto' },
         6: { halign: 'center', cellWidth: 'auto' }
       } : selectedPerson.isRoom ? {
         0: { halign: 'left', cellWidth: 'auto' },
         1: { halign: 'center', cellWidth: 20 },
         2: { halign: 'center', cellWidth: 16 },
         3: { halign: 'center', cellWidth: 16 },
         4: { halign: 'center', cellWidth: 'auto' }
       } : {
         0: { halign: 'left', cellWidth: 'auto' },
         1: { halign: 'center', cellWidth: 20 },
         2: { halign: 'center', cellWidth: 16 },
         3: { halign: 'center', cellWidth: 16 },
         4: { halign: 'center', cellWidth: 16 },
         5: { halign: 'left', cellWidth: 'auto' },
         6: { halign: 'center', cellWidth: 20 },
         7: { halign: 'center', cellWidth: 'auto' }
       },
      margin: { left: 20, right: 20 },
      styles: {
        overflow: 'linebreak',
        cellWidth: 'wrap'
      }
    });
    
    // Ajouter des statistiques en bas (toujours afficher le résumé)
    const finalY = (doc.lastAutoTable && doc.lastAutoTable.finalY) ? doc.lastAutoTable.finalY : 120;
    
    // Gestion du résumé selon le type d'utilisateur
    if (isArtist) {
      // Résumé spécialisé pour les artistes
      const summary = computeArtistShowSummary(personEvents);
      renderArtistSummaryPDF(doc, finalY + 12, summary);
    } else {
      // Ancien comportement pour les autres utilisateurs
      let yPosition = finalY + 20;
      
      // Si on dépasse la page, créer une nouvelle page pour le résumé
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 30;
      }
      
      doc.setTextColor(...primaryColor);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('RÉSUMÉ', 20, yPosition);
      
      doc.setDrawColor(...primaryColor);
      doc.line(20, yPosition + 3, 190, yPosition + 3);
        
        doc.setTextColor(...textColor);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        
        if (selectedPerson.isAbsence) {
        // Calculer les totaux par type d'absence
        const congesPayesEvents = personEvents.filter(event => event.personnelType === 'CP');
        const maladieEvents = personEvents.filter(event => event.personnelType === 'maladie');
        const absenceEvents = personEvents.filter(event => event.personnelType === 'absence');
        const teletravailEvents = personEvents.filter(event => event.personnelType === 'télétravail');
        
        const congesPayesHours = congesPayesEvents.reduce((sum, event) => sum + (event.hours || 0), 0);
        const maladieHours = maladieEvents.reduce((sum, event) => sum + (event.hours || 0), 0);
        const absenceHours = absenceEvents.reduce((sum, event) => sum + (event.hours || 0), 0);
        const teletravailHours = teletravailEvents.reduce((sum, event) => sum + (event.hours || 0), 0);
        
        const congesPayesDays = (congesPayesHours / 7).toFixed(1);
        const maladieDays = (maladieHours / 7).toFixed(1);
        
          let yPos = yPosition + 15;
          doc.text(`Total congés payés : ${congesPayesDays} jours`, 20, yPos);
          yPos += 8;
          doc.text(`Total maladie : ${maladieDays} jours`, 20, yPos);
          yPos += 8;
          doc.text(`Total absences : ${absenceHours.toFixed(1)} heures`, 20, yPos);
          yPos += 8;
          doc.text(`Total télétravail : ${teletravailHours.toFixed(1)} heures`, 20, yPos);
      } else {
        const totalHours = personEvents.reduce((sum, event) => {
          // Exclure la gestion du personnel du calcul des heures
          if (event.type === 'gestion' || event.type === 'personnel' || event.type === 'administratif') {
            return sum;
          }
          
          if (event.start && event.end) {
            return sum + ((new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60));
          }
          return sum;
        }, 0);
        
        // Calculer séparément les heures de télétravail
        const teleworkHours = personEvents.reduce((sum, event) => {
          if (event.type === 'telework' || event.type === 'télétravail') {
            if (event.start && event.end) {
              return sum + ((new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60));
            }
          }
          return sum;
        }, 0);
        
        let yPos = yPosition + 15;
        doc.text(`Total des heures (hors gestion): ${totalHours.toFixed(1)}h`, 20, yPos);
        yPos += 10;
        if (teleworkHours > 0) {
          doc.text(`Heures de télétravail: ${teleworkHours.toFixed(1)}h`, 20, yPos);
          yPos += 10;
        }
         // Ligne supprimée - informations détaillées affichées dans les sections spécifiques ci-dessous
        
        // Résumé standardisé pour tous les utilisateurs avec heures du personnel
        
        // Calculer les heures de gestion du personnel
        const personnelHours = personEvents.reduce((sum, event) => {
          if (event.type === 'gestion' || event.type === 'personnel') {
            if (event.personnelType === 'télétravail' && event.start && event.end) {
              return sum + ((new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60));
            }
          }
          return sum;
        }, 0);
        
        // Calculer les événements par type
        const showEvents = personEvents.filter(event => event.type === 'show');
        const repetitionEvents = personEvents.filter(event => event.type === 'repetition');
        const eventEvents = personEvents.filter(event => event.type === 'event');
        const ticketingEvents = personEvents.filter(event => event.type === 'ticketing' || event.type === 'permanence');
        const rentalEvents = personEvents.filter(event => event.type === 'rental');
        
        // Calculer les congés payés, maladie et absences
        const congesPayes = personEvents.filter(event => 
          (event.type === 'gestion' || event.type === 'personnel') && event.personnelType === 'CP'
        );
        const maladie = personEvents.filter(event => 
          (event.type === 'gestion' || event.type === 'personnel') && event.personnelType === 'maladie'
        );
        const absences = personEvents.filter(event => 
          (event.type === 'gestion' || event.type === 'personnel') && 
          !['CP', 'maladie', 'télétravail'].includes(event.personnelType)
        );
        
        // Calculer les heures de congés payés et maladie
        const congesPayesHours = congesPayes.reduce((sum, event) => {
          if (event.start && event.end) {
            return sum + ((new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60));
          }
          return sum;
        }, 0);
        
        const maladieHours = maladie.reduce((sum, event) => {
          if (event.start && event.end) {
            return sum + ((new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60));
          }
          return sum;
        }, 0);
        
        const absencesHours = absences.reduce((sum, event) => {
          if (event.start && event.end) {
            return sum + ((new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60));
          }
          return sum;
        }, 0);
        
        // Affichage du résumé standardisé
        yPosition += 10;
        doc.text(`Nombre total d'événements de gestion: ${personEvents.filter(e => e.type === 'gestion' || e.type === 'personnel').length}`, 20, yPosition);
        yPosition += 10;
        doc.text(`Congés payés: ${congesPayes.length} événements (${(congesPayesHours / 7).toFixed(1)} jours)`, 20, yPosition);
        yPosition += 10;
        doc.text(`Maladie: ${maladie.length} événements (${(maladieHours / 7).toFixed(1)} jours)`, 20, yPosition);
        yPosition += 10;
        doc.text(`Absences: ${absences.length} événements (${(absencesHours / 7).toFixed(1)} jours)`, 20, yPosition);
        yPosition += 10;
        // Télétravail en HEURES (plus de /7)
        const teletravailEvents = personEvents.filter(e => 
          ((e.type === 'gestion' || e.type === 'personnel') && (e.personnelType === 'télétravail' || e.personnelType === 'teletravail')) ||
          e.type === 'Télétravail' || e.type === 'télétravail' || e.type === 'teletravail'
        );
        const teletravailHours = teletravailEvents.reduce((sum, event) => {
          if (event.hours) {
            return sum + event.hours;
          } else if (event.start && event.end) {
            return sum + ((new Date(event.end) - new Date(event.start)) / (1000 * 60 * 60));
          }
          return sum;
        }, 0);
        doc.text(`Télétravail: ${teletravailEvents.length} événements (${teletravailHours.toFixed(1)} heures)`, 20, yPosition);
        
        // Affichage des événements de travail
        if (showEvents.length > 0 || repetitionEvents.length > 0 || eventEvents.length > 0 || ticketingEvents.length > 0 || rentalEvents.length > 0) {
          yPosition += 15;
          doc.setFont('helvetica', 'bold');
          doc.text('ÉVÉNEMENTS DE TRAVAIL', 20, yPosition);
          doc.setFont('helvetica', 'normal');
          yPosition += 10;
          
          if (showEvents.length > 0) {
            doc.text(`Spectacles: ${showEvents.length}`, 20, yPosition);
            yPosition += 8;
          }
          if (repetitionEvents.length > 0) {
            doc.text(`Répétitions: ${repetitionEvents.length}`, 20, yPosition);
            yPosition += 8;
          }
          if (eventEvents.length > 0) {
            doc.text(`Événements: ${eventEvents.length}`, 20, yPosition);
            yPosition += 8;
          }
          if (ticketingEvents.length > 0) {
            doc.text(`Billetterie/Permanence: ${ticketingEvents.length}`, 20, yPosition);
            yPosition += 8;
          }
          if (rentalEvents.length > 0) {
            doc.text(`Locations: ${rentalEvents.length}`, 20, yPosition);
            yPosition += 8;
          }
        }
        
        // Affichage des informations financières pour artistesProd
        if (selectedPerson.role === 'artistesProd') {
          const totalAmount = personEvents.reduce((sum, event) => {
            if ((event.invoiceStatus === 'accepted' || event.invoiceStatus === 'paid') && event.amount) {
              return sum + parseFloat(event.amount);
            }
            return sum;
          }, 0);
          
          const paidEvents = personEvents.filter(event => event.invoiceStatus === 'paid');
          const acceptedEvents = personEvents.filter(event => event.invoiceStatus === 'accepted');
          const pendingEvents = personEvents.filter(event => event.invoiceStatus === 'pending' || !event.invoiceStatus);
          
          if (totalAmount > 0 || paidEvents.length > 0 || acceptedEvents.length > 0 || pendingEvents.length > 0) {
            yPosition += 15;
            doc.setFont('helvetica', 'bold');
            doc.text('INFORMATIONS FINANCIÈRES', 20, yPosition);
            doc.setFont('helvetica', 'normal');
            yPosition += 10;
            
            doc.text(`Total facturé (accepté/payé): ${totalAmount.toFixed(2)}€`, 20, yPosition);
            yPosition += 8;
            doc.text(`Factures payées: ${paidEvents.length}`, 20, yPosition);
            yPosition += 8;
            doc.text(`Factures acceptées: ${acceptedEvents.length}`, 20, yPosition);
            yPosition += 8;
            doc.text(`Devis en attente: ${pendingEvents.length}`, 20, yPosition);
          }
        }
      }
    } // Fin du bloc else pour les non-artistes
    
    // Sauvegarder le PDF
    doc.save(fileName);
  };

  /**
   * Exportation des statistiques au format Excel
   */
  const exportStats = async () => {
    // Création d'un nouveau classeur
    const wb = XLSX.utils.book_new();
    
    // Définition des couleurs pour les types d'événements (correspondant aux couleurs du calendrier)
    const eventTypeColors = {
      'Spectacles': '9ED2F4',      // Bleu clair
      'Calages': 'B5927D',        // Marron clair
      'Locations': 'CC7A3D',      // Orange
      'Événements': 'D6406E',     // Rose
      'Permanence': 'D4B943',     // Jaune
      'Billetterie': '2F7A4B',    // Vert foncé
      'Régie': 'A683CC',          // Violet
      'Disponible': '4CAF50',     // Vert
      'Non disponible': 'F44336'  // Rouge
    };
    
    // Définition des couleurs pour les statuts de spectacle
    const showStatusColors = {
      'Confirmés': '3A6BCF',       // Bleu primaire
      'Provisoires': '9ED2F4',     // Bleu clair
      'Billetterie ouverte': '0038FF', // Bleu foncé
      'Annulés': '000000'          // Noir
    };
    
    // Filtrer les événements pour obtenir uniquement les spectacles dans le contexte de l'export
    // Appliquer d'abord le filtre par salle si une salle est sélectionnée
    const filteredEvents = selectedRoom === 'all' 
      ? originalEvents 
      : originalEvents.filter(event => event.roomId === parseInt(selectedRoom));
    
    // Ensuite, filtrer pour obtenir uniquement les spectacles
    const exportShowEvents = filteredEvents.filter(event => {
      const eventType = event.type ? event.type.toLowerCase() : '';
      const eventTitle = event.title ? event.title.toLowerCase() : '';
      return eventType === 'show' || eventType.includes('spectacle') || eventTitle.includes('spectacle');
    });
    
    // Données pour les statistiques générales avec colonne mois
    // Calculer le nombre net de spectacles (programmés - annulés)
    const totalShows = exportShowEvents.length;
    const cancelledShows = exportShowEvents.filter(evt => evt.showStatus === 'cancelled').length;
    const netShows = totalShows - cancelledShows;
    
    // Calculer le nombre net de spectacles par type
    const productionShows = exportShowEvents.filter(evt => evt.showType === 'production' || evt.production);
    const productionCancelledShows = productionShows.filter(evt => evt.showStatus === 'cancelled').length;
    const netProductionShows = productionShows.length - productionCancelledShows;
    
    const coproductionShows = exportShowEvents.filter(evt => evt.showType === 'coproduction' || evt.coProd);
    const coproductionCancelledShows = coproductionShows.filter(evt => evt.showStatus === 'cancelled').length;
    const netCoproductionShows = coproductionShows.length - coproductionCancelledShows;
    
    const oneShows = exportShowEvents.filter(evt => evt.showType === 'one' || evt.one);
    const oneCancelledShows = oneShows.filter(evt => evt.showStatus === 'cancelled').length;
    const netOneShows = oneShows.length - oneCancelledShows;
    
    const generalStats = [
      ['Type d\'événement', 'Nombre', 'Mois'],
      ['Total', stats.events, formattedPeriod],
      ['Spectacles (net)', netShows, formattedPeriod],
      ['Calages', stats.rehearsals, formattedPeriod],
      ['Locations', stats.rentals, formattedPeriod],
      ['Événements (type event)', stats.total, formattedPeriod],
      ['Spectacles en production (net)', netProductionShows, formattedPeriod],
      ['Spectacles en co-production (net)', netCoproductionShows, formattedPeriod],
      ['Spectacles en ONE (net)', netOneShows, formattedPeriod],
      ['Spectacles annulés', cancelledShows, formattedPeriod],
      ['Spectacles en production annulés', productionCancelledShows, formattedPeriod],
      ['Spectacles en co-production annulés', coproductionCancelledShows, formattedPeriod],
      ['Spectacles en ONE annulés', oneCancelledShows, formattedPeriod]
    ];
    


    // Données pour les statistiques des artistesProds avec détails des spectacles
    const artistsData = [
      ['artistesProd', 'Email', 'IBAN', 'Spectacle', 'Date', 'Salle', 'Total spectacles', 'Mois']
    ];

    let usersById = {};
    try {
      const usersRes = await api.get('/users');
      const users = usersRes?.data?.data?.users || [];
      users.forEach(u => { usersById[u.id] = u; });
    } catch (e) {
      console.error('Erreur chargement utilisateurs pour export artistes:', e);
    }
    
    artistStats.forEach(artist => {
      const u = usersById[artist.id];
      const email = u?.email || '';
      const iban = formatIban(u?.iban || '');
      artistsData.push([artist.name, email, iban, '', '', '', artist.count, formattedPeriod]);
      
      // Détails de chaque spectacle (triés par date)
      if (artist.shows && artist.shows.length > 0) {
        // Trier les spectacles par date
        const sortedShows = [...artist.shows].sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateA - dateB;
        });
        
        sortedShows.forEach(show => {
          artistsData.push([
            '',
            '',
            '',
            show.title,
            new Date(show.date).toLocaleDateString('fr-FR'),
            resolveRoomName(show),
            '',
            formattedPeriod
          ]);
        });
      }
      
      // Ligne vide pour séparer les artistes
      artistsData.push(['', '', '', '', '', '', '', '']);
    });

    // Feuilles par rôle (régie, billetterie, permanence) avec coordonnées et total d'heures sur la période
    const regisseurData = [
      ['Prénom', 'Nom', 'Email', 'IBAN', 'Total heures (mois)']
    ];
    const billetterieData = [
      ['Prénom', 'Nom', 'Email', 'IBAN', 'Total heures (mois)']
    ];
    const permanenceData = [
      ['Prénom', 'Nom', 'Email', 'IBAN', 'Total heures (mois)']
    ];

    const pushRoleRow = (staff, targetArray) => {
      const u = usersById[staff.id];
      const firstName = u?.firstName || '';
      const lastName = u?.lastName || '';
      const email = u?.email || '';
      const iban = formatIban(u?.iban || '');
      const hours = typeof staff.hours === 'number'
        ? staff.hours.toFixed(1)
        : parseFloat(staff.hours || 0).toFixed(1);
      targetArray.push([firstName, lastName, email, iban, hours]);
    };

    staffStats.forEach(staff => {
      if (staff.role === 'regie') {
        pushRoleRow(staff, regisseurData);
      } else if (staff.role === 'billeterie') {
        pushRoleRow(staff, billetterieData);
      } else if (staff.role === 'permanence') {
        pushRoleRow(staff, permanenceData);
      }
    });
    
    // Données pour les statistiques du personnel avec détails des événements
const staffData = [
  ['Membre du personnel',
   'Rôle',
   'Type d’événement',
   'Événement',
   'Date',            // fusion Date début + Date fin
   'Heure début',
   'Heure fin',
   'Salle',           // Ajout de la colonne Salle
   'Durée (h)',
   'Mois']
];
    
    // Variables pour calculer les statistiques de gestion du personnel
    let totalStaffHours = 0;
    let totalCongesPayesJours = 0;
    let totalTeletravailHeures = 0;
    let totalAutreHeures = 0;
    let totalMaladieHeures = 0;
    let totalAbsenceHeures = 0;
    let totalEvenements = 0;
    let totalSpectacles = 0;
    let totalRepetitions = 0;
    let totalAutresEvenements = 0;
    let totalHeuresTravaillees = 0; // Heures travaillées (hors congés, maladie, absence)
    
    staffStats.forEach(staff => {
      // Ligne principale avec le nom du membre et son total d'heures
      const roleLabel = staff.role === 'billeterie' ? 'Billetterie' : 
                       staff.role === 'permanence' ? 'Permanence' : 
                       staff.role === 'regie' ? 'Régie' : 
                       staff.role === 'technique' ? 'Technique' : staff.role;
      
      // Ajouter les heures de ce membre au total
      totalStaffHours += parseFloat(staff.hours) || 0;
      
// Ajouter une ligne avec le nom du membre du personnel et son rôle
staffData.push([
  staff.name,                  // Membre du personnel
  roleLabel,                   // Rôle
  '',                          // Type d'événement
  '',                          // Événement
  '',                          // Date
  '',                          // Heure début
  '',                          // Heure fin
  '',                          // Salle
  staff.hours.toFixed(2),      // Durée (h) - total des heures pour ce membre
  formattedPeriod              // Mois
]);
      
  // Trier les événements par date avant de les ajouter
  const sortedShows = [...staff.shows].sort((a, b) => {
    const dateA = a.startTime ? new Date(a.startTime) : new Date(a.date);
    const dateB = b.startTime ? new Date(b.startTime) : new Date(b.date);
    return dateA - dateB;
  });
  
  // Détails de chaque événement (triés par date)
  sortedShows.forEach(show => {
    // 1) Dates / heures de l’événement
    const start = show.startTime ? new Date(show.startTime) : null;
    const end   = show.endTime   ? new Date(show.endTime)   : null;

    const dateDéb  = start
      ? start.toLocaleDateString('fr-FR')
      : 'N/A';
    const heureDéb = start
      ? start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
      : 'N/A';
    const dateFin  = end
      ? end.toLocaleDateString('fr-FR')
      : 'N/A';
    const heureFin = end
      ? end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
      : 'N/A';

    // 2) Durée calculée
    const durée = (start && end)
      ? ((end - start) / (1000 * 60 * 60)).toFixed(2)
      : 'N/A';
    
    // 3) Calculer les statistiques par catégorie de gestion du personnel
    totalEvenements++;
    
    // Compter les événements par type
    if (show.type === 'spectacle') {
      totalSpectacles++;
    } else if (show.type === 'repetition') {
      totalRepetitions++;
    } else {
      totalAutresEvenements++;
    }
    
    // Calculer les heures travaillées (tous les événements sauf congés, maladie, absence)
    const heures = parseFloat(durée) || 0;
    let estHeureTravaillee = true;
    
    if (show.type === 'personnel' && show.personnelType) {
      switch (show.personnelType) {
        case 'CP':
          // Congés payés en jours (8h = 1 jour)
          totalCongesPayesJours += heures / 7;
          estHeureTravaillee = false;
          break;
        case 'teletravail':
          totalTeletravailHeures += heures;
          break;
        case 'autre':
          totalAutreHeures += heures;
          break;
        case 'maladie':
          totalMaladieHeures += heures;
          estHeureTravaillee = false;
          break;
        case 'absence':
          totalAbsenceHeures += heures;
          estHeureTravaillee = false;
          break;
      }
    }
    
    // Ajouter aux heures travaillées si ce n'est pas un congé, maladie ou absence
    if (estHeureTravaillee) {
      totalHeuresTravaillees += heures;
    }
    
    // 4) Pousser la ligne détaillée
    // 1) préparer la chaîne fusionnée dateDéb – dateFin
    const dates = dateDéb + ' – ' + dateFin;

    staffData.push([
      staff.name,                  // Membre du personnel (ajout du nom de l'utilisateur assigné)
      show.eventRole    || '',     // Rôle
      show.type === 'personnel' ? 
        `Personnel (${show.personnelType ? show.personnelType.charAt(0).toUpperCase() + show.personnelType.slice(1) : 'N/A'})` : 
        (show.type || 'N/A'),      // Type d'événement avec type de personnel entre parenthèses
      show.title,                  // Événement
      dates,                       // Date (début – fin)
      heureDéb,                    // Heure début
      heureFin,                    // Heure fin
      resolveRoomName(show),    // Salle
      durée,                       // Durée (h)
      formattedPeriod              // Mois
    ]);
  });
  // Ajouter une ligne vide après chaque membre du personnel
  staffData.push(['', '', '', '', '', '', '', '', '', '']);

    });
    
    // Ajouter une ligne pour le total des heures de tous les membres du personnel
    // Ajouter un résumé détaillé de la gestion du personnel
    staffData.push(
      ['', '', '', '', '', '', '', '', '', ''], // Ligne vide
      ['RÉSUMÉ', '', '', '', '', '', '', '', '', ''],
      ['', '', '', '', '', '', '', '', '', ''],
      ['Total des heures travaillées:', totalHeuresTravaillees.toFixed(1) + 'h', '', '', '', '', '', '', '', ''],
       ['', '', '', '', '', '', '', '', '', ''],
       ['Nombre total d\'événements:', totalEvenements, '', '', '', '', '', '', '', ''],
       ['', '', '', '', '', '', '', '', '', ''],
       ['Spectacles:', totalSpectacles, '', '', '', '', '', '', '', ''],
       ['', '', '', '', '', '', '', '', '', ''],
       ['Répétitions:', totalRepetitions, '', '', '', '', '', '', '', ''],
       ['', '', '', '', '', '', '', '', '', ''],
       ['Autres événements:', totalAutresEvenements, '', '', '', '', '', '', '', ''],
      ['', '', '', '', '', '', '', '', '', ''],
      ['Congés payés:', totalCongesPayesJours.toFixed(1) + ' jours', '', '', '', '', '', '', '', ''],
      ['', '', '', '', '', '', '', '', '', ''],
      ['Télétravail:', totalTeletravailHeures.toFixed(1) + 'h', '', '', '', '', '', '', '', ''],
      ['', '', '', '', '', '', '', '', '', ''],
      ['Maladie:', totalMaladieHeures.toFixed(1) + 'h', '', '', '', '', '', '', '', ''],
      ['', '', '', '', '', '', '', '', '', ''],
      ['Absence:', totalAbsenceHeures.toFixed(1) + 'h', '', '', '', '', '', '', '', ''],
      ['', '', '', '', '', '', '', '', '', ''],
      ['Autres événements:', totalAutreHeures.toFixed(1) + 'h', '', '', '', '', '', '', '', '']
    );
    
    // Données pour les statuts des spectacles avec colonne mois
    // Calcul du nombre net de spectacles (programmés - annulés)
    const netConfirmedShows = showStatusStats.confirmed - showStatusStats.cancelled;
    
    const showStatusData = [
      ['Statut', 'Nombre de spectacles', 'Mois'],
      ['Confirmés (net)', netConfirmedShows, formattedPeriod],
      ['Provisoires', showStatusStats.provisional, formattedPeriod],
      ['Billetterie ouverte', showStatusStats.ticketsOpen, formattedPeriod]
      // Ligne 'Annulés' supprimée car non pertinente pour la gestion du personnel
    ];
    
    // Création des feuilles de calcul
    const wsGeneral = XLSX.utils.aoa_to_sheet(generalStats);
    const wsArtists = XLSX.utils.aoa_to_sheet(artistsData);
    const wsRegisseurs = XLSX.utils.aoa_to_sheet(regisseurData);
    const wsBilletterie = XLSX.utils.aoa_to_sheet(billetterieData);
    const wsPermanence = XLSX.utils.aoa_to_sheet(permanenceData);
    const wsStaff = XLSX.utils.aoa_to_sheet(staffData);
    const wsShowStatus = XLSX.utils.aoa_to_sheet(showStatusData);
    
    // Fonction pour appliquer des styles à une feuille
    const applyStyles = (worksheet, data, options = {}) => {
      // Définir les largeurs de colonnes
      worksheet['!cols'] = options.cols || data[0].map(() => ({ wch: 20 }));
      
      // Appliquer les styles aux en-têtes (première ligne)
      const headerStyle = {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { patternType: 'solid', fgColor: { rgb: options.headerColor || '3A6BCF' } },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        border: {
          top: { style: 'thin', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } }
        }
      };
      
      // Style pour les lignes de données
      const dataStyle = {
        alignment: { vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } }
        }
      };
      
      // Style pour les lignes alternées
      const altRowStyle = {
        ...dataStyle,
        fill: { patternType: 'solid', fgColor: { rgb: 'F5F5F5' } }
      };
      
      // Appliquer les styles à chaque cellule
      for (let i = 0; i < data.length; i++) {
        // Déterminer le style de base pour la ligne
        let rowStyle = i === 0 ? headerStyle : (i % 2 === 0 ? dataStyle : altRowStyle);
        
        // Pour les lignes de données dans les statistiques générales, appliquer les couleurs spécifiques
        if (options.typeColors && i > 0 && data[i][0]) {
          const typeName = data[i][0];
          if (options.typeColors[typeName]) {
            rowStyle = {
              ...rowStyle,
              fill: { patternType: 'solid', fgColor: { rgb: options.typeColors[typeName] } }
            };
          }
        }
        
        // Pour les lignes de données dans les statuts de spectacle, appliquer les couleurs spécifiques
        if (options.statusColors && i > 0 && data[i][0]) {
          const statusName = data[i][0];
          if (options.statusColors[statusName]) {
            rowStyle = {
              ...rowStyle,
              fill: { patternType: 'solid', fgColor: { rgb: options.statusColors[statusName] } }
            };
          }
        }
        
        for (let j = 0; j < data[i].length; j++) {
          const cellRef = XLSX.utils.encode_cell({ r: i, c: j });
          if (!worksheet[cellRef]) continue;
          
          worksheet[cellRef].s = { ...rowStyle };
          
          // Alignement spécial pour les nombres (à droite)
          if (i > 0 && typeof data[i][j] === 'number') {
            worksheet[cellRef].s.alignment = { ...worksheet[cellRef].s.alignment, horizontal: 'right' };
          }
          
          // Mettre en évidence la colonne du mois
          if (j === data[i].length - 1 && i > 0) {
            worksheet[cellRef].s.font = { ...worksheet[cellRef].s.font, bold: true };
          }
          
          // Appliquer des styles spécifiques à certaines colonnes si nécessaire
          if (options.columnStyles && options.columnStyles[j]) {
            worksheet[cellRef].s = { ...worksheet[cellRef].s, ...options.columnStyles[j] };
          }
        }
      }
    };
    
    // Appliquer les styles à la feuille des statistiques générales
    applyStyles(wsGeneral, generalStats, {
      headerColor: '3A6BCF', // Bleu primaire
      cols: [
        { wch: 25 }, // Colonne Type d'événement
        { wch: 15 }, // Colonne Nombre
        { wch: 20 }  // Colonne Mois
      ],
      columnStyles: {
        1: { alignment: { horizontal: 'center' } }, // Centrer les nombres
        2: { alignment: { horizontal: 'center' } }  // Centrer le mois
      },
      typeColors: eventTypeColors
    });
    
    // Appliquer les styles à la feuille des artistesProds
    applyStyles(wsArtists, artistsData, {
      headerColor: '9ED2F4', // Bleu clair
      cols: [
        { wch: 30 },
        { wch: 28 },
        { wch: 24 },
        { wch: 30 },
        { wch: 12 },
        { wch: 20 },
        { wch: 18 },
        { wch: 15 }
      ],
      columnStyles: {
        4: { alignment: { horizontal: 'center' } },
        5: { alignment: { horizontal: 'center' } },
        6: { alignment: { horizontal: 'center' } },
        7: { alignment: { horizontal: 'center' } }
      }
    });

    // Appliquer les styles à la feuille Régisseur
    applyStyles(wsRegisseurs, regisseurData, {
      headerColor: 'A683CC',
      cols: [
        { wch: 18 },
        { wch: 18 },
        { wch: 28 },
        { wch: 26 },
        { wch: 18 }
      ],
      columnStyles: {
        4: { alignment: { horizontal: 'center' } }
      }
    });

    // Appliquer les styles à la feuille Billetterie
    applyStyles(wsBilletterie, billetterieData, {
      headerColor: '2F7A4B',
      cols: [
        { wch: 18 },
        { wch: 18 },
        { wch: 28 },
        { wch: 26 },
        { wch: 18 }
      ],
      columnStyles: {
        4: { alignment: { horizontal: 'center' } }
      }
    });

    // Appliquer les styles à la feuille Permanence
    applyStyles(wsPermanence, permanenceData, {
      headerColor: 'D4B943',
      cols: [
        { wch: 18 },
        { wch: 18 },
        { wch: 28 },
        { wch: 26 },
        { wch: 18 }
      ],
      columnStyles: {
        4: { alignment: { horizontal: 'center' } }
      }
    });
    
    // Appliquer les styles à la feuille du personnel
    applyStyles(wsStaff, staffData, {
      headerColor: 'A683CC', // Violet (couleur régie)
      cols: [
        { wch: 30 }, // Colonne Membre du personnel
        { wch: 20 }, // Colonne Rôle
        { wch: 30 }, // Colonne Événement
        { wch: 15 }, // Colonne Date
        { wch: 15 }, // Colonne Heures
        { wch: 15 }, // Colonne Total heures
        { wch: 20 }  // Colonne Mois
      ],
      columnStyles: {
        4: { alignment: { horizontal: 'center' } }, // Centrer les heures individuelles
        5: { alignment: { horizontal: 'center' } }, // Centrer les totaux d'heures
        6: { alignment: { horizontal: 'center' } }  // Centrer le mois
      }
    });
    
    // Appliquer un style spécial à la ligne de total
    const totalRowIndex = staffData.length - 1;
    const totalStyle = {
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: 'A683CC' } }, // Même couleur que l'en-tête
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: '000000' } },
        left: { style: 'thin', color: { rgb: '000000' } },
        bottom: { style: 'thin', color: { rgb: '000000' } },
        right: { style: 'thin', color: { rgb: '000000' } }
      }
    };
    
    // Appliquer le style à chaque cellule de la ligne de total
    for (let j = 0; j < staffData[totalRowIndex].length; j++) {
      const cellRef = XLSX.utils.encode_cell({ r: totalRowIndex, c: j });
      if (wsStaff[cellRef]) {
        wsStaff[cellRef].s = totalStyle;
      }
    }
    
    // Appliquer les styles à la feuille des statuts de spectacles
    applyStyles(wsShowStatus, showStatusData, {
      headerColor: '0038FF', // Bleu foncé
      cols: [
        { wch: 25 }, // Colonne Statut
        { wch: 20 }, // Colonne Nombre de spectacles
        { wch: 20 }  // Colonne Mois
      ],
      columnStyles: {
        1: { alignment: { horizontal: 'center' } }, // Centrer les nombres
        2: { alignment: { horizontal: 'center' } }  // Centrer le mois
      },
      statusColors: showStatusColors
    });
    
    // Ajout des feuilles au classeur
    XLSX.utils.book_append_sheet(wb, wsGeneral, 'Statistiques générales');
    XLSX.utils.book_append_sheet(wb, wsArtists, 'artistesProds');
    XLSX.utils.book_append_sheet(wb, wsRegisseurs, 'Régisseur');
    XLSX.utils.book_append_sheet(wb, wsBilletterie, 'Billetterie');
    XLSX.utils.book_append_sheet(wb, wsPermanence, 'Permanence');
    XLSX.utils.book_append_sheet(wb, wsStaff, 'Personnel');
    XLSX.utils.book_append_sheet(wb, wsShowStatus, 'Statut des spectacles');


  // -------------------------------------------------
  // 🚀 Feuille "ANNEE 2025" calquée sur votre SUIVI SPECTACLE CDA.xlsx
  // -------------------------------------------------
  const currentYear = new Date().getFullYear();
  // 1) Les 6 premières lignes = votre légende (suppression de la ligne 7)
  const legendRows = [
    // ligne 1 - DOSSIER CLOS (vert) et CO PROD (jaune)
    ['DOSSIER CLOS','CO PROD', ...Array(33).fill('')],
    // ligne 2 - BILAN ENVOYE (orange) et PRODUCTION (cyan)
    ['BILAN ENVOYE','PRODUCTION', ...Array(33).fill('')],
    // ligne 3 - ONE (rose/magenta)
    ['','ONE','', ' ', ...Array(32).fill('')],
    // ligne 4 - SPECTACLE ANNULEE (noir avec texte blanc)
    ['🔍🔎','SPECTACLE ANNULEE', ...Array(33).fill('')],
    // ligne 5 - LOCATION SALLE (marron/doré)
    ['','LOCATION SALLE', ...Array(33).fill('')],
    // ligne 6 (vide)
    Array(35).fill('')
  ];

  // 2) Vos en-têtes (ligne 8) - Modification des colonnes
  const suiviHeaders = [
    'SALLE','NOM  artistesProd ou COMEDIE','du','au','horaire','Séances joué','Séances annulé','nombre VHR','ADRESSE','JAUGE','ONE',
    'COMEDIE COPROD','COMEDIE PROD','LOCATION SALLE','CONTRAT','contact Production','contact régisseur',
    'Ouverture billetterie','Commande FLYERS','HOTEL OK','NOM HOTEL','arrivé date et heure','Lieu arrivée',
    'TRANSFERT arrivée','DEPART date et heure','lieu départ','transfert Départ',
    'TRANSFERT départ','bilan  soirée remis','DROIT DIRECT MES','Décla SACD','date envoi décla SACD','SACEM',
    'date envoi décla SACEM','bilan envoyé prod','date envoi  bilan','FACTURE PROD DRIVE','SPECTACLE CLOTURE','NOTES DETAILS ARTISTES','COMMENTAIRES'
  ];

  // 3) Les données à partir des événements filtrés par salle (ligne 9 et suivantes)
  // Utiliser les événements filtrés par salle au lieu de tous les événements originaux
  const eventsToExport = selectedRoom === 'all' 
    ? originalEvents 
    : originalEvents.filter(event => event.roomId === parseInt(selectedRoom));
  
  // Filtrer uniquement les spectacles (type 'show')
  const showEvents = eventsToExport.filter(evt => evt.type === 'show');
  
  // Regrouper les événements par nom de spectacle pour compter les séances jouées et annulées
  const groupedByName = {};
  
  showEvents.forEach(event => {
    // Normaliser le titre (insensible à la casse et espaces)
    const originalTitle = event.title || 'Sans titre';
    const normalizedTitle = originalTitle.trim().toLowerCase();
    
    if (!groupedByName[normalizedTitle]) {
      groupedByName[normalizedTitle] = {
        ...event,
        title: originalTitle, // Garder le titre original pour l'affichage
        seancesJouees: 0,
        seancesAnnulees: 0,
        events: [],
        dates: [] // Pour calculer la plage de dates
      };
    }
    
    groupedByName[normalizedTitle].events.push(event);
    
    // Ajouter la date de l'événement pour calculer la plage
    if (event.start) {
      groupedByName[normalizedTitle].dates.push(new Date(event.start));
    }
    
    // Compter les séances jouées et annulées
    if (event.showStatus === 'cancelled') {
      groupedByName[normalizedTitle].seancesAnnulees += 1;
    } else {
      groupedByName[normalizedTitle].seancesJouees += 1;
    }
  });
  
  // Calculer les plages de dates pour chaque spectacle
  Object.values(groupedByName).forEach(group => {
    if (group.dates.length > 0) {
      // Trier les dates
      group.dates.sort((a, b) => a - b);
      // Définir la date de début comme la première date
      group.start = group.dates[0];
      // Définir la date de fin comme la dernière date
      group.end = group.dates[group.dates.length - 1];
    }
  });
  
  // Convertir en tableau
  const mergedEvents = Object.values(groupedByName);
  
  // Trier les événements par date de début croissante (du plus ancien au plus récent)
  const sortedEvents = [...mergedEvents].sort((a, b) => {
    // Trier par date de début (du plus ancien au plus récent)
    if (a.start && b.start) {
      return new Date(a.start) - new Date(b.start);
    }
    // Si un seul événement a une date de début, le placer avant
    if (a.start) return -1;
    if (b.start) return 1;
    // Si aucun n'a de date de début, trier par date de création
    if (a.createdAt && b.createdAt) {
      return new Date(a.createdAt) - new Date(b.createdAt);
    }
    // Si un seul événement a une date de création, le placer avant
    if (a.createdAt) return -1;
    if (b.createdAt) return 1;
    // Si aucun n'a de date de création, conserver l'ordre original
    return 0;
  });
  
  // Déterminer si on doit afficher le nom de la salle dans chaque ligne
  const shouldShowRoomInRows = selectedRoom === 'all';
  
  const suiviData = sortedEvents.map(evt => {
    // Déterminer la couleur de fond en fonction du type d'événement
    let rowColor = null;
    if (evt.spectacleCloture) rowColor = 'FF00FF00'; // Vert pour DOSSIER CLOS
    else if (evt.bilanEnvoyeProd) rowColor = 'FFFF9900'; // Orange pour BILAN ENVOYE
    else if (evt.type === 'show' && evt.showStatus === 'cancelled') rowColor = 'FF000000'; // Noir pour SPECTACLE ANNULEE
    else if (evt.type === 'rental' || evt.locationSalle) rowColor = 'FF7F6000'; // Marron/doré pour LOCATION SALLE
    else if (evt.one) rowColor = 'FFFF00FF'; // Rose/magenta pour ONE
    else if (evt.openBilletterie) rowColor = 'FFFF9900'; // Orange pour billetterie ouverte
    else if (evt.flyersOrdered || evt.commandeFlyers) rowColor = 'FFFF9900'; // Orange pour flyers commandés
    else if (evt.hotelOrdered || evt.hotelOK) rowColor = 'FFFF9900'; // Orange pour hôtel réservé
    
    // Formater les heures de début et de fin précises
    const startTime = evt.start ? format(new Date(evt.start), 'HH:mm') : '';
    const endTime = evt.end ? format(new Date(evt.end), 'HH:mm') : '';
    const horaire = startTime && endTime ? `${startTime} - ${endTime}` : evt.horaire || '';
    
    // Marquer les types de spectacles spécifiques avec un 'X'
    // Déterminer correctement le type de spectacle en fonction des données disponibles
    let isOne = '';
    let isCoProd = '';
    let isProduction = '';
    let isLocationSalle = '';
    
    // Vérifier le type de spectacle en priorité depuis showType, puis depuis les propriétés spécifiques
    // Si c'est une coproduction, mettre la croix uniquement dans la colonne COMEDIE CO PROD
    if (evt.showType === 'coproduction' || evt.coProd) {
      isCoProd = 'X';
      isOne = '';
      isProduction = '';
    } else {
      // Sinon, vérifier les autres types
      if (evt.showType === 'one' || evt.one) {
        isOne = 'X';
      }
      
      if (evt.showType === 'production' || evt.production) {
        isProduction = 'X';
      }
    }
    
    // Location de salle est indépendant des autres types
    if (evt.type === 'rental' || evt.locationSalle) {
      isLocationSalle = 'X';
    }
    
    // Marquer les informations logistiques avec un 'X' ou la valeur textuelle si disponible
    const commandeFlyers = evt.flyersOrdered || evt.commandeFlyers ? 'X' : '';
    const hotelOK = evt.hotelOrdered || evt.hotelOK ? 'X' : '';
    // Mettre une croix dans la colonne billetterie ouverte si l'événement a ce statut
    const ouvertureBilletterie = evt.openBilletterie || evt.showStatus === 'ticketsOpen' ? 'X' : '';
    
    // Utiliser les valeurs textuelles pour les transferts au lieu des croix
    const transfertArrivee = evt.transfertArriveeText || '';
    const transfertDepart = evt.transfertDepartText || '';
    
    const regieOK = evt.regieOK ? 'X' : '';
    
    // Vérifier les PDFs associés à l'événement pour mettre des croix dans les colonnes correspondantes
    // Fonction helper pour vérifier la présence d'un PDF
    const hasPdfCategory = (category) => {
      if (!evt.Pdfs || !Array.isArray(evt.Pdfs)) return false;
      return evt.Pdfs.some(pdf => pdf && pdf.category === category);
    };
    
    // Debug pour comprendre pourquoi les champs ne s'ajoutent pas
    console.log('Event PDFs debug:', {
      title: evt.title,
      pdfs: evt.Pdfs ? evt.Pdfs.map(pdf => ({ category: pdf.category, filename: pdf.filename })) : 'No PDFs'
    });
    
    // Vérifier si un contrat PDF est présent
    const hasContract = hasPdfCategory('contrat') || evt.Pdfs?.some(pdf => {
      const filename = pdf?.filename?.toLowerCase() || '';
      return filename.includes('contrat') || filename.includes('convention');
    });
    const contratPdf = hasContract ? 'X' : '';
    
    // Vérifier si le spectacle est annulé
    const spectacleAnnule = evt.showStatus === 'cancelled' ? 'X' : '';
    
    // Bilan soirée remis - basé sur bilan_definitif
    const bilanSoireeRemis = evt.bilanSoireeRemis || hasPdfCategory('bilan_definitif') ? 'X' : '';
    
    // Droit direct - basé sur facture_droit_direct
    const droitDirectMes = evt.droitDirectMes || hasPdfCategory('facture_droit_direct') ? 'X' : '';
    
    // Décla SACD - basé sur facture_sacd
    const declaSACD = evt.declaSACD || hasPdfCategory('facture_sacd') ? 'X' : '';
    
    // SACEM - basé sur facture_sacem
    const sacem = evt.sacem || hasPdfCategory('facture_sacem') ? 'X' : '';
    
    // Bilan envoyé prod - pas de mapping automatique
    const bilanEnvoyeProd = evt.bilanEnvoyeProd ? 'X' : '';
    
    // Facture prod - basé sur facture_producteur
    const factureProdDrive = evt.factureProdDrive || hasPdfCategory('facture_producteur') ? 'X' : '';
    
    const spectacleCloture = evt.spectacleCloture ? 'X' : '';
    
    // Récupérer les informations des utilisateurs/artistes liés à l'événement
    let artistsInfo = '';
    let transfertArriveeDetails = '';
    let transfertDepartDetails = '';
    let lieuArriveeDetails = '';
    let lieuDepartDetails = '';
    let quiTransfertArriveeDetails = '';
    let quiTransfertDepartDetails = '';
    let dateHeureArriveeDetails = '';
    let dateHeureDepartDetails = '';
    
    if (evt.Users && evt.Users.length > 0) {
      // Filtrer les utilisateurs qui sont des artistes (rôle artistesProd ou artistesCoProd)
      const artists = evt.Users.filter(user => 
        user.role === 'artistesProd' || user.role === 'artistesCoProd'
      );
      
      if (artists.length > 0) {
        // Formater les noms des artistes
        artistsInfo = artists.map(artist => 
          `${artist.firstName} ${artist.lastName}`
        ).join(', ');
        
        // Récupérer les détails de transfert pour chaque artiste
        artists.forEach(artist => {
          // Récupérer les informations de transfert depuis la relation EventUser
          const userEventDetails = artist.EventUser || artist._through || {};
          
          // Informations d'arrivée
          if (userEventDetails.arrivalLocation) {
            lieuArriveeDetails += `${artist.firstName} ${artist.lastName}: ${userEventDetails.arrivalLocation}\n`;
          }
          
          if (userEventDetails.arrivalTransfer) {
            transfertArriveeDetails += `${artist.firstName} ${artist.lastName}: ${userEventDetails.arrivalTransfer}\n`;
          }
          
          if (userEventDetails.arrivalTime) {
            try {
              const arrivalDate = new Date(userEventDetails.arrivalTime);
              if (!isNaN(arrivalDate.getTime())) {
                const arrivalDateTime = format(arrivalDate, 'dd/MM/yyyy HH:mm');
                dateHeureArriveeDetails += `${artist.firstName} ${artist.lastName}: ${arrivalDateTime}\n`;
              }
            } catch (error) {
              console.warn('Erreur lors du formatage de la date d\'arrivée:', userEventDetails.arrivalTime, error);
            }
          }
          
          // Informations de départ
          if (userEventDetails.departureLocation) {
            lieuDepartDetails += `${artist.firstName} ${artist.lastName}: ${userEventDetails.departureLocation}\n`;
          }
          
          if (userEventDetails.departureTransfer) {
            transfertDepartDetails += `${artist.firstName} ${artist.lastName}: ${userEventDetails.departureTransfer}\n`;
          }
          
          if (userEventDetails.departureTime) {
            try {
              const departureDate = new Date(userEventDetails.departureTime);
              if (!isNaN(departureDate.getTime())) {
                const departureDateTime = format(departureDate, 'dd/MM/yyyy HH:mm');
                dateHeureDepartDetails += `${artist.firstName} ${artist.lastName}: ${departureDateTime}\n`;
              }
            } catch (error) {
              console.warn('Erreur lors du formatage de la date de départ:', userEventDetails.departureTime, error);
            }
          }
          
          // Informations sur qui fait le transfert
          if (userEventDetails.arrivalTransfer) {
            quiTransfertArriveeDetails += `${artist.firstName} ${artist.lastName}: ${userEventDetails.arrivalTransfer}\n`;
          }
          
          if (userEventDetails.departureTransfer) {
            quiTransfertDepartDetails += `${artist.firstName} ${artist.lastName}: ${userEventDetails.departureTransfer}\n`;
          }
        });
      }
    }
    
    // Ajouter les informations des artistes au titre si disponibles
    const titleWithArtists = artistsInfo ? 
      `${evt.title || ''} (${artistsInfo})` : 
      evt.title || '';
    
    // Récupérer les notes détaillées des artistes
    let notesDetailsArtistes = '';
    if (evt.Users && evt.Users.length > 0) {
      const artists = evt.Users.filter(user => 
        user.role === 'artistesProd' || user.role === 'artistesCoProd'
      );
      
      artists.forEach(artist => {
        const userEventDetails = artist.EventUser || artist._through || {};
        if (userEventDetails.notes) {
          notesDetailsArtistes += `${artist.firstName} ${artist.lastName}: ${userEventDetails.notes}\n`;
        }
      });
    }

    // Récupérer le nom de l'hôtel
    let nomHotel = '';
    if (evt.Users && evt.Users.length > 0) {
      const artists = evt.Users.filter(user => 
        user.role === 'artistesProd' || user.role === 'artistesCoProd'
      );
      
      artists.forEach(artist => {
        const userEventDetails = artist.EventUser || artist._through || {};
        if (userEventDetails.accommodation) {
          nomHotel += `${artist.firstName} ${artist.lastName}: ${userEventDetails.accommodation}\n`;
        }
      });
    }

    // Créer la ligne de données avec les nouvelles colonnes pour séances jouées et annulées
    const seancesJouees = evt.seancesJouees || 0;
    const seancesAnnulees = evt.seancesAnnulees || 0;
    
    return [
      shouldShowRoomInRows ? (evt.room?.name || evt.Room?.name || 'Non assignée') : '',
      titleWithArtists,
      evt.start ? (() => {
        try {
          const date = new Date(evt.start);
          return isNaN(date.getTime()) ? 'N/A' : format(date, 'dd/MM/yyyy');
        } catch (error) {
          console.warn('Erreur lors du formatage de la date de début:', evt.start, error);
          return 'N/A';
        }
      })() : 'N/A',
      evt.end ? (() => {
        try {
          const date = new Date(evt.end);
          return isNaN(date.getTime()) ? 'N/A' : format(date, 'dd/MM/yyyy');
        } catch (error) {
          console.warn('Erreur lors du formatage de la date de fin:', evt.end, error);
          return 'N/A';
        }
      })() : 'N/A',
      horaire, 
      seancesJouees, // Nombre de séances jouées
      seancesAnnulees, // Nombre de séances annulées
      evt.expectedAttendees || evt.nombreVHR || '', // Utiliser expectedAttendees pour le nombre VHR
      evt.address || '', // Nouvelle colonne ADRESSE
      evt.jauge || '', // Nouvelle colonne JAUGE
      isOne,
      isCoProd, 
      isProduction, 
      isLocationSalle, 
      contratPdf, // Colonne pour contrat
      evt.contactProdName || evt.productionContact || '',
      evt.stageManagerContact || '', // Contact régisseur
      evt.openBilletterie ? (() => {
        try {
          const date = new Date(evt.openBilletterie);
          return isNaN(date.getTime()) ? ouvertureBilletterie : format(date, 'dd/MM/yyyy');
        } catch (error) {
          console.warn('Erreur lors du formatage de la date d\'ouverture billetterie:', evt.openBilletterie, error);
          return ouvertureBilletterie;
        }
      })() : ouvertureBilletterie,
      commandeFlyers, 
      hotelOK, 
      nomHotel, // Nouvelle colonne pour le nom de l'hôtel
      dateHeureArriveeDetails || (evt.arrivalDate ? (() => {
        try {
          const date = new Date(evt.arrivalDate);
          return isNaN(date.getTime()) ? '' : format(date, 'dd/MM/yyyy HH:mm');
        } catch (error) {
          console.warn('Erreur lors du formatage de la date d\'arrivée:', evt.arrivalDate, error);
          return '';
        }
      })() : ''),
      lieuArriveeDetails || evt.lieuArrivee || evt.arrivalLocation || '', 
      quiTransfertArriveeDetails || evt.quiTransfertArrivee || '',
      dateHeureDepartDetails || (evt.departDate ? (() => {
        try {
          const date = new Date(evt.departDate);
          return isNaN(date.getTime()) ? '' : format(date, 'dd/MM/yyyy HH:mm');
        } catch (error) {
          console.warn('Erreur lors du formatage de la date de départ:', evt.departDate, error);
          return '';
        }
      })() : ''),
      lieuDepartDetails || evt.lieuDepart || evt.departureLocation || '', 
      transfertDepartDetails || transfertDepart || '', 
      quiTransfertDepartDetails || evt.quiTransfertDepart || '',
      bilanSoireeRemis,
      droitDirectMes, 
      // Utiliser sacdDeclared pour la colonne Décla SACD
      evt.sacdDeclared ? 'X' : declaSACD, 
      // Date d'envoi décla SACD
      evt.sacdSendDate ? (() => {
        try {
          const date = new Date(evt.sacdSendDate);
          return isNaN(date.getTime()) ? '' : format(date, 'dd/MM/yyyy');
        } catch (error) {
          console.warn('Erreur lors du formatage de la date d\'envoi SACD:', evt.sacdSendDate, error);
          return '';
        }
      })() : '',
      // Utiliser sacemDeclared pour la colonne SACEM
      evt.sacemDeclared ? 'X' : sacem,
      // Date d'envoi décla SACEM
      evt.sacemSendDate ? (() => {
        try {
          const date = new Date(evt.sacemSendDate);
          return isNaN(date.getTime()) ? '' : format(date, 'dd/MM/yyyy');
        } catch (error) {
          console.warn('Erreur lors du formatage de la date d\'envoi SACEM:', evt.sacemSendDate, error);
          return '';
        }
      })() : '',
      bilanEnvoyeProd,
      evt.dateEnvoiBilan ? (() => {
        try {
          const date = new Date(evt.dateEnvoiBilan);
          return isNaN(date.getTime()) ? '' : format(date, 'dd/MM/yyyy');
        } catch (error) {
          console.warn('Erreur lors du formatage de la date d\'envoi bilan:', evt.dateEnvoiBilan, error);
          return '';
        }
      })() : '',
      factureProdDrive, 
      spectacleCloture, 
      notesDetailsArtistes, // Nouvelle colonne pour les notes détaillées des artistes
      evt.commentaires || evt.description || '',
      rowColor // Stocker la couleur pour l'appliquer plus tard
    ];
  });

  // 3.5) Retirer la colonne couleur **avant** l’export
  suiviData.forEach(row => row.pop());

  // 4) Assembler l'ensemble en une seule matrice
  let allRows = [
    ...legendRows
  ];
  
  // Ajouter une ligne avec le nom de la salle si un filtre est appliqué
  if (selectedRoom !== 'all') {
    const selectedRoomData = rooms.find(room => room.id === parseInt(selectedRoom));
    if (selectedRoomData) {
      const roomHeaderRow = Array(suiviHeaders.length).fill('');
      roomHeaderRow[0] = `SALLE: ${selectedRoomData.name}`;
      allRows.push(roomHeaderRow);
    }
  }
  
  allRows.push(suiviHeaders);
  allRows.push(...suiviData);
  const wsSuivi = XLSX.utils.aoa_to_sheet(allRows);

  // 5) Largeurs de colonnes – ajustées avec la nouvelle colonne contact régisseur
  wsSuivi['!cols'] = [
    {wch:15},{wch:35},{wch:12},{wch:12},{wch:11},{wch:12},{wch:12}, // SALLE, NOM, du, au, horaire, SÉANCES JOUÉ, SÉANCES ANNULÉ
    {wch:12},{wch:20},{wch:10},{wch:10},{wch:12},{wch:15},{wch:15}, // nombre VHR, ADRESSE, JAUGE, ONE, CO PROD, PRODUITE, LOCATION
    {wch:10},{wch:20},{wch:20},                            // CONTRAT, contact Prod, contact régisseur
    {wch:12},{wch:12},{wch:10},{wch:20},                   // billetterie, FLYERS, HOTEL, Lieu arrivée
    {wch:18},{wch:18},{wch:15},{wch:15},{wch:18},          // QUI TRANSFERT arrivée, arrivé date, lieu départ, transfert, DEPART date
    {wch:18},{wch:15},{wch:15},{wch:15},{wch:15},          // QUI TRANSFERT départ, bilan, DROIT, Décla, date
    {wch:15},{wch:15},{wch:15},{wch:15},{wch:15},          // SACEM, date, bilan, date, FACTURE
    {wch:15},{wch:25}                                      // CLOTURE, COMMENTAIRES
  ];

  // 6) Hauteur des lignes (ajusté pour la ligne de salle optionnelle)
  wsSuivi['!rows'] = [];
  
  // Calculer l'index de la ligne d'en-tête en fonction de la présence de la ligne salle
  const roomHeaderRowIndex = selectedRoom !== 'all' ? legendRows.length : -1;
  const mainHeaderRowIndex = selectedRoom !== 'all' ? legendRows.length + 1 : legendRows.length;
  
  // Hauteur de la ligne salle si présente
  if (roomHeaderRowIndex >= 0) {
    wsSuivi['!rows'][roomHeaderRowIndex] = { hpt: 30 };
  }
  
  // Hauteur de la ligne d'en-tête principale
  wsSuivi['!rows'][mainHeaderRowIndex] = { hpt: 53.25 };
  
  // Hauteur standard pour les lignes de données
  for (let i = mainHeaderRowIndex + 1; i < mainHeaderRowIndex + 1 + suiviData.length; i++) {
    wsSuivi['!rows'][i] = { hpt: 30 };
  }

  // 7) Figer volets (ajusté pour la ligne de salle optionnelle et la nouvelle colonne)
  const freezeYSplit = selectedRoom !== 'all' ? legendRows.length + 2 : legendRows.length + 1;
  wsSuivi['!freeze'] = { xSplit: 10, ySplit: freezeYSplit }; // Ajusté pour inclure les colonnes ADRESSE, JAUGE et contact régisseur

  // 8) Appliquer les fonds colorés de la légende (suppression des références à la ligne 7)
  const legendFills = {
    'A1':'FF00FF00','B1':'FFFFFF00', // Vert pour DOSSIER CLOS, Jaune pour CO PROD
    'A2':'FFFF9900','B2':'FF00FFFF', // Orange pour BILAN ENVOYE, Cyan pour PRODUCTION
    'B3':'FFFF00FF', // Rose/magenta pour ONE
    'B4':'FF000000','A4':'FF000000','A5':'FFFF0000','B5':'FF7F6000' // Noir pour SPECTACLE ANNULEE, Rouge pour 🔍🔎, Marron/doré pour LOCATION SALLE
  };

  // Appliquer les couleurs de la légende
  Object.entries(legendFills).forEach(([cell, rgb]) => {
    if (!wsSuivi[cell]) return;
    wsSuivi[cell].s = wsSuivi[cell].s || {};
    wsSuivi[cell].s.fill = { patternType:'solid', fgColor:{rgb} };
    wsSuivi[cell].s.alignment = { horizontal: 'center', vertical: 'center' };
    wsSuivi[cell].s.border = {
      top: { style: 'thin', color: { rgb: 'FF000000' } },
      left: { style: 'thin', color: { rgb: 'FF000000' } },
      bottom: { style: 'thin', color: { rgb: 'FF000000' } },
      right: { style: 'thin', color: { rgb: 'FF000000' } }
    };
  });

  // Texte blanc sur fond noir pour SPECTACLE ANNULEE
  if(wsSuivi['B4']) {
    wsSuivi['B4'].s = wsSuivi['B4'].s || {};
    wsSuivi['B4'].s.font = { color:{ rgb:'FFFFFFFF' }, bold: true };
  }
  if(wsSuivi['A4']) {
    wsSuivi['A4'].s = wsSuivi['A4'].s || {};
    wsSuivi['A4'].s.font = { color:{ rgb:'FFFFFFFF' }, bold: true };
  }

  // Appliquer les styles à la ligne de salle si présente
  if (roomHeaderRowIndex >= 0) {
    const roomCellRef = XLSX.utils.encode_cell({r: roomHeaderRowIndex, c: 0});
    if (wsSuivi[roomCellRef]) {
      wsSuivi[roomCellRef].s = {
        font: { bold: true, color: { rgb: 'FFFFFFFF' }, size: 14 },
        fill: { patternType: 'solid', fgColor: { rgb: 'FF3A6BCF' } }, // Bleu primaire pour la salle
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: 'FF000000' } },
          left: { style: 'thin', color: { rgb: 'FF000000' } },
          bottom: { style: 'thin', color: { rgb: 'FF000000' } },
          right: { style: 'thin', color: { rgb: 'FF000000' } }
        }
      };
    }
  }
  
  // Appliquer les styles aux en-têtes principaux
  for (let i = 0; i < suiviHeaders.length; i++) {
    const cellRef = XLSX.utils.encode_cell({r: mainHeaderRowIndex, c: i});
    if (!wsSuivi[cellRef]) continue;
    wsSuivi[cellRef].s = {
      font: { bold: true, color: { rgb: 'FF000000' } },
      fill: { patternType: 'solid', fgColor: { rgb: 'FFE0E0E0' } }, // Gris clair pour les en-têtes
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: {
        top: { style: 'thin', color: { rgb: 'FF000000' } },
        left: { style: 'thin', color: { rgb: 'FF000000' } },
        bottom: { style: 'thin', color: { rgb: 'FF000000' } },
        right: { style: 'thin', color: { rgb: 'FF000000' } }
      }
    };
  }

  // Appliquer les couleurs aux lignes de données (ajusté pour la ligne de salle optionnelle)
  suiviData.forEach((row, idx) => {
    const rowColor = row[row.length - 1]; // La couleur est stockée dans le dernier élément
    
    // Déterminer si c'est une ligne paire ou impaire pour les couleurs alternées
    const isAlternateRow = idx % 2 === 1;
    const alternateColor = isAlternateRow ? 'FFF5F5F5' : 'FFFFFFFF'; // Gris clair pour les lignes impaires, blanc pour les paires
    
    // Appliquer la couleur à toute la ligne
    for (let i = 0; i < row.length - 1; i++) { // -1 pour ne pas inclure la cellule de couleur
      const cellRef = XLSX.utils.encode_cell({r: mainHeaderRowIndex + 1 + idx, c: i});
      if (!wsSuivi[cellRef]) continue;
      wsSuivi[cellRef].s = wsSuivi[cellRef].s || {};
      
      // Surbrillance spéciale pour la première colonne (colonne A - SALLE) si activée
      if (i === 0 && highlightFirstColumn) {
        wsSuivi[cellRef].s.fill = { patternType: 'solid', fgColor: { rgb: 'FFFFCC00' } }; // Jaune pour surbrillance
        wsSuivi[cellRef].s.font = { bold: true, color: { rgb: 'FF000000' } };
      } else {
        // Appliquer la couleur de fond spécifique ou la couleur alternée pour les autres colonnes
        if (rowColor) {
          wsSuivi[cellRef].s.fill = { patternType: 'solid', fgColor: { rgb: rowColor } };
        } else {
          wsSuivi[cellRef].s.fill = { patternType: 'solid', fgColor: { rgb: alternateColor } };
        }
      }
      
      // Si la couleur est noire, mettre le texte en blanc (sauf pour la première colonne)
      if (rowColor === 'FF000000' && i !== 0) {
        wsSuivi[cellRef].s.font = { color: { rgb: 'FFFFFFFF' } };
      }
      
      // Mettre en gras les cellules contenant 'X'
      const cellValue = row[i];
      if (cellValue === 'X') {
        wsSuivi[cellRef].s.font = { 
          ...wsSuivi[cellRef].s.font,
          bold: true 
        };
      }
      
      // Ajouter des bordures
      wsSuivi[cellRef].s.border = {
        top: { style: 'thin', color: { rgb: 'FF000000' } },
        left: { style: 'thin', color: { rgb: 'FF000000' } },
        bottom: { style: 'thin', color: { rgb: 'FF000000' } },
        right: { style: 'thin', color: { rgb: 'FF000000' } }
      };
      
      // Centrer certaines colonnes et toutes les colonnes avec 'X'
      // Ajuster les indices de colonnes pour inclure les nouvelles colonnes "OCCURRENCES", "CONTRAT" et "ANNULÉ"
      const columnsToCenter = [
        2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 16, 17, 18, 19, 20, 24, 26, 27, 28, 29, 30, 32, 33,
        // Colonnes avec des 'X' pour les types de spectacles et informations logistiques
        6, 8, 9, 10, 11, 12, 17, 18, 20, 24, 26, 28, 29, 30, 32
      ];
      
      // Mettre en gras et centrer la colonne OCCURRENCES (indice 5)
      if (i === 5) {
        wsSuivi[cellRef].s.font = { 
          ...wsSuivi[cellRef].s.font,
          bold: true 
        };
        wsSuivi[cellRef].s.alignment = { horizontal: 'center', vertical: 'center' };
      }
      if (columnsToCenter.includes(i) || cellValue === 'X') {
        wsSuivi[cellRef].s.alignment = { horizontal: 'center', vertical: 'center' };
      }
    }
  });
  
  // Surbrillance de la première colonne pour l'en-tête aussi si activée
  if (highlightFirstColumn) {
    const headerFirstColumnRef = XLSX.utils.encode_cell({r: mainHeaderRowIndex, c: 0});
    if (wsSuivi[headerFirstColumnRef]) {
      wsSuivi[headerFirstColumnRef].s = {
        ...wsSuivi[headerFirstColumnRef].s,
        fill: { patternType: 'solid', fgColor: { rgb: 'FFFFCC00' } }, // Jaune pour surbrillance
        font: { bold: true, color: { rgb: 'FF000000' } }
      };
    }
  }

  // Supprimer la colonne de couleur des données
  suiviData.forEach((row, idx) => {
    row.pop(); // Supprimer le dernier élément (couleur)
  });

  // 9) Ajout au classeur avec le nom de feuille approprié
  XLSX.utils.book_append_sheet(wb, wsSuivi, `ANNEE ${currentYear}`);

  // 10) Ajouter une feuille avec les informations des utilisateurs
  try {
    const usersResponse = await api.get('/users');
    
    if (usersResponse.data) {
      const usersData = usersResponse.data;
      
      // Créer les données pour la feuille des informations utilisateurs
      const usersInfoData = [
        [
          'Nom',
          'Prénom', 
          'Email',
          'Téléphone',
          'Adresse',
          'Date de naissance',
          'Lieu de naissance',
          'Numéro de sécurité sociale',
          'IBAN',
          'Rôle'
        ]
      ];
      
      // Ajouter les données de chaque utilisateur
      console.log('usersData structure:', usersData);
      console.log('usersData type:', typeof usersData);
      console.log('usersData is array:', Array.isArray(usersData));
      
      let users = [];
      if (Array.isArray(usersData)) {
        users = usersData;
      } else if (usersData && Array.isArray(usersData.data)) {
        users = usersData.data;
      } else if (usersData && Array.isArray(usersData.users)) {
        users = usersData.users;
      } else {
        console.warn('Structure de données utilisateurs inattendue:', usersData);
        users = [];
      }
      
      console.log('users final:', users);
      console.log('users is array:', Array.isArray(users));
      
      if (Array.isArray(users) && users.length > 0) {
        users.forEach(user => {
        const roleLabel = user.role === 'billeterie' ? 'Billetterie' : 
                         user.role === 'permanence' ? 'Permanence' : 
                         user.role === 'regie' ? 'Régie' : 
                         user.role === 'technique' ? 'Technique' :
                         user.role === 'artistesProd' ? 'Artiste Production' :
                         user.role === 'artistesCoProd' ? 'Artiste Co-Production' :
                         user.role === 'admin' ? 'Administrateur' : user.role;
        
        const formattedBirthDate = user.birthDate ? 
          new Date(user.birthDate).toLocaleDateString('fr-FR') : '';
        
        usersInfoData.push([
          user.lastName || '',
          user.firstName || '',
          user.email || '',
          user.phone || '',
          user.address || '',
          formattedBirthDate,
          user.birthPlace || '',
          user.socialSecurityNumber || '',
          formatIban(user.iban || ''),
          roleLabel
        ]);
        });
      } else {
        console.warn('Aucune donnée utilisateur valide trouvée pour l\'export');
      }
      
      // Créer la feuille de calcul pour les informations utilisateurs
      const wsUsersInfo = XLSX.utils.aoa_to_sheet(usersInfoData);
      
      // Définir les largeurs de colonnes
      wsUsersInfo['!cols'] = [
        { wch: 20 }, // Nom
        { wch: 20 }, // Prénom
        { wch: 25 }, // Email
        { wch: 15 }, // Téléphone
        { wch: 30 }, // Adresse
        { wch: 15 }, // Date de naissance
        { wch: 20 }, // Lieu de naissance
        { wch: 20 }, // Numéro de sécurité sociale
        { wch: 25 }, // IBAN
        { wch: 20 }  // Rôle
      ];
      
      // Appliquer les styles à la feuille des informations utilisateurs
      applyStyles(wsUsersInfo, usersInfoData, {
        headerColor: '28A745', // Vert pour les informations utilisateurs
        cols: wsUsersInfo['!cols'],
        columnStyles: {
          2: { alignment: { horizontal: 'left' } },   // Email aligné à gauche
          4: { alignment: { horizontal: 'left' } },   // Adresse alignée à gauche
          5: { alignment: { horizontal: 'center' } }, // Date de naissance centrée
          9: { alignment: { horizontal: 'center' } }  // Rôle centré
        }
      });
      
      // Ajouter la feuille au classeur
      XLSX.utils.book_append_sheet(wb, wsUsersInfo, 'Informations Utilisateurs');
    }
  } catch (error) {
    console.error('Erreur lors de la récupération des informations utilisateurs:', error);
  }
    
    // Génération du fichier Excel
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    // Générer le nom du fichier avec le nom de la salle si une salle spécifique est sélectionnée
    let fileName = `Statistiques_${formattedPeriod}`;
    if (selectedRoom !== 'all') {
      const selectedRoomData = rooms.find(room => room.id === parseInt(selectedRoom));
      if (selectedRoomData) {
        fileName += `_${selectedRoomData.name.replace(/[^a-zA-Z0-9]/g, '_')}`;
      }
    }
    fileName += '.xlsx';
    
    // Téléchargement du fichier
    saveAs(data, fileName);
  };

   const goToPreviousMonth  = goToPreviousPeriod;
   const goToNextMonth      = goToNextPeriod;
   const goToCurrentMonth   = goToCurrentPeriod;

  return (
    <div className="space-y-8 max-w-7xl mx-auto px-4 py-6">
      {/* En-tête du tableau de bord */}
      <div className="flex justify-between items-center bg-white p-5 rounded-lg shadow-md">
        <h1 className="text-3xl font-bold text-primary">Tableau de bord</h1>
        <div className="flex gap-2">
          <button 
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-primary bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary shadow-sm"
            title="Actualiser les données"
          >
            <ArrowPathIcon className="h-5 w-5 mr-1" />
            <span className="hidden sm:inline">Actualiser</span>
          </button>
          
          <button 
            onClick={exportStats}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary shadow-sm"
            title="Exporter les statistiques en Excel"
          >
            <ArrowDownTrayIcon className="h-5 w-5 mr-1" />
            <span className="hidden sm:inline">Exporter en Excel</span>
          </button>
          

          
          <button 
            onClick={navigateToCalendar}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-secondary hover:bg-secondary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary shadow-sm"
            title="Voir le calendrier"
          >
            <CalendarIcon className="h-5 w-5 mr-1" />
            <span className="hidden sm:inline">Calendrier</span>
          </button>
        </div>
      </div>
      
      {/* Sélecteur de mois et de salle */}
      <div className="flex flex-col md:flex-row justify-between items-center bg-white p-5 rounded-lg shadow-md">
        <div className="flex items-center space-x-2 mb-4 md:mb-0">
          <button 
            onClick={goToPreviousMonth}
            className="p-2 rounded-full hover:bg-gray-100"
            title="Mois précédent"
          >
            <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
          </button>
          
          <h2 className="text-xl font-semibold text-primary">{formattedPeriod}</h2>
          
          <button 
            onClick={goToNextMonth}
            className="p-2 rounded-full hover:bg-gray-100"
            title="Mois suivant"
          >
            <ChevronRightIcon className="h-5 w-5 text-gray-600" />
          </button>

            {/* Sélecteur de découpage */}
            <select
            value={periodType}
            onChange={e => setPeriodType(e.target.value)}
            className="ml-4 form-select rounded-md border-gray-300 focus:ring focus:ring-primary/30 focus:ring-opacity-50 text-sm"
          >
            <option value="month">Mois</option>
            <option value="quarter">Trimestre</option>
            <option value="year">Année</option>
          </select>
          
          <button 
            onClick={goToCurrentMonth}
            className="ml-2 px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700"
            title="Revenir au mois actuel"
          >
            Aujourd'hui
          </button>
        </div>
        
        {/* Sélecteur de salle */}
        <div className="flex items-center space-x-2">
          <FunnelIcon className="h-5 w-5 text-gray-600" />
          <select
            value={selectedRoom}
            onChange={handleRoomChange}
            className="form-select rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/30 focus:ring-opacity-50 text-sm"
          >
            <option value="all">Toutes les salles</option>
            {rooms.map(room => (
              <option key={room.id} value={room.id}>{room.name}</option>
            ))}
          </select>
        </div>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md">
          <p className="font-bold">Erreur</p>
          <p>{error}</p>
        </div>
      ) : (
        <>
          {/* Cartes de statistiques */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <StatCard 
              title="Nombre total de spectacles" 
              value={stats.shows} 
              icon={CalendarIcon} 
              color="bg-primary"
              description={`Spectacles non annulés pour ${formattedPeriod}${selectedRoom !== 'all' ? ` - ${rooms.find(r => r.id == selectedRoom)?.name || ''}` : ''}`}
            />
            <StatCard 
              title="Production" 
              value={stats.production} 
              icon={SparklesIcon} 
              color="bg-blue-600"
              description="Spectacles en production"
            />
            <StatCard 
              title="Co-production" 
              value={stats.coproduction} 
              icon={ClockIcon} 
              color="bg-purple-600"
              description="Spectacles en co-production"
            />
            <StatCard 
              title="ONE" 
              value={stats.one} 
              icon={FireIcon} 
              color="bg-red-600"
              description="Spectacles ONE"
            />
            <StatCard 
              title="Locations" 
              value={stats.rentals} 
              icon={BuildingOfficeIcon} 
              color="bg-amber-600"
              description="Locations de salle et événements"
            />

            <StatCard 
              title="Nombre total d'événements" 
              value={stats.total} 
              icon={CalendarIcon} 
              color="bg-green-600"
              description={`Tous les événements pour ${formattedPeriod}${selectedRoom !== 'all' ? ` - ${rooms.find(r => r.id == selectedRoom)?.name || ''}` : ''}`}
            />
          </div>
          
          {/* Graphique et statistiques des spectacles */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-semibold mb-4 text-primary">
                Statut des spectacles {selectedRoom !== 'all' && `- ${rooms.find(r => r.id == selectedRoom)?.name || ''}`}
              </h2>
              <div className="h-64">
                {stats.shows > 0 ? (
                  <Pie data={pieChartData} options={pieChartOptions} />
                ) : (
                  <div className="flex justify-center items-center h-full text-gray-500">
                    Aucun spectacle pour cette période
                  </div>
                )}
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-semibold mb-4 text-primary">
              Nombre cachets artistes Prod {selectedRoom !== 'all' && `- ${rooms.find(r => r.id == selectedRoom)?.name || ''}`}
              </h2>
              {artistStats.length > 0 ? (
                <div className="overflow-y-auto max-h-64">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          artistesProd
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Spectacles
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {artistStats.map((artist, index) => (
                        <tr 
                          key={index} 
                          className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 cursor-pointer`}
                          onClick={() => handlePersonClick(artist, true)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {artist.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {artist.count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex justify-center items-center h-64 text-gray-500">
                  Aucun artistesProd pour cette période
                </div>
              )}
            </div>
          </div>
          
          {/* Statistiques du personnel */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-primary">
                Heures du personnel {selectedRoom !== 'all' && `- ${rooms.find(r => r.id == selectedRoom)?.name || ''}`}
              </h2>
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">Objectif mensuel :</label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={targetInput}
                    onChange={(e) => handleMonthlyTargetChange(e.target.value)}
                    onBlur={(e) => saveMonthlyTarget(e.target.value)}
                    placeholder="Heures"
                    className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                  />
                  <span className="text-xs text-gray-400">h</span>
                </div>
                <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="form-select rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/30 focus:ring-opacity-50 text-sm"
              >
                <option value="">Tous les rôles</option>
                <option value="billeterie">Billetterie</option>
                <option value="permanence">Permanence</option>
                <option value="regie">Régie</option>
                <option value="technique">Technique</option>
                <option value="consultation">Consultation</option>
              </select>
                <button
                  onClick={exportUsersInfo}
                  className="
                    inline-flex items-center px-3 py-2
                    bg-primary text-white
                    rounded-md shadow-sm
                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary
                    transition-colors duration-200
                  "
                >
                  <DocumentArrowDownIcon className="h-5 w-5 mr-1" />
                  <span>Exporter infos utilisateurs</span>
                </button>
              </div>
            </div>
            
            {staffStats.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Membre
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rôle
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Heures
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {staffStats
                      .filter(staff => roleFilter === '' || staff.role === roleFilter)
                      .map((staff, index) => (
                        <tr 
                          key={index} 
                          className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 cursor-pointer`}
                          onClick={() => handlePersonClick(staff)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {staff.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span 
                              className={`px-2 py-1 text-xs font-medium rounded-full`}
                              style={{ 
                                backgroundColor: getRoleColor(staff.role, 0.2),
                                color: getRoleColor(staff.role, 1)
                              }}
                            >
                              {staff.role === 'billeterie' ? 'Billetterie' : 
                               staff.role === 'permanence' ? 'Permanence' : 
                               staff.role === 'regie' ? 'Régie' : 
                               staff.role === 'technique' ? 'Technique' : staff.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span style={{
                              color: getHoursColor(staff.hours, monthlyTarget || 0)
                            }}>
                              {staff.hours} h
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex justify-center items-center h-64 text-gray-500">
                Aucun personnel pour cette période
              </div>
            )}
          </div>
          
          {/* Statistiques des absences du personnel */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-primary">
                Absences du personnel {selectedRoom !== 'all' && `- ${rooms.find(r => r.id == selectedRoom)?.name || ''}`}
              </h2>
              <select
                value={absenceTypeFilter}
                onChange={(e) => setAbsenceTypeFilter(e.target.value)}
                className="form-select rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary/30 focus:ring-opacity-50 text-sm"
              >
                <option value="">Tous les types</option>
                <option value="maladie">Maladie</option>
                <option value="absence">Absence</option>
                <option value="CP">Congés payés</option>
                <option value="télétravail">Télétravail</option>
                <option value="autre">Autre</option>
              </select>
            </div>
            
            {absenceStats.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Membre
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rôle
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Heures
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {absenceStats
                      .filter(absence => {
                        // Si aucun filtre n'est sélectionné, afficher toutes les absences
                        if (absenceTypeFilter === '') return true;
                        // Sinon, vérifier si l'utilisateur a des absences du type sélectionné
                        return absence.types[absenceTypeFilter] > 0;
                      })
                      .map((absence, index) => (
                        <tr 
                          key={index} 
                          className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 cursor-pointer`}
                          onClick={() => handlePersonClick(absence)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {absence.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span 
                              className={`px-2 py-1 text-xs font-medium rounded-full`}
                              style={{ 
                                backgroundColor: getRoleColor(absence.role, 0.2),
                                color: getRoleColor(absence.role, 1)
                              }}
                            >
                              {absence.role === 'billeterie' ? 'Billetterie' : 
                               absence.role === 'permanence' ? 'Permanence' : 
                               absence.role === 'regie' ? 'Régie' : 
                               absence.role === 'technique' ? 'Technique' : 
                               absence.role === 'artistesProd' ? 'Artiste' : absence.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex flex-col space-y-1">
                              {Object.entries(absence.types)
                                .filter(([_, count]) => count > 0)
                                .map(([type, count], i) => (
                                  <span 
                                    key={i} 
                                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                                      absenceTypeFilter === '' || absenceTypeFilter === type 
                                        ? 'bg-blue-100 text-blue-800' 
                                        : 'bg-gray-100 text-gray-600'
                                    }`}
                                  >
                                    {type === 'maladie' ? 'Maladie' :
                                     type === 'absence' ? 'Absence' :
                                     type === 'CP' ? 'Congés payés' :
                                     type === 'télétravail' ? 'Télétravail' : 'Autre'}: {type === 'CP' ? (count / 7).toFixed(1) + ' j' : count.toFixed(1) + ' h'}
                                  </span>
                                ))}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {absenceTypeFilter === '' ? 
                              absence.totalHours.toFixed(1) + ' h' : 
                              absenceTypeFilter === 'CP' ? 
                                (absence.types[absenceTypeFilter] / 7).toFixed(1) + ' j' : 
                                absence.types[absenceTypeFilter].toFixed(1) + ' h'}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex justify-center items-center h-64 text-gray-500">
                Aucune absence pour cette période
              </div>
            )}
          </div>
          
          {/* Tableau des indisponibilités des utilisateurs */}
          <div className="bg-white p-6 rounded-lg shadow-md mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-primary">
                Indisponibilités des utilisateurs {selectedRoom !== 'all' && `- ${rooms.find(r => r.id == selectedRoom)?.name || ''}`}
              </h2>
            </div>
            
            {unavailabilityStats.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Utilisateur
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rôle
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Heures d'indisponibilité
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Détails
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {unavailabilityStats.map((unavailability, index) => (
                      <tr 
                        key={index} 
                        className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 cursor-pointer`}
                        onClick={() => handlePersonClick(unavailability)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {unavailability.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span 
                            className={`px-2 py-1 text-xs font-medium rounded-full`}
                            style={{ 
                              backgroundColor: getRoleColor(unavailability.role, 0.2),
                              color: getRoleColor(unavailability.role, 1)
                            }}
                          >
                            {unavailability.role === 'billeterie' ? 'Billetterie' : 
                             unavailability.role === 'permanence' ? 'Permanence' : 
                             unavailability.role === 'regie' ? 'Régie' : 
                             unavailability.role === 'technique' ? 'Technique' : 
                             unavailability.role === 'artistesProd' ? 'Artiste' : unavailability.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {unavailability.totalHours} h
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex flex-col space-y-1">
                            {unavailability.unavailabilities
                              .sort((a, b) => new Date(a.start) - new Date(b.start))
                              .slice(0, 3) // Limiter à 3 indisponibilités pour ne pas surcharger
                              .map((item, i) => (
                                <span key={i} className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100">
                                  {format(new Date(item.start), 'dd/MM/yyyy')} - {item.hours.toFixed(1)} h
                                </span>
                              ))}
                            {unavailability.unavailabilities.length > 3 && (
                              <span className="text-xs text-gray-500 italic">
                                + {unavailability.unavailabilities.length - 3} autres...
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex justify-center items-center h-64 text-gray-500">
                Aucune indisponibilité pour cette période
              </div>
            )}
          </div>
          

        </>
      )}
      {/* Modal de détails pour une personne */}
      {showDetailsModal && selectedPerson && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* En-tête du modal */}
            <div className="bg-primary text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-semibold">
                Détails pour {selectedPerson.name} 
                <span className="ml-2 text-sm bg-white/20 text-white px-2 py-1 rounded">
                  {selectedPerson.isRoom ? 'Salle' :
                   selectedPerson.isArtist ? 'artistesProd' : 
                   selectedPerson.isAbsence ? (
                     selectedPerson.role === 'billeterie' ? 'Billetterie' : 
                     selectedPerson.role === 'administrateur' ? 'Administrateur' : 
                     selectedPerson.role === 'permanence' ? 'Permanence' : 
                     selectedPerson.role === 'regie' ? 'Régie' : 
                     selectedPerson.role === 'technique' ? 'Technique' : 
                     selectedPerson.role.charAt(0).toUpperCase() + selectedPerson.role.slice(1)
                   ) : 
                   selectedPerson.role === 'billeterie' ? 'Billetterie' : 
                   selectedPerson.role === 'administrateur' ? 'Administrateur' : 
                   selectedPerson.role === 'permanence' ? 'Permanence' : 
                   selectedPerson.role === 'regie' ? 'Régie' : 
                   selectedPerson.role === 'technique' ? 'Technique' : 
                   selectedPerson.role.charAt(0).toUpperCase() + selectedPerson.role.slice(1)}
                </span>
              </h3>
              <button 
                onClick={() => setShowDetailsModal(false)}
                className="text-white hover:text-gray-200"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            {/* Corps du modal */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-primary mb-2">
                {selectedPerson.isRoom ? 'Événements dans cette salle ' :
                 selectedPerson.isArtist ? 'Spectacles joués ' : 
                 selectedPerson.isAbsence ? 'Absences ' :
                 selectedPerson.isUnavailability ? 'Indisponibilités ' : 'Heures de travail '} 
                pendant {formattedPeriod}
              </h4>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="bg-primary/10 rounded-lg px-4 py-2 flex-1">
                    <div className="text-sm text-gray-600">Total</div>
                    <div className="text-2xl font-bold text-primary">
                      {selectedPerson.isRoom ? personEvents.length :
                       selectedPerson.isArtist ? selectedPerson.count : 
                       selectedPerson.isAbsence ? 
                         (selectedPerson.types && selectedPerson.types.CP ? 
                           (selectedPerson.types.CP / 7).toFixed(1) + ' j' + (Object.keys(selectedPerson.types).length > 1 ? ' + ' + (selectedPerson.totalHours - selectedPerson.types.CP).toFixed(1) + ' h' : '') : 
                           selectedPerson.totalHours + ' h') :
                       selectedPerson.isUnavailability ? selectedPerson.totalHours : selectedPerson.hours}
                      <span className="text-sm ml-1">
                        {selectedPerson.isRoom ? 'événements' :
                         selectedPerson.isArtist ? 'spectacles' : 
                         (selectedPerson.isAbsence && selectedPerson.types && selectedPerson.types.CP && Object.keys(selectedPerson.types).length === 1) ? 
                           '' : 
                         (selectedPerson.isAbsence && selectedPerson.types && selectedPerson.types.CP) ? 
                           '' : ''}
                      </span>
                    </div>
                  </div>
                  {selectedPerson.isRoom && (
                    <div className="bg-primary/10 rounded-lg px-4 py-2 flex-1">
                      <div className="text-sm text-gray-600">Capacité</div>
                      <div className="text-2xl font-bold text-primary">
                        {selectedPerson.capacity || 'N/A'}
                        <span className="text-sm ml-1">places</span>
                      </div>
                    </div>
                  )}
                  {selectedPerson.isAbsence && (
                    <div className="bg-primary/10 rounded-lg px-4 py-2 flex-1">
                      <div className="text-sm text-gray-600">Types d'absences</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {Object.entries(selectedPerson.types).map(([type, days], index) => {
                          if (days > 0) {
                            return (
                              <span 
                                key={index} 
                                className={`inline-block px-2 py-1 text-xs rounded-full ${
                                  type === 'maladie' ? 'bg-red-100 text-red-800' : 
                                  type === 'absence' ? 'bg-yellow-100 text-yellow-800' : 
                                  type === 'CP' ? 'bg-green-100 text-green-800' : 
                                  type === 'télétravail' ? 'bg-blue-100 text-blue-800' : 
                                  'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {type === 'maladie' ? 'Maladie' : 
                                 type === 'absence' ? 'Absence' : 
                                 type === 'CP' ? 'Congé payé' : 
                                 type === 'télétravail' ? 'Télétravail' : 
                                 type || 'Autre'} ({type === 'CP' ? (days / 7).toFixed(1) + ' j' : days.toFixed(1) + ' h'})
                              </span>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Jours de travail */}
                {!selectedPerson.isArtist && !selectedPerson.isAbsence && personEvents.length > 0 && (
                  <div className="mt-4">
                    <h5 className="text-md font-semibold text-gray-700 mb-2">Jours travaillés</h5>
                    <div className="bg-gray-50 rounded-lg p-4 max-h-48 overflow-y-auto">
                      {getWorkDays().map((monthData, monthIndex) => (
                        <div key={monthIndex} className="mb-3">
                          <h6 className="text-sm font-medium text-gray-600 mb-1 capitalize">{monthData.month}</h6>
                          <div className="flex flex-wrap gap-1 mb-2">
                            {monthData.days.map((day, dayIndex) => (
                              <span 
                                key={dayIndex} 
                                className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                              >
                                {day}
                              </span>
                            ))}
                          </div>
                          
                          {/* Détail des heures par lieu */}
                          {!selectedPerson.isArtist && !selectedPerson.isRoom && (
                            <div className="mt-2 pl-2 border-l-2 border-blue-200">
                              <h6 className="text-xs font-medium text-gray-500 mb-1">Détail par lieu:</h6>
                              <div className="grid grid-cols-1 gap-1">
                                {monthData.locations.map((location, locIndex) => (
                                  <div key={locIndex} className="text-xs flex justify-between">
                                    <span className="text-gray-700">{location.name}</span>
                                    <span className="font-medium text-primary">
                                      {location.count} événement{location.count > 1 ? 's' : ''} 
                                      {!selectedPerson.isArtist && !selectedPerson.isRoom ? 
                                        `(${location.hours} h)` : ''}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Résumé des absences */}
                {selectedPerson.isAbsence && personEvents.length > 0 && (
                  <div className="mt-4">
                    <h5 className="text-md font-semibold text-gray-700 mb-2">Résumé des absences</h5>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(selectedPerson.types).map(([type, days], index) => {
                          if (days > 0) {
                            return (
                              <div key={index} className="flex items-center justify-between p-2 rounded bg-white shadow-sm">
                                <span className="font-medium">
                                  <span className={`inline-block w-3 h-3 rounded-full mr-2 ${
                                    type === 'maladie' ? 'bg-red-500' : 
                                    type === 'absence' ? 'bg-yellow-500' : 
                                    type === 'CP' ? 'bg-green-500' : 
                                    type === 'télétravail' ? 'bg-blue-500' : 
                                    'bg-gray-500'
                                  }`}></span>
                                  {type === 'maladie' ? 'Maladie' : 
                                   type === 'absence' ? 'Absence' : 
                                   type === 'CP' ? 'Congé payé' : 
                                   type === 'télétravail' ? 'Télétravail' : 
                                   type || 'Autre'}
                                </span>
                                <span className="text-primary font-bold">{type === 'CP' ? (days / 7).toFixed(1) + ' jours' : days.toFixed(1) + ' heures'}</span>
                              </div>
                            );
                          }
                          return null;
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Tableau des événements */}
              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {selectedPerson.isAbsence ? (
                        <>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Titre
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type d'absence
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Durée
                          </th>
                        </>
                      ) : selectedPerson.isUnavailability ? (
                        <>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Titre
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Durée (heures)
                          </th>
                        </>
                      ) : (
                        <>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Événement
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Salle
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {selectedPerson.isArtist ? 'Spectacle' : 'Heures'}
                          </th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {personEvents.length > 0 ? (
                      personEvents.map((event, index) => {
                        // Si c'est une absence
                        if (selectedPerson.isAbsence) {
                          return (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-900">{event.title || 'Absence'}</td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {event.start ? format(new Date(event.start), 'dd/MM/yyyy') : 'N/A'}<br/>
                                {event.end && event.start !== event.end && (
                                  <span className="text-xs text-gray-600">
                                    au {format(new Date(event.end), 'dd/MM/yyyy')}
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  event.personnelType === 'maladie' ? 'bg-red-100 text-red-800' : 
                                  event.personnelType === 'absence' ? 'bg-yellow-100 text-yellow-800' : 
                                  event.personnelType === 'CP' ? 'bg-green-100 text-green-800' : 
                                  event.personnelType === 'télétravail' ? 'bg-blue-100 text-blue-800' : 
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {event.personnelType === 'maladie' ? 'Maladie' : 
                                   event.personnelType === 'absence' ? 'Absence' : 
                                   event.personnelType === 'CP' ? 'Congé payé' : 
                                   event.personnelType === 'télétravail' ? 'Télétravail' : 
                                   event.personnelType || 'Autre'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {event.personnelType === 'CP' ? (event.hours / 7).toFixed(1) + ' j' : event.hours.toFixed(1) + ' h'}
                              </td>
                            </tr>
                          );
                        }
                        
                        // Si c'est une indisponibilité
                        if (selectedPerson.isUnavailability) {
                          return (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-900">{event.title || 'Indisponibilité'}</td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {event.start ? format(new Date(event.start), 'dd/MM/yyyy') : 'N/A'}<br/>
                                {event.end && event.start !== event.end && (
                                  <span className="text-xs text-gray-600">
                                    au {format(new Date(event.end), 'dd/MM/yyyy')}
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {event.hours.toFixed(1)} h
                              </td>
                            </tr>
                          );
                        }
                        
                        // Sinon, c'est un événement normal
                        // Calcul de la durée de l'événement en heures
                        const start = new Date(event.start);
                        const end = new Date(event.end);
                        const durationHours = ((end - start) / (1000 * 60 * 60)).toFixed(1);
                        
                        // Récupération des heures spécifiques pour le personnel
                        const userHours = !selectedPerson.isArtist && !selectedPerson.isRoom && event.Users && event.Users.length > 0 ?
                          (event.Users.find(u => u.id === parseInt(selectedPerson.id))?.through?.hours || durationHours)
                          : durationHours;
                        
                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">{event.title}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {event.start ? format(new Date(event.start), 'dd/MM/yyyy') : 'N/A'}<br/>
                              <span className="text-xs text-gray-600">
                                {event.start ? format(new Date(event.start), 'HH:mm') : ''} - {event.end ? format(new Date(event.end), 'HH:mm') : ''}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {event.room && event.room.name ? event.room.name : 'Non assignée'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                event.type === 'show' ? 'bg-blue-100 text-blue-800' : 
                                event.type === 'repetition' ? 'bg-green-100 text-green-800' : 
                                event.type === 'personnel' && event.personnelType === 'maladie' ? 'bg-red-100 text-red-800' : 
                                event.type === 'personnel' && event.personnelType === 'absence' ? 'bg-yellow-100 text-yellow-800' : 
                                event.type === 'personnel' && event.personnelType === 'CP' ? 'bg-green-100 text-green-800' : 
                                event.type === 'personnel' && event.personnelType === 'télétravail' ? 'bg-blue-100 text-blue-800' : 
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {event.type === 'show' ? 'Spectacle' : 
                                 event.type === 'repetition' ? 'Répétition' : 
                                 event.type === 'personnel' && event.personnelType === 'maladie' ? 'Maladie' : 
                                 event.type === 'personnel' && event.personnelType === 'absence' ? 'Absence' : 
                                 event.type === 'personnel' && event.personnelType === 'CP' ? 'Congés payés' : 
                                 event.type === 'personnel' && event.personnelType === 'télétravail' ? 'Télétravail' : 
                                 event.type === 'personnel' && event.personnelType ? event.personnelType.charAt(0).toUpperCase() + event.personnelType.slice(1) : 
                                 event.type}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {selectedPerson.isArtist ? 
                                (event.showStatus === 'confirmed' ? 
                                  'Confirmé' : 
                                  event.showStatus === 'ticketsOpen' ? 
                                  'Billetterie ouverte' : 
                                  event.showStatus === 'provisional' ? 
                                  'Provisoire' : 
                                  event.showStatus === 'cancelled' ? 
                                  'Annulé' : 
                                  'Non défini') : 
                                `${userHours} h`}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={selectedPerson.isAbsence ? 4 : 5} className="px-4 py-3 text-sm text-gray-500 text-center">
                          Aucun événement trouvé pour cette période
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Pied du modal */}
            <div className="bg-gray-50 px-6 py-3 flex justify-between">
              <div className="flex space-x-2">
                <button
                  onClick={exportPersonEvents}
                  disabled={personEvents.length === 0}
                  className={`px-4 py-2 rounded transition-colors ${personEvents.length === 0 ? 
                    'bg-gray-300 text-gray-500 cursor-not-allowed' : 
                    'bg-green-600 text-white hover:bg-green-700'}`}
                >
                  Exporter en Excel
                </button>
                <button
                  onClick={exportContextualPDF}
                  disabled={personEvents.length === 0}
                  className={`px-4 py-2 rounded transition-colors ${personEvents.length === 0 ? 
                    'bg-gray-300 text-gray-500 cursor-not-allowed' : 
                    'bg-red-600 text-white hover:bg-red-700'}`}
                >
                  {selectedPerson?.isAbsence ? 'Export PDF Absences' : 
                   selectedPerson?.isRoom ? 'Export PDF Planning' : 
                   'Export PDF Détaillé'}
                </button>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
          </div>
  );
};

export default Dashboard;
